import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LiveBandwidthPanel } from '../components/LiveBandwidth';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import HardwareHistoryPanel from '../components/customer/HardwareHistoryPanel';
import CustomerInfoOverlay from '../components/customer/CustomerInfoOverlay';
import UptimePanel from '../components/customer/UptimePanel';
import GtplLongTermSwapSection from '../components/customer/GtplLongTermSwapSection';
import { customerAPI, paymentAPI, historyImportAPI, complaintAPI, userAPI, broadbandAPI, catvAPI, gtplAPI, gtplSaathiAPI, imbsAPI, oltAPI, configAPI, nasAPI, acsAPI, playboxtvAPI, messagingAPI, agentAppAPI, logsAPI } from '../services/api';
import { customerTypeLabel } from '../utils/customerTypeLabel';
import { pickTemplate, fillTemplate } from '../utils/messageTemplates';
import QRCode from 'react-qr-code';
import { EditPaymentModal } from './PaymentsPage';
import { parseHistoryFile } from './HistoryImportPage';
import { useAuth } from '../context/AuthContext';
import { useViewMode } from '../hooks/useViewMode';
import { AppFold } from '../components/common/AppViewKit';
import { CustomerMobile } from '../components/customer/CustomerMobileKit';
import { format, addDays, addMonths, differenceInDays, parse, isValid, isSameDay } from 'date-fns';
import toast from 'react-hot-toast';
import ISPVlanSelect from '../components/common/ISPVlanSelect';
import AddableSelect from '../components/common/AddableSelect';
import PaymentMethodSelect from '../components/common/PaymentMethodSelect';
import SearchableSelect from '../components/common/SearchableSelect';
import UniqueField from '../components/common/UniqueField';
import DtdAfter8pmNotice from '../components/common/DtdAfter8pmNotice';
import LongTermRegularNotice from '../components/common/LongTermRegularNotice';
import Over90DaysNotice from '../components/common/Over90DaysNotice';
import { useLongTermAutoStart, longTermRegularNoticeApplies, LONG_TERM_MONTHS } from '../utils/longTermNotice';
import { dtdStartsTomorrowFrom } from '../utils/dtdCutoff';
import {
  ArrowLeft, CreditCard, MessageSquare, Edit2, ExternalLink,
  Save, X, Trash2, Receipt, RefreshCw, MapPin,
  Phone, Calendar, History, Cpu, Wallet,
  UserCheck, AlertCircle, Plus, FileText, ChevronRight, CheckCircle, RotateCcw,
  Link2, Unlink, Wifi, Zap, ShieldOff, Router, Search, Eye, EyeOff, KeyRound, Tv, Activity, Send, Radio, PackagePlus, Clock, Power, PowerOff, Unplug, ShoppingCart,
  Ticket, ChevronDown, ChevronUp, Loader, XCircle, ScanSearch, UploadCloud, Paperclip, QrCode, ImagePlus, Film,
  BarChart2, Info, Package,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import '../styles/customer-refresh.css';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtDate = d => d ? format(new Date(d), 'dd-MMM-yyyy') : '—';
const fmtDateTime = d => d ? format(new Date(d), 'dd-MMM-yyyy, HH:mm') : '—';
// Default suggestion for a scheduled GTPL disconnect ticket (Close CATV Connection modal).
const todayInput = () => format(new Date(), 'yyyy-MM-dd');
const firstOfNextMonth = () => {
  const d = new Date();
  return format(new Date(d.getFullYear(), d.getMonth() + 1, 1), 'yyyy-MM-dd');
};
// Break hints after underscores so long codes like "25_LTD_100SPEED_12M_5500" wrap at
// the underscore instead of mid-word.
const withWbr = str => !str ? str : str.split('_').flatMap((part, i, arr) =>
  i < arr.length - 1 ? [part, '_', <wbr key={i} />] : [part]
);

// Validity choices (days) in the Renew panel's drop-down for DTD customers.
const DTD_DAY_OPTIONS = [30, 60, 90, 180, 360];
const DTD_FREE_STB_DAY_OPTIONS = [90, 180, 360]; // STB given free to use (catv.stbFreeToUse)

// ─── GTPL Saathi vs CRM reconciliation ─────────────────────────────────────
// GTPL's fields are scraped page text, not structured data, so any field we can't confidently
// parse/compare is reported as "unknown" (match: null) rather than guessed as a mismatch.
const GTPL_DATE_FORMATS = ['dd-MM-yyyy', 'dd/MM/yyyy', 'dd-MMM-yyyy', 'dd-MMM-yy', 'MM/dd/yyyy', 'yyyy-MM-dd'];
const parseGtplDate = (str) => {
  if (!str) return null;
  for (const f of GTPL_DATE_FORMATS) {
    const d = parse(str.trim(), f, new Date());
    if (isValid(d)) return d;
  }
  return null;
};
const normalizeGtplText = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const gtplTextsMatch = (a, b) => {
  const na = normalizeGtplText(a), nb = normalizeGtplText(b);
  if (!na || !nb) return null;
  return na === nb || na.includes(nb) || nb.includes(na);
};

function buildGtplCrmComparison(customer, subscriber) {
  const rows = [];

  // `fixable: true` marks the fields the "Push CRM → GTPL" button (below) knows how to write
  // back via Modify.aspx — keep in sync with MODIFY_EDITABLE_FIELDS in gtplSaathiService.js.
  rows.push({ label: 'Name', crm: customer.name, gtpl: subscriber.customer?.name, match: gtplTextsMatch(customer.name, subscriber.customer?.name), fixable: true });

  if (customer.mobile || subscriber.customer?.mobile) {
    rows.push({ label: 'Mobile', crm: customer.mobile, gtpl: subscriber.customer?.mobile, match: gtplTextsMatch(customer.mobile, subscriber.customer?.mobile), fixable: true });
  }

  const crmAddress = [customer.address?.line1, customer.address?.line2].filter(Boolean).join(', ');
  const gtplAddress = subscriber.address?.line1 || '';
  rows.push({ label: 'Address', crm: crmAddress || null, gtpl: gtplAddress || null, match: gtplTextsMatch(crmAddress, gtplAddress), fixable: true });

  const gtplStatusRaw = (subscriber.stb?.status || '').trim();
  const gtplActive = /inactive/i.test(gtplStatusRaw) ? false : (/active/i.test(gtplStatusRaw) ? true : null);
  const crmActive = !!customer.catv?.isActive;
  rows.push({
    label: 'Status',
    crm: crmActive ? 'Active' : 'Inactive',
    gtpl: gtplStatusRaw || null,
    match: gtplActive === null ? null : crmActive === gtplActive,
  });

  const packages = subscriber.packages || [];
  const activePkgs = packages.filter(p => /active/i.test(p.status || '') && !/inactive/i.test(p.status || ''));
  const relevantPkgs = activePkgs.length ? activePkgs : packages;
  const crmPkgName = customer.catv?.package?.displayName || customer.catv?.package?.name || '';
  const gtplPkgNames = relevantPkgs.map(p => p.name).filter(Boolean).join(', ');
  rows.push({
    label: 'Package',
    crm: crmPkgName || null,
    gtpl: gtplPkgNames || null,
    match: (!crmPkgName || !gtplPkgNames) ? null : relevantPkgs.some(p => gtplTextsMatch(crmPkgName, p.name)),
  });

  const gtplExpiryDates = relevantPkgs.map(p => parseGtplDate(p.endDate)).filter(Boolean);
  const gtplExpiry = gtplExpiryDates.length ? new Date(Math.max(...gtplExpiryDates.map(d => d.getTime()))) : null;
  const crmExpiry = customer.catv?.expiryDate ? new Date(customer.catv.expiryDate) : null;
  rows.push({
    label: 'Expiry Date',
    crm: crmExpiry ? fmtDate(crmExpiry) : null,
    gtpl: relevantPkgs.map(p => p.endDate).filter(Boolean).join(', ') || null,
    match: (!crmExpiry || !gtplExpiry) ? null : isSameDay(crmExpiry, gtplExpiry),
  });

  return rows;
}

// Shown once a GTPL search returns a result for a customer that already has a CRM record —
// answers "is GTPL's data the same as my CRM" field-by-field instead of making the user eyeball
// the raw scrape against the edit form. `rows` is computed once by the caller (buildGtplCrmComparison)
// and passed in so the "Push CRM → GTPL" button below can act on the exact same mismatch list
// shown to the user. Pushing writes Name/Address straight to GTPL's Modify.aspx via the same
// modifyProfile() the standalone GTPL Saathi → Modify page uses — see handlePushFix in
// GtplSaathiPanel. Mobile is never included in that Modify.aspx submission (GTPL rejects the
// whole save with "-1:Not Allowed to Update KYC Verified Customer Details" for any KYC-verified
// subscriber otherwise); a mobile-number push instead always raises a real GTPL eSupport ticket
// server-side (complaintType 540, "Update Customer Mobile Number") — that's real, and worth
// surfacing before the click.
function GtplCrmCompareCard({ rows, canModify, pushing, onPushFix }) {
  const mismatches = rows.filter(r => r.match === false).length;
  const fixableMismatches = rows.filter(r => r.fixable && r.match === false);
  const mobileNeedsFix = fixableMismatches.some(r => r.label === 'Mobile');
  return (
    <div style={{ background: mismatches ? 'var(--cb-fef2f2)' : 'var(--cb-f0fdf4)', border: `1px solid ${mismatches ? 'var(--cb-fecaca)' : 'var(--cb-bbf7d0)'}`, borderRadius: 8, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 10, color: mismatches ? 'var(--c-991b1b)' : 'var(--c-166534)' }}>
        <ScanSearch size={14} />
        {mismatches ? `${mismatches} mismatch${mismatches > 1 ? 'es' : ''} vs CRM` : 'Matches CRM'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 18px', gap: '6px 10px', fontSize: '.78rem', color: 'var(--c-9ca3af)', marginBottom: 4 }}>
        <span>Field</span><span>Our CRM</span><span>GTPL Saathi</span><span />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 18px', gap: '6px 10px', fontSize: '.82rem', alignItems: 'center' }}>
            <span style={{ color: 'var(--c-6b7280)', fontWeight: 500 }}>{r.label}</span>
            <span>{r.crm || '—'}</span>
            <span>{r.gtpl || '—'}</span>
            {r.match === true && <CheckCircle size={15} color="var(--c-16a34a)" />}
            {r.match === false && <XCircle size={15} color="var(--c-dc2626)" />}
            {r.match === null && <span style={{ color: 'var(--c-9ca3af)' }} title="Couldn't confidently compare this field">?</span>}
          </div>
        ))}
      </div>

      {fixableMismatches.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,.08)' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={onPushFix} disabled={pushing || !canModify}>
            <UploadCloud size={14} style={{ marginRight: 6 }} />
            {pushing ? 'Pushing to GTPL…' : `Push CRM → GTPL (${fixableMismatches.map(r => r.label).join(', ')})`}
          </button>
          {!canModify && (
            <span style={{ fontSize: '.78rem', color: 'var(--c-b91c1c)', marginLeft: 10 }}>You don't have permission to modify profiles on GTPL Saathi.</span>
          )}
          {mobileNeedsFix && (
            <div style={{ fontSize: '.78rem', color: 'var(--c-92400e)', marginTop: 8 }}>
              Mobile is out of sync — GTPL doesn't apply a mobile-number edit directly, so this will also raise a real GTPL eSupport
              "Update Customer Mobile Number" ticket for this account automatically, same as the standalone Modify page.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Area Select ─────────────────────────────────────────────────────────────
function AreaSelect({ value, onChange, areas, style }) {
  const [adding,  setAdding]  = useState(false);
  const [newArea, setNewArea] = useState('');
  const inputRef = React.useRef(null);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const confirm = () => {
    const n = newArea.trim();
    if (!n) return;
    onChange(n);
    setNewArea('');
    setAdding(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <select style={{ ...style, flex: 1 }} value={value} onChange={e => onChange(e.target.value)}>
          <option value="">-- Select Area --</option>
          {areas.map(a => <option key={a} value={a}>{a}</option>)}
          {value && !areas.includes(value) && <option value={value}>{value}</option>}
        </select>
        <button
          type="button"
          title="Add new area"
          onClick={() => { setAdding(a => !a); setNewArea(''); }}
          style={{
            flexShrink: 0, width: 32, borderRadius: 5,
            border: `1.5px solid ${adding ? 'var(--accent)' : 'var(--cb-d1d5db)'}`,
            background: adding ? 'var(--cb-e0f2fe)' : 'transparent',
            color: adding ? 'var(--c-0284c7)' : 'var(--c-6b7280)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Plus size={14} />
        </button>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            ref={inputRef}
            value={newArea}
            onChange={e => setNewArea(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } if (e.key === 'Escape') { setAdding(false); setNewArea(''); } }}
            placeholder="New area name…"
            style={{ flex: 1, border: '1px solid var(--cb-93c5fd)', borderRadius: 5, padding: '4px 8px', fontSize: '.83rem', outline: 'none' }}
          />
          <button type="button" onClick={confirm} disabled={!newArea.trim()}
            style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: 'var(--accent)', color: 'var(--c-ffffff)', cursor: 'pointer', fontSize: '.82rem', fontWeight: 600, opacity: !newArea.trim() ? 0.5 : 1 }}>
            Add
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewArea(''); }}
            style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid var(--cb-e5e7eb)', background: 'transparent', cursor: 'pointer', color: 'var(--c-6b7280)' }}>
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Collect Payment Panel ────────────────────────────────────────────────────
// Slide switch controlling the auto WhatsApp bill/receipt for a single payment — shared by
// the Renew and Collect Payment panels; the same control exists in the agent app's Renew screen.
// Compact stat strip used at the top of the customer panels: one row of small tiles that wraps
// on narrow screens (replaces the tall centred blocks).
function SummaryStrip({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 14, background: 'var(--border)' }}>
      {items.filter(Boolean).map(t => (
        <div key={t.label} style={{ padding: '10px 14px', background: 'var(--bg-card)', minWidth: 0 }}>
          <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>{t.label}</div>
          <div style={{ fontWeight: 700, fontSize: '.98rem', overflowWrap: 'anywhere' }}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}

function SendMessageToggle({ checked, onChange }) {
  return (
    <div onClick={() => onChange(!checked)} role="switch" aria-checked={checked}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
      <span style={{
        width: 40, height: 22, borderRadius: 11, position: 'relative', flexShrink: 0,
        background: checked ? 'var(--cb-16a34a)' : 'var(--cb-d1d5db)', transition: 'background .15s',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 20 : 2, width: 18, height: 18,
          borderRadius: '50%', background: 'var(--cb-ffffff)', transition: 'left .15s',
          boxShadow: '0 1px 2px rgba(0,0,0,.25)',
        }} />
      </span>
      <span style={{ fontSize: '.85rem', fontWeight: 600, color: checked ? 'var(--c-15803d)' : 'var(--c-6b7280)' }}>
        Send message to customer
      </span>
    </div>
  );
}

// ─── Split Payment Fields ────────────────────────────────────────────────────
// One collection paid across multiple modes at once (e.g. ₹500 cash + ₹300 UPI) — see
// paymentService.collectPayment's splitPayments handling. `value` is null when off (the
// caller's own single Mode/Transaction ID fields apply as before) or an array of
// {method, amount, transactionId} rows that must sum to exactly `total`.
function SplitPaymentFields({ total, value, onChange }) {
  const rows = value || [];
  const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remaining = Math.round(total || 0) - sum;
  const updateRow = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const smallInput = { padding: '6px 8px', border: '1px solid var(--cb-d1d5db)', borderRadius: 5, fontSize: '.85rem' };

  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={e => onChange(e.target.checked ? [{ method: 'cash', amount: '', transactionId: '' }, { method: 'upi', amount: '', transactionId: '' }] : null)}
        />
        Split this payment across multiple modes (e.g. part cash, part UPI)
      </label>
      {value && (
        <div style={{ marginTop: 8, padding: 10, border: '1px dashed var(--cb-d1d5db)', borderRadius: 6, background: 'var(--cb-f9fafb)' }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 150px' }}>
                <PaymentMethodSelect value={r.method} onChange={v => updateRow(i, { method: v })} />
              </div>
              <div style={{ position: 'relative', flex: '0 0 110px' }}>
                <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-6b7280)', fontSize: '.85rem' }}>₹</span>
                <input type="number" min="0" value={r.amount} onChange={e => updateRow(i, { amount: e.target.value })}
                  style={{ ...smallInput, width: '100%', paddingLeft: 20, boxSizing: 'border-box' }} placeholder="0" />
              </div>
              {r.method !== 'cash' && (
                <input placeholder="Ref / UTR (optional)" value={r.transactionId}
                  onChange={e => updateRow(i, { transactionId: e.target.value })}
                  style={{ ...smallInput, flex: '1 1 140px' }} />
              )}
              {rows.length > 2 && (
                <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
                  style={{ border: 'none', background: 'transparent', color: 'var(--c-dc2626)', cursor: 'pointer', fontSize: '1rem' }}>×</button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginBottom: 6 }}
            onClick={() => onChange([...rows, { method: 'cash', amount: '', transactionId: '' }])}>
            + Add another mode
          </button>
          <div style={{ fontSize: '.78rem', color: remaining === 0 ? 'var(--c-059669)' : 'var(--c-b45309)' }}>
            {remaining === 0
              ? `Splits add up to ₹${fmt(total)}.`
              : `₹${fmt(Math.abs(remaining))} ${remaining > 0 ? 'still unassigned' : 'over the total'} of ₹${fmt(total)}.`}
          </div>
        </div>
      )}
    </div>
  );
}

function CollectPaymentPanel({ customer, payments, onSuccess }) {
  const lastPayment = payments[0] || null;
  const planPrice =
    customer.catv?.package?.price ||
    customer.broadband?.plan?.price || 0;

  const balance = customer.outstandingBalance ?? 0;
  const expiryDate =
    customer.catv?.expiryDate ||
    customer.broadband?.expiryDate ||
    null;

  const [form, setForm] = useState({
    paymentFor: customer.customerType === 'catv' ? 'catv_renewal' : 'broadband_renewal',
    amount: balance > 0 ? String(balance) : '',
    discount: '',
    paymentMethod: 'cash',
    transactionId: '',
    recordTime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    notes: '',
  });
  const [sendMessage, setSendMessage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  // Split payment (part cash, part UPI/etc. in one collection) — null when off, else an
  // array of {method, amount, transactionId} rows that must sum to `total` below.
  const [splitRows, setSplitRows] = useState(null);
  const [upiConfig, setUpiConfig] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrMode, setQrMode] = useState('balance'); // 'balance' | 'custom' — see QR Code modal below
  const [qrCustomAmount, setQrCustomAmount] = useState('');
  const [sendingQr, setSendingQr] = useState(false);
  const [msgTemplates, setMsgTemplates] = useState([]);

  React.useEffect(() => {
    configAPI.get().then(({ data }) => setUpiConfig(data?.data)).catch(() => {});
  }, []);

  // Fetched lazily (only once the Advance Payment Link modal is actually opened) so this
  // panel doesn't fire an extra request on every customer page load.
  React.useEffect(() => {
    if (!linkOpen || msgTemplates.length > 0) return;
    messagingAPI.getTemplates().then(({ data }) => setMsgTemplates(data?.data || [])).catch(() => {});
  }, [linkOpen]); // eslint-disable-line

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ type: 'reduce', amount: '', reason: '' });
  const [adjustSaving, setAdjustSaving] = useState(false);

  const handleAdjust = async () => {
    const amt = Number(adjustForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!adjustForm.reason.trim()) { toast.error('A reason is required'); return; }
    setAdjustSaving(true);
    try {
      const { data } = await customerAPI.adjustBalance(customer._id, { amount: amt, type: adjustForm.type, reason: adjustForm.reason.trim() });
      toast.success(data.message || 'Balance adjusted');
      setAdjustOpen(false);
      setAdjustForm({ type: 'reduce', amount: '', reason: '' });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Adjustment failed');
    } finally {
      setAdjustSaving(false);
    }
  };

  const handleLocation = () => {
    const { coordinates, line1, line2, area, city, pincode } = customer.address || {};
    const url = coordinates?.lat && coordinates?.lng
      ? `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([line1, line2, area, city, pincode].filter(Boolean).join(', '))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSendPaymentQr = async (mode, amount) => {
    setSendingQr(true);
    try {
      const { data } = await customerAPI.sendPaymentQr(customer._id, { mode, amount });
      toast.success(data.message || 'QR sent on WhatsApp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send QR');
    } finally {
      setSendingQr(false);
    }
  };

  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      const { data } = await customerAPI.sendReminder(customer._id);
      toast.success(data.message || 'Reminder sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reminder failed');
    } finally {
      setSendingReminder(false);
    }
  };

  // CableTV and Broadband each have their own UPI entries (Settings > Payments) —
  // 'both' customers default to CableTV, matching this page's own service priority elsewhere.
  const upiIsCatv = customer.customerType === 'catv' || customer.customerType === 'both';
  const upiId = (upiIsCatv ? upiConfig?.catvUpiEntries?.[0]?.upiId : upiConfig?.upiEntries?.[0]?.upiId);
  const upiPayeeName = upiIsCatv ? upiConfig?.catvUpiName : upiConfig?.upiName;
  const upiLink = balance > 0 && upiId
    ? `upi://pay?pa=${upiId}&pn=${encodeURIComponent(upiPayeeName || 'AngelNet')}&am=${balance}&cu=INR&tn=${encodeURIComponent(`Payment for ${customer.name} - ${customer.customerId}`)}`
    : null;
  // Bare gateway link (no ?amt=) for the standalone QR Code button below — always
  // resolves to whatever's actually due when the customer's phone scans it, same as the
  // agent app's own QR Code button (AgentCustomerDetailPage.js's barePayLink).
  const barePayLink = customer.paymentLinkGatewayEnabled && customer.paymentLink ? customer.paymentLink : null;

  const paid = parseFloat(form.amount) || 0;
  const disc = parseFloat(form.discount) || 0;
  const total = Math.max(0, paid - disc);
  const newBal = balance - total;
  const splitSum = (splitRows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const splitValid = !splitRows || (splitRows.length >= 2 && splitRows.every(r => r.method && Number(r.amount) > 0) && Math.round(splitSum) === Math.round(total));

  const handleRecord = async (e) => {
    e.preventDefault();
    if (!paid) { toast.error('Enter a paid amount'); return; }
    if (splitRows && !splitValid) { toast.error(`Split amounts must add up to ₹${fmt(total)}`); return; }
    setLoading(true);
    try {
      const { data } = await paymentAPI.create({
        customer: customer._id,
        paymentFor: form.paymentFor,
        amount: total,
        notes: form.notes,
        sendMessage,
        ...(splitRows
          ? { paymentMethod: 'split', splitPayments: splitRows.map(r => ({ method: r.method, amount: Number(r.amount) || 0, transactionId: r.transactionId || undefined })) }
          : { paymentMethod: form.paymentMethod, transactionId: form.transactionId }),
        // "Record Time" (form.recordTime, the datetime-local input above) was being
        // collected but never actually sent — the payment always landed on paymentDate:
        // now regardless of what was picked, since paymentService.collectPayment defaults
        // to `new Date()` whenever paymentDate is omitted.
        ...(form.recordTime ? { paymentDate: form.recordTime } : {}),
      });
      toast.success(`Payment recorded! Receipt: ${data.data.receiptNumber}`);
      if (data.imbsSync?.attempted) toast('Syncing to Netzur…', { icon: '🔄' });
      // Reset recordTime back to now too — otherwise a backdated pick would silently
      // carry over and apply to the next payment recorded in this same panel.
      setForm(f => ({ ...f, amount: '', discount: '', notes: '', transactionId: '', recordTime: format(new Date(), "yyyy-MM-dd'T'HH:mm") }));
      setSplitRows(null);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)',
    borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box',
    background: 'var(--cb-ffffff)',
  };
  const labelStyle = { fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' };

  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Collect Payment</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }} onClick={handleLocation}>
            <MapPin size={12} /> Customer Location
          </button>
          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }} onClick={() => setLinkOpen(true)} disabled={balance <= 0}>
            <CreditCard size={12} /> Advance Payment Link
          </button>
          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }}
            onClick={() => { setQrMode('balance'); setQrCustomAmount(''); setQrOpen(true); }}>
            <QrCode size={12} /> QR Code
          </button>
          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }} onClick={handleSendReminder} disabled={sendingReminder}>
            <MessageSquare size={12} /> {sendingReminder ? 'Sending…' : 'Share Reminder'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }}
            onClick={() => { setAdjustForm({ type: 'reduce', amount: '', reason: '' }); setAdjustOpen(true); }}>
            <Wallet size={12} /> Adjust Amount
          </button>
        </div>
      </div>

      {adjustOpen && (
        <div className="modal-overlay" onClick={() => !adjustSaving && setAdjustOpen(false)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adjust Amount</h3>
              <button className="modal-close" onClick={() => setAdjustOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
                Current due: <strong>₹{fmt(balance)}</strong> — adjustments are recorded in System Logs.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, marginBottom: 10 }}>
                <select className="form-control" value={adjustForm.type} onChange={e => setAdjustForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="reduce">Reduce (−)</option>
                  <option value="add">Add (+)</option>
                </select>
                <input className="form-control" type="number" min="0" placeholder="Amount ₹"
                  value={adjustForm.amount} onChange={e => setAdjustForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <input className="form-control" placeholder="Reason (required)"
                value={adjustForm.reason} onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAdjustOpen(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={adjustSaving} onClick={handleAdjust}>
                {adjustSaving ? 'Saving…' : 'Save Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleRecord}>
        {/* Summary strip: four compact tiles in one row (wraps to 2x2 on narrow screens) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 14, background: 'var(--border)' }}>
          {[
            { label: 'Balance', node: <span style={{ fontWeight: 800, fontSize: '1.15rem', color: balance > 0 ? 'var(--c-dc2626)' : 'var(--text)' }}>₹{fmt(balance)}</span> },
            ...(expiryDate ? [{ label: 'Till date', node: <span style={{ fontWeight: 700, fontSize: '.98rem' }}>{fmtDate(expiryDate)}</span> }] : []),
            { label: 'Last bill', node: <span style={{ fontWeight: 700, fontSize: '.98rem' }}>₹{fmt(planPrice)}</span> },
            { label: 'Last payment', node: <><span style={{ fontWeight: 700, fontSize: '.98rem' }}>{lastPayment ? `₹${fmt(lastPayment.totalAmount)}` : '—'}</span>{lastPayment && <span style={{ marginLeft: 6, fontSize: '.72rem', color: 'var(--text-muted)' }}>{fmtDate(lastPayment.paymentDate)}</span>}</> },
          ].map(t => (
            <div key={t.label} style={{ padding: '10px 14px', background: 'var(--bg-card)', minWidth: 0 }}>
              <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>{t.label}</div>
              <div>{t.node}</div>
            </div>
          ))}
        </div>

        {/* Form: two balanced columns that collapse to one on narrow screens */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '10px 16px' }}>
          {customer.customerType === 'catv' ? (
            <div>
              <label style={labelStyle}>Payment For</label>
              <div style={{ ...inputStyle, background: 'var(--cb-f3f4f6)', color: 'var(--c-374151)' }}>CATV Renewal</div>
            </div>
          ) : (
            <div>
              <label style={labelStyle}>Payment For</label>
              <select style={inputStyle} value={form.paymentFor} onChange={e => setForm(f => ({ ...f, paymentFor: e.target.value }))}>
                {(customer.customerType === 'broadband') && <option value="broadband_renewal">Broadband Renewal</option>}
                <option value="installation">Installation</option>
                <option value="security_deposit">Security Deposit</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}
          {!splitRows && (
            <div>
              <label style={labelStyle}>Mode</label>
              <PaymentMethodSelect style={inputStyle} value={form.paymentMethod} onChange={v => setForm(f => ({ ...f, paymentMethod: v }))} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Paid Amount</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-6b7280)', fontSize: '.88rem' }}>₹</span>
              <input type="number" min="0" style={{ ...inputStyle, paddingLeft: 24 }} value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Discount</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-6b7280)', fontSize: '.88rem' }}>₹</span>
              <input type="number" min="0" style={{ ...inputStyle, paddingLeft: 24 }} value={form.discount}
                onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} placeholder="Discount" />
            </div>
          </div>
          {!splitRows && (form.paymentMethod !== 'cash' && form.paymentMethod !== 'cheque') && (
            <div>
              <label style={labelStyle}>Transaction ID <span style={{ color: 'var(--c-9ca3af)', fontWeight: 400 }}>(optional)</span></label>
              <input style={inputStyle} value={form.transactionId}
                onChange={e => setForm(f => ({ ...f, transactionId: e.target.value }))}
                placeholder="Reference — leave blank if unknown" />
            </div>
          )}
          <div>
            <label style={labelStyle}>Record Time</label>
            <input type="datetime-local" style={inputStyle} value={form.recordTime}
              onChange={e => setForm(f => ({ ...f, recordTime: e.target.value }))} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <SplitPaymentFields total={total} value={splitRows} onChange={setSplitRows} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Comment</label>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 44 }} rows={1}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional comment..." />
          </div>
        </div>

        {/* Action bar: totals on the left, message toggle + Record on the right */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Total payment</div>
              <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>₹{fmt(total)}</div>
            </div>
            <div>
              <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>New balance</div>
              <div style={{ fontWeight: 800, fontSize: '1.1rem', color: newBal > 0 ? 'var(--c-dc2626)' : 'var(--text)' }}>₹{fmt(newBal)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <SendMessageToggle checked={sendMessage} onChange={setSendMessage} />
              <div style={{ fontSize: '.7rem', color: 'var(--c-9ca3af)', marginTop: 2 }}>
                {sendMessage ? 'WhatsApp receipt will be sent.' : 'No message will be sent.'}
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ background: 'var(--cb-1e293b)', borderColor: 'var(--cb-1e293b)', minWidth: 120 }}>
              <Receipt size={14} /> {loading ? 'Recording…' : 'Record'}
            </button>
          </div>
        </div>
      </form>

      {linkOpen && (
        <div onClick={() => setLinkOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--cb-ffffff)', borderRadius: 12, padding: 24, width: 320,
            textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '.95rem', fontWeight: 700 }}>Advance Payment Link</h3>
            {(() => {
              const paymentLinkTpl = pickTemplate(msgTemplates, 'payment_link')?.body;
              const buildText = (link, fallback) => paymentLinkTpl
                ? fillTemplate(paymentLinkTpl, { name: customer.name, amount: fmt(balance), link })
                : fallback;
              return customer.paymentLinkGatewayEnabled && balance > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                    <QRCode value={customer.paymentLink} size={160} />
                  </div>
                  <div style={{ fontWeight: 700, marginBottom: 12 }}>₹{fmt(balance)}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
                    Opens a secure page where the customer can pay by UPI, card or net banking.
                  </div>
                  <a
                    href={`https://wa.me/91${String(customer.mobile || '').replace(/\D/g, '').slice(-10)}?text=${encodeURIComponent(buildText(customer.paymentLink, `Dear ${customer.name}, please pay ₹${fmt(balance)} using this secure link: ${customer.paymentLink}`))}`}
                    target="_blank" rel="noopener noreferrer"
                    className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}
                  >
                    Share via WhatsApp
                  </a>
                </>
              ) : upiLink ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                    <QRCode value={upiLink} size={160} />
                  </div>
                  <div style={{ fontWeight: 700, marginBottom: 12 }}>₹{fmt(balance)}</div>
                  <a
                    href={`https://wa.me/91${String(customer.mobile || '').replace(/\D/g, '').slice(-10)}?text=${encodeURIComponent(buildText(upiLink, `Dear ${customer.name}, please pay ₹${fmt(balance)} using this UPI link: ${upiLink}`))}`}
                    target="_blank" rel="noopener noreferrer"
                    className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}
                  >
                    Share via WhatsApp
                  </a>
                </>
              ) : null;
            })()}
            {!(customer.paymentLinkGatewayEnabled && balance > 0) && !upiLink && (
              <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
                {!upiId ? `UPI payment is not configured yet (Settings → Payments → ${upiIsCatv ? 'CableTV' : 'Broadband'}).` : 'No due balance to collect.'}
              </p>
            )}
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 12, width: '100%', justifyContent: 'center' }} onClick={() => setLinkOpen(false)}>Close</button>
          </div>
        </div>
      )}

      {qrOpen && (() => {
        // Two modes in one modal: 'balance' — same source as the Advance Payment Link
        // modal above (gateway link preferred, raw UPI deep-link as fallback), tied to
        // whatever's actually due. 'custom' — a typed amount instead, for a prepaid
        // broadband customer with nothing currently due who still wants to pay ahead for
        // next month (replaces generating that UPI QR by hand outside the CRM). Bare
        // gateway link (no ?amt=) in balance mode always charges whatever's live-due when
        // scanned; custom mode appends ?amt= like the agent app's own Advance Link.
        const customAmt = parseFloat(qrCustomAmount) || 0;
        const customGatewayLink = customAmt > 0 && customer.paymentLinkGatewayEnabled && customer.paymentLink
          ? `${customer.paymentLink}?amt=${Math.round(customAmt)}` : null;
        const customUpiLink = customAmt > 0 && upiId
          ? `upi://pay?pa=${upiId}&pn=${encodeURIComponent(upiPayeeName || 'AngelNet')}&am=${Math.round(customAmt)}&cu=INR&tn=${encodeURIComponent(`Payment for ${customer.name} - ${customer.customerId}`)}`
          : null;
        const qrValue = qrMode === 'custom' ? (customGatewayLink || customUpiLink) : (barePayLink || upiLink);
        const qrAmount = qrMode === 'custom' ? customAmt : balance;
        return (
          <div onClick={() => setQrOpen(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'var(--cb-ffffff)', borderRadius: 12, padding: 24, width: 320,
              textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}>
              <h3 style={{ margin: '0 0 14px', fontSize: '.95rem', fontWeight: 700 }}>Payment QR Code</h3>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                <button type="button" onClick={() => setQrMode('balance')}
                  className={`btn btn-sm ${qrMode === 'balance' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, fontSize: '.78rem' }}>
                  Outstanding Balance
                </button>
                <button type="button" onClick={() => setQrMode('custom')}
                  className={`btn btn-sm ${qrMode === 'custom' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, fontSize: '.78rem' }}>
                  Custom Amount
                </button>
              </div>
              {qrMode === 'custom' && (
                <input type="number" min="1" autoFocus className="form-control" placeholder="Enter amount"
                  value={qrCustomAmount} onChange={e => setQrCustomAmount(e.target.value)}
                  style={{ marginBottom: 14, textAlign: 'center' }} />
              )}
              {qrValue ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                    <QRCode value={qrValue} size={180} />
                  </div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>₹{fmt(qrAmount)}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginBottom: 12 }}>Scan to pay by UPI, card or net banking</div>
                  <button type="button" className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }}
                    disabled={sendingQr || !customer.mobile}
                    onClick={() => handleSendPaymentQr(qrMode, qrAmount)}>
                    {sendingQr ? 'Sending…' : 'Send via WhatsApp'}
                  </button>
                  {!customer.mobile && (
                    <div style={{ fontSize: '.7rem', color: 'var(--c-dc2626)', marginTop: 6 }}>No mobile number on file for this customer.</div>
                  )}
                </>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
                  {qrMode === 'custom'
                    ? (customAmt > 0 ? `UPI payment is not configured yet (Settings → Payments → ${upiIsCatv ? 'CableTV' : 'Broadband'}).` : 'Enter an amount above to generate a QR.')
                    : (balance > 0
                        ? `Payment gateway is not configured yet (Settings → Payments → ${upiIsCatv ? 'CableTV' : 'Broadband'}).`
                        : 'No due balance to collect.')}
                </p>
              )}
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 12, width: '100%', justifyContent: 'center' }} onClick={() => setQrOpen(false)}>Close</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Balance History Panel ─────────────────────────────────────────────────
// Same ledger construction and behavior as the agent app's Balance Sheet
// (frontend/src/pages/agent/AgentLedgerPage.js + GET /api/v1/payments/customer/:id/ledger,
// which mirrors GET /api/v1/agent/customers/:id/ledger field-for-field) — bills, payments and
// Collection Pending visits together with a running balance, all three deletable with the
// same balance/expiry-reversal logic either surface uses. Edit/Transfer/Delete are gated per
// the payments.edit/transferService/delete Site Role rights (same as the backend routes) rather
// than admin-only, so a staff role can be granted just the ones it needs — e.g. Edit Payment to
// fix a payment method without also getting Delete. (Edit looks its target up from the
// already-fetched `payments` list by paymentId since the ledger entries themselves don't carry
// transactionId/notes/paymentFor.)
function BalanceHistoryPanel({ payments, customer, canEditPayment, canTransferPayment, canDeletePayment, onEditPayment, onSuccess, isAppView }) {
  const { isAdmin } = useAuth();
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState(null);
  const [transferringKey, setTransferringKey] = useState(null);
  const [viewingKey, setViewingKey] = useState(null);
  const [sendingKey, setSendingKey] = useState(null);
  // Combo ('both') customers get separate CableTV / Broadband views since a single merged
  // table can't tell the two apart at a glance — single-service customers never see these tabs.
  const [serviceTab, setServiceTab] = useState('all');
  // Admin-only manual expiry override (see routes/customers.js edit-validity) — for
  // correcting a validity date that's wrong for reasons outside the normal renewal math,
  // e.g. the 2026-08-25 expiry-drift bug (unpaid bills auto-advancing expiry). Bypasses
  // collectPayment entirely: no Payment/Renewal created, balance untouched, just the date.
  const [editingValidity, setEditingValidity] = useState(null); // { service, current } | null
  const [validityDate, setValidityDate] = useState('');
  const [validityReason, setValidityReason] = useState('');
  const [validitySaving, setValiditySaving] = useState(false);

  const openValidityEdit = (service, current) => {
    setEditingValidity({ service, current });
    setValidityDate(current ? format(new Date(current), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'));
    setValidityReason('');
  };

  const handleValiditySave = async () => {
    if (!validityDate) { toast.error('Pick a date'); return; }
    if (!validityReason.trim()) { toast.error('A reason is required'); return; }
    setValiditySaving(true);
    try {
      await customerAPI.editValidity(customer._id, {
        service: editingValidity.service, expiryDate: validityDate, reason: validityReason.trim(),
      });
      toast.success('Validity updated');
      setEditingValidity(null);
      load();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update validity');
    } finally {
      setValiditySaving(false);
    }
  };

  // Admin-only fix of one renewal entry's validity period (see routes/payments.js
  // PATCH :id/validity). "Also move the customer's validity" defaults on only for the
  // newest paid entry of that service — correcting an old entry shouldn't touch today's expiry.
  const [editingPeriod, setEditingPeriod] = useState(null); // ledger entry | null
  const [periodForm, setPeriodForm] = useState({ from: '', to: '', days: '', reason: '', updateExpiry: false });

  // From/To/Days stay linked: From or Days moves To (inclusive count, so 30 days from
  // 1 Sep ends 30 Sep); editing To recomputes Days.
  const parseYmd = (s) => parse(s, 'yyyy-MM-dd', new Date());
  const toFromDays = (from, days) => (from && days > 0 ? format(addDays(parseYmd(from), days - 1), 'yyyy-MM-dd') : '');
  const daysBetween = (from, to) => (from && to && to >= from ? differenceInDays(parseYmd(to), parseYmd(from)) + 1 : '');
  const setPeriodFrom = (from) => setPeriodForm(f => ({ ...f, from, to: f.days ? toFromDays(from, Number(f.days)) : f.to, days: f.days || daysBetween(from, f.to) }));
  const setPeriodTo = (to) => setPeriodForm(f => ({ ...f, to, days: daysBetween(f.from, to) }));
  const setPeriodDays = (raw) => {
    const days = raw === '' ? '' : Math.max(1, parseInt(raw, 10) || 1);
    setPeriodForm(f => ({ ...f, days, to: days ? toFromDays(f.from, days) : f.to }));
  };
  const [periodSaving, setPeriodSaving] = useState(false);
  const [importingHistory, setImportingHistory] = useState(false);

  const openPeriodEdit = (e) => {
    const newestPaid = ledger.entries
      .filter(x => x.type === 'payment' && x.paymentFor === e.paymentFor && x.periodTo)
      .sort((a, b) => new Date(b.periodTo) - new Date(a.periodTo))[0];
    setEditingPeriod(e);
    const from = e.periodFrom ? format(new Date(e.periodFrom), 'yyyy-MM-dd') : format(new Date(e.date), 'yyyy-MM-dd');
    const to = e.periodTo ? format(new Date(e.periodTo), 'yyyy-MM-dd') : '';
    setPeriodForm({
      from, to, days: daysBetween(from, to),
      reason: '',
      updateExpiry: e.type === 'payment' && (!newestPaid || newestPaid.paymentId === e.paymentId),
    });
  };

  const handlePeriodSave = async () => {
    if (!periodForm.from || !periodForm.to) { toast.error('Pick From and To dates'); return; }
    if (periodForm.to < periodForm.from) { toast.error('To date must be on or after From date'); return; }
    if (!periodForm.reason.trim()) { toast.error('A reason is required'); return; }
    setPeriodSaving(true);
    try {
      await paymentAPI.editValidity(editingPeriod.paymentId, {
        periodFrom: periodForm.from, periodTo: periodForm.to,
        reason: periodForm.reason.trim(), updateExpiry: periodForm.updateExpiry,
      });
      toast.success('Validity updated');
      setEditingPeriod(null);
      load();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update validity');
    } finally {
      setPeriodSaving(false);
    }
  };

  // Shared by the mobile-card and desktop-table ledger layouts.
  const renderValidityLine = (e, extraStyle) => {
    const editable = isAdmin && e.paymentId && ['catv_renewal', 'broadband_renewal'].includes(e.paymentFor);
    if (!(e.periodFrom && e.periodTo) && !editable) return null;
    return (
      <div style={{ marginTop: 2, fontSize: '.75rem', color: 'var(--c-9ca3af)', ...extraStyle }}>
        {e.periodFrom && e.periodTo
          ? <>Validity: {fmtDate(e.periodFrom)} to {fmtDate(e.periodTo)} ({differenceInDays(new Date(e.periodTo), new Date(e.periodFrom)) + 1} days){e.periodEstimated ? ' (approx.)' : ''}</>
          : 'Validity: —'}
        {editable && (
          <button type="button" onClick={() => openPeriodEdit(e)}
            style={{ marginLeft: 6, border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-2563eb)', fontSize: '.75rem', textDecoration: 'underline' }}>
            Edit
          </button>
        )}
      </div>
    );
  };

  const isCombo = customer.customerType === 'both';
  // Either the same customer record holds both services (relabel in place), or there's a
  // linked sibling record for the other service (actually moves the payment + its balance
  // effect across — see handleTransfer/PATCH :id/transfer-service's linked-account case).
  const canTransfer = isCombo || !!customer.linkedAccount;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await paymentAPI.getLedger(customer._id);
      setLedger(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load balance history');
    } finally {
      setLoading(false);
    }
  }, [customer._id]);

  useEffect(() => { load(); }, [load]);

  const canDeleteEntry = (entry) => !!entry.billId || !!entry.paymentId || !!entry.attemptId || !!entry.changeLogId;

  // Fetches our own generated bill/receipt PDF as an authenticated blob (a plain <a href>
  // to this staffOrAdmin route would 401 — the JWT only ever goes out as a header) and opens
  // it in a new tab as a real PDF, instead of a link that silently failed.
  const handleViewReceipt = async (paymentId) => {
    setViewingKey(paymentId);
    try {
      const { data } = await paymentAPI.getReceiptBlob(paymentId);
      const url = URL.createObjectURL(data);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Failed to open invoice');
    } finally {
      setViewingKey(null);
    }
  };

  // Re-sends the invoice/receipt on WhatsApp on demand — same message the auto-notification
  // sends at renewal time, useful when a customer says they never got it or asks again later.
  const handleSendBill = async (paymentId) => {
    setSendingKey(paymentId);
    try {
      const { data } = await paymentAPI.shareWhatsApp(paymentId);
      toast.success(data.message || 'Bill sent on WhatsApp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send bill');
    } finally {
      setSendingKey(null);
    }
  };

  const handleDelete = async (entry) => {
    const label = entry.billId ? 'bill' : entry.attemptId ? 'Collection Pending record'
      : entry.changeLogId ? 'balance adjustment' : 'payment';
    if (!window.confirm(`Delete this ${label}? This will reverse its effect on the balance.`)) return;

    const key = entry.billId || entry.paymentId || entry.attemptId || entry.changeLogId;
    setDeletingKey(key);
    try {
      if (entry.billId) await paymentAPI.deleteLedgerBill(customer._id, entry.billId);
      else if (entry.attemptId) await paymentAPI.deleteLedgerAttempt(customer._id, entry.attemptId);
      else if (entry.changeLogId) await paymentAPI.deleteLedgerAdjustment(customer._id, entry.changeLogId);
      else await paymentAPI.deleteLedgerPayment(customer._id, entry.paymentId);
      toast.success('Deleted');
      load();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingKey(null);
    }
  };

  const handleTransfer = async (entry) => {
    const to = entry.serviceType === 'broadband' ? 'catv' : 'broadband';
    const toLabel = to === 'broadband' ? 'Broadband' : 'CableTV';
    const confirmMsg = isCombo
      ? `Move this entry to ${toLabel}? Only its classification (and receipt number) changes — the amount and balance are unaffected.`
      : `Move this entry to the linked ${toLabel} account (${customer.linkedAccount?.name || 'linked customer'})? The payment and its ₹${entry.credit || entry.debit} balance effect move to that customer — this customer's outstanding balance will change.`;
    if (!window.confirm(confirmMsg)) return;
    setTransferringKey(entry.paymentId);
    try {
      const { data } = await paymentAPI.transferService(entry.paymentId, to);
      toast.success(data.message || `Transferred to ${toLabel}`);
      load();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Transfer failed');
    } finally {
      setTransferringKey(null);
    }
  };

  // Uploads this customer's own bix42 history sheet (same columns as Payment History Import).
  // Rows land as HIST- ledger entries only — the balance and validity are not touched.
  const handleHistoryFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    try {
      const rows = await parseHistoryFile(file);
      // bix42's per-customer history sheet: bills and payments described in a DATE text column.
      if ('TXN_AMOUNT' in rows[0] && 'DATE' in rows[0]) {
        const names = [...new Set(rows.map(r => String(r['CUSTOMER NAME'] || '').trim()).filter(Boolean))];
        if (names.length > 1) { toast.error(`This file has more than one customer (${names.slice(0, 3).join(', ')}…). Upload one customer's history at a time.`, { duration: 12000 }); return; }
        const body = { rows: rows.map(r => ({ text: r.DATE, amount: r.TXN_AMOUNT })), fileCustomerName: names[0] || '' };
        setImportingHistory(true);
        const { data: preview } = await historyImportAPI.importBix42Sheet(customer._id, { ...body, dryRun: true });
        const p = preview.data;
        if (p.paymentsInFile + p.billsAdded + p.billsAlreadyThere === 0) { toast.error('No bill or payment rows found in this file, so nothing was imported.', { duration: 12000 }); return; }
        const sameName = !names[0] || names[0].toLowerCase() === String(customer.name || '').trim().toLowerCase();
        const lines = [
          sameName ? null : `⚠ The file is for "${names[0]}" but this customer is "${customer.name}".\n`,
          `${p.paymentsInFile} payment(s) in the file:`,
          `• ${p.alreadyThere} already in this customer's history (not added again)`,
          `• ${p.added} new, will be added`,
          `• ${p.periodsSet} will get the old system's bill period`,
          `\n${p.billsAdded + p.billsAlreadyThere} old bill(s) in the file:`,
          `• ${p.billsAlreadyThere} already in this customer's history (not added again)`,
          `• ${p.billsAdded} new, will be added as history rows`,
          p.unknown.length ? `• ${p.unknown.length} row(s) not understood, skipped (first: "${p.unknown[0].text}")` : null,
          '\nThe current balance and validity will NOT change. Continue?',
        ].filter(Boolean);
        if (!window.confirm(lines.join('\n'))) return;
        const { data: done } = await historyImportAPI.importBix42Sheet(customer._id, body);
        const r = done.data;
        const summary = `Payments: ${r.added} added, ${r.alreadyThere} already there. Bills: ${r.billsAdded} added, ${r.billsAlreadyThere} already there${r.failed ? `, ${r.failed} failed` : ''}`;
        if (r.failed) toast.error(`${summary}. First error: ${r.errors[0]?.error}`, { duration: 12000 });
        else toast.success(summary);
        load();
        return;
      }
      if (!rows.some(r => Number(r.payment_id))) {
        // Unknown layout: nothing is imported; the first rows are kept so the layout can be added.
        await historyImportAPI.saveUnknownLayoutSample({ fileName: file.name, customerId: customer._id, totalRows: rows.length, rows: rows.slice(0, 25) });
        toast.error('This file\'s layout is not supported yet, so nothing was imported. A sample of it has been saved for setup.', { duration: 12000 });
        return;
      }
      if (rows.length > 500) { toast.error('Max 500 rows per upload'); return; }
      const total = rows.reduce((s, r) => s + (Number(r.paid_amount) || 0), 0);
      if (!window.confirm(`Add ${rows.length} old payment(s) totalling ₹${fmt(total)} to ${customer.name}'s history?\n\nThe current balance and validity will NOT change.`)) return;
      setImportingHistory(true);
      const { data } = await historyImportAPI.importForCustomer(customer._id, rows);
      const r = data.data;
      const summary = `${r.imported} added, ${r.alreadyImported} already there${r.failed ? `, ${r.failed} failed` : ''}`;
      if (r.failed) toast.error(`${summary}. First error: ${r.errors[0]?.error}`, { duration: 12000 });
      else toast.success(summary);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Import failed');
    } finally {
      setImportingHistory(false);
    }
  };

  if (loading || !ledger) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-9ca3af)' }}>Loading…</div>;
  }

  const filteredEntries = !isCombo || serviceTab === 'all'
    ? ledger.entries
    : ledger.entries.filter((e) => e.serviceType === serviceTab || e.serviceType == null);

  const validityRows = [
    customer.catv?.package && { service: 'catv', label: 'CableTV Validity', expiry: customer.catv.expiryDate },
    customer.broadband?.plan && { service: 'broadband', label: 'Broadband Validity', expiry: customer.broadband.expiryDate },
  ].filter(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Balance History</h3>
        {isAdmin && (
          <label className="btn btn-secondary btn-sm" style={{ cursor: importingHistory ? 'wait' : 'pointer', margin: 0 }}>
            <UploadCloud size={12} style={{ marginRight: 6 }} />
            {importingHistory ? 'Importing…' : 'Import old history (Excel)'}
            <input type="file" accept=".xlsx,.xls,.csv" hidden disabled={importingHistory} onChange={handleHistoryFile} />
          </label>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <SummaryStat label="Total Billed" value={ledger.totalBilled} />
        <SummaryStat label="Total Paid" value={ledger.totalPaid} color="var(--c-16a34a)" />
        <SummaryStat label="Current Due" value={ledger.outstandingBalance} color={ledger.outstandingBalance > 0 ? 'var(--c-dc2626)' : 'var(--c-16a34a)'} />
      </div>

      {validityRows.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {validityRows.map(({ service, label, expiry }) => (
            <div key={service} style={{
              border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10, background: 'var(--cb-ffffff)',
            }}>
              <div>
                <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)' }}>{label}</div>
                <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{expiry ? fmtDate(expiry) : '—'}</div>
              </div>
              {isAdmin && (
                <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.72rem' }}
                  onClick={() => openValidityEdit(service, expiry)}>
                  Edit
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editingValidity && (
        <div className="modal-overlay" onClick={() => !validitySaving && setEditingValidity(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {editingValidity.service === 'broadband' ? 'Broadband' : 'CableTV'} Validity</h3>
              <button className="modal-close" onClick={() => setEditingValidity(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
                Currently: <strong>{editingValidity.current ? fmtDate(editingValidity.current) : '—'}</strong>. This only
                changes the paid-through date — no payment is created and the balance is unaffected. Recorded in
                System Logs.
              </div>
              <label style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' }}>New Validity Date</label>
              <input className="form-control" type="date" style={{ marginBottom: 10 }}
                value={validityDate} onChange={e => setValidityDate(e.target.value)} />
              <label style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' }}>Reason (required)</label>
              <input className="form-control" placeholder="e.g. correcting expiry-drift bug — actually paid through June"
                value={validityReason} onChange={e => setValidityReason(e.target.value)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingValidity(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={validitySaving} onClick={handleValiditySave}>
                {validitySaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPeriod && (
        <div className="modal-overlay" onClick={() => !periodSaving && setEditingPeriod(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {editingPeriod.paymentFor === 'broadband_renewal' ? 'Broadband' : 'CableTV'} Validity — {editingPeriod.reference}</h3>
              <button className="modal-close" onClick={() => setEditingPeriod(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
                Currently: <strong>{editingPeriod.periodFrom && editingPeriod.periodTo && !editingPeriod.periodEstimated
                  ? `${fmtDate(editingPeriod.periodFrom)} to ${fmtDate(editingPeriod.periodTo)}` : 'not set'}</strong>.
                Only the dates change — no payment is created and the balance is unaffected. Recorded in System Logs.
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' }}>From</label>
                  <input className="form-control" type="date" value={periodForm.from}
                    onChange={e => setPeriodFrom(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' }}>To</label>
                  <input className="form-control" type="date" value={periodForm.to}
                    onChange={e => setPeriodTo(e.target.value)} />
                </div>
              </div>
              <label style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' }}>Days (To is calculated)</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <input className="form-control" type="number" min={1} style={{ width: 90 }} value={periodForm.days}
                  onChange={e => setPeriodDays(e.target.value)} />
                {[1, 7, 15, 30].map(n => (
                  <button key={n} type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.72rem' }}
                    disabled={!periodForm.from} onClick={() => setPeriodDays((Number(periodForm.days) || 0) + n)}>
                    +{n}
                  </button>
                ))}
              </div>
              <label style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' }}>Reason (required)</label>
              <input className="form-control" style={{ marginBottom: 10 }} placeholder="e.g. entered with wrong start date"
                value={periodForm.reason} onChange={e => setPeriodForm(f => ({ ...f, reason: e.target.value }))} />
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '.8rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={periodForm.updateExpiry}
                  onChange={e => setPeriodForm(f => ({ ...f, updateExpiry: e.target.checked }))} />
                <span>Also set the customer's {editingPeriod.paymentFor === 'broadband_renewal' ? 'Broadband' : 'CableTV'} validity to this To date</span>
              </label>
              {periodForm.from > format(new Date(), 'yyyy-MM-dd') && (
                <div style={{ fontSize: '.75rem', color: 'var(--c-b45309)', marginTop: 10 }}>
                  From is in the future — the "new period starts today" WhatsApp goes out on that date.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingPeriod(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={periodSaving} onClick={handlePeriodSave}>
                {periodSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCombo && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {[['all', 'All'], ['catv', 'CableTV'], ['broadband', 'Broadband']].map(([key, label]) => (
            <button key={key} onClick={() => setServiceTab(key)}
              className={`btn btn-sm ${serviceTab === key ? 'btn-primary' : 'btn-secondary'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {filteredEntries.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--c-9ca3af)' }}>
          <CreditCard size={28} style={{ marginBottom: 8 }} />
          <div>No bills or payments recorded yet{isCombo && serviceTab !== 'all' ? ` for ${serviceTab === 'catv' ? 'CableTV' : 'Broadband'}` : ''}</div>
        </div>
      ) : isAppView ? (
        // Card list — the 7-column table can't fit a phone width without a left-right
        // scroll, which the App View is specifically meant to avoid (see the tab-strip
        // comment above). Same data, stacked instead of columned.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredEntries.map((e) => {
            const key = e.billId || e.paymentId || e.attemptId || e.changeLogId || `${e.type}-${e.date}`;
            const isPendingVisit = e.type === 'pending_visit';
            const isUnpaidBill = e.type === 'bill' && e.status === 'pending';
            const fullPayment = e.paymentId ? payments.find((p) => p._id === e.paymentId) : null;
            const hasActions = e.paymentId || ((canEditPayment && fullPayment) || (canTransferPayment && canTransfer && e.transferable) || (canDeletePayment && canDeleteEntry(e)));
            return (
              <div key={key} style={{
                border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '10px 12px',
                background: isPendingVisit ? 'var(--cb-fffbeb)' : 'var(--cb-ffffff)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: '.78rem', color: 'var(--c-6b7280)' }}>{fmtDate(e.date)}</span>
                  {!isPendingVisit && <span style={{ fontWeight: 700, fontSize: '.88rem' }}>₹{fmt(e.balance)}</span>}
                </div>
                <div style={{ marginTop: 4, wordBreak: 'break-word', color: isPendingVisit ? 'var(--c-b45309)' : undefined, fontWeight: isPendingVisit ? 600 : 400, fontSize: '.88rem' }}>
                  {e.description}
                  {isUnpaidBill && (
                    <span style={{ marginLeft: 6, fontSize: '.65rem', fontWeight: 700, color: 'var(--c-b45309)', background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 8, padding: '1px 6px' }}>
                      UNPAID
                    </span>
                  )}
                  {isCombo && e.serviceType && (
                    <span style={{
                      marginLeft: 6, fontSize: '.65rem', fontWeight: 700, borderRadius: 8, padding: '1px 6px',
                      color: e.serviceType === 'broadband' ? 'var(--c-0369a1)' : 'var(--c-7c3aed)',
                      background: e.serviceType === 'broadband' ? 'var(--cb-e0f2fe)' : 'var(--cb-ede9fe)',
                    }}>
                      {e.serviceType === 'broadband' ? 'BROADBAND' : 'CABLE TV'}
                    </span>
                  )}
                </div>
                {renderValidityLine(e)}
                {e.oldAmount > 0 && (
                  <div style={{ marginTop: 2, fontSize: '.75rem', color: 'var(--c-b45309)', fontWeight: 600 }}>
                    Old amount ₹{fmt(e.oldAmount)} + Current ₹{fmt(e.debit)} = Total ₹{fmt(e.oldAmount + e.debit)}
                  </div>
                )}
                {e.reference && (
                  <div style={{ marginTop: 2, fontFamily: 'monospace', fontSize: '.75rem', color: 'var(--c-9ca3af)', wordBreak: 'break-word' }}>{e.reference}</div>
                )}
                {(e.debit > 0 || e.credit > 0) && (
                  <div style={{ marginTop: 4, display: 'flex', gap: 14, fontSize: '.82rem' }}>
                    {e.debit > 0 && <span style={{ fontWeight: 700, color: 'var(--c-dc2626)' }}>−₹{fmt(e.debit)}</span>}
                    {e.credit > 0 && <span style={{ fontWeight: 700, color: 'var(--c-16a34a)' }}>+₹{fmt(e.credit)}</span>}
                  </div>
                )}
                {hasActions && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, justifyContent: 'flex-end' }}>
                    {e.paymentId && (
                      <button className="btn btn-secondary btn-sm" title="View Invoice"
                        disabled={viewingKey === e.paymentId} onClick={() => handleViewReceipt(e.paymentId)}>
                        {viewingKey === e.paymentId ? '…' : <Eye size={11} />}
                      </button>
                    )}
                    {e.paymentId && (
                      <button className="btn btn-secondary btn-sm" title="Send Bill on WhatsApp"
                        disabled={sendingKey === e.paymentId} onClick={() => handleSendBill(e.paymentId)}>
                        {sendingKey === e.paymentId ? '…' : <Send size={11} />}
                      </button>
                    )}
                    {canEditPayment && fullPayment && (
                      <button className="btn btn-secondary btn-sm" title="Edit" onClick={() => onEditPayment(fullPayment)}>
                        <Edit2 size={11} />
                      </button>
                    )}
                    {canTransferPayment && canTransfer && e.transferable && (
                      <button className="btn btn-secondary btn-sm"
                        title={isCombo
                          ? `Move to ${e.serviceType === 'broadband' ? 'CableTV' : 'Broadband'}`
                          : `Shift to linked ${e.serviceType === 'broadband' ? 'CableTV' : 'Broadband'} account (${customer.linkedAccount?.name || ''})`}
                        disabled={transferringKey === e.paymentId} onClick={() => handleTransfer(e)}>
                        <RefreshCw size={11} />
                      </button>
                    )}
                    {canDeletePayment && canDeleteEntry(e) && (
                      <button className="btn btn-danger btn-sm" title="Delete" disabled={deletingKey === key} onClick={() => handleDelete(e)}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--cb-f9fafb)', borderBottom: '2px solid var(--cb-e5e7eb)' }}>
                {['Date', 'Description', 'Reference', 'Debit', 'Credit', 'Balance', ''].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--c-374151)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((e) => {
                const key = e.billId || e.paymentId || e.attemptId || e.changeLogId || `${e.type}-${e.date}`;
                const isPendingVisit = e.type === 'pending_visit';
                const isUnpaidBill = e.type === 'bill' && e.status === 'pending';
                const fullPayment = e.paymentId ? payments.find((p) => p._id === e.paymentId) : null;
                return (
                  <tr key={key} style={{ borderBottom: '1px solid var(--cb-f3f4f6)', background: isPendingVisit ? 'var(--cb-fffbeb)' : undefined }}>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmtDateTime(e.date)}</td>
                    <td style={{ padding: '9px 12px', color: isPendingVisit ? 'var(--c-b45309)' : undefined, fontWeight: isPendingVisit ? 600 : 400 }}>
                      {e.description}
                      {isUnpaidBill && (
                        <span style={{ marginLeft: 6, fontSize: '.65rem', fontWeight: 700, color: 'var(--c-b45309)', background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 8, padding: '1px 6px' }}>
                          UNPAID
                        </span>
                      )}
                      {isCombo && e.serviceType && (
                        <span style={{
                          marginLeft: 6, fontSize: '.65rem', fontWeight: 700, borderRadius: 8, padding: '1px 6px',
                          color: e.serviceType === 'broadband' ? 'var(--c-0369a1)' : 'var(--c-7c3aed)',
                          background: e.serviceType === 'broadband' ? 'var(--cb-e0f2fe)' : 'var(--cb-ede9fe)',
                        }}>
                          {e.serviceType === 'broadband' ? 'BROADBAND' : 'CABLE TV'}
                        </span>
                      )}
                      {renderValidityLine(e, { fontWeight: 400 })}
                      {e.oldAmount > 0 && (
                        <div style={{ marginTop: 2, fontSize: '.75rem', color: 'var(--c-b45309)', fontWeight: 600 }}>
                          Old amount ₹{fmt(e.oldAmount)} + Current ₹{fmt(e.debit)} = Total ₹{fmt(e.oldAmount + e.debit)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: '.8rem', color: 'var(--c-6b7280)' }}>{e.reference || '—'}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--c-dc2626)' }}>{e.debit > 0 ? `₹${fmt(e.debit)}` : ''}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--c-16a34a)' }}>{e.credit > 0 ? `₹${fmt(e.credit)}` : ''}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 600 }}>{!isPendingVisit ? `₹${fmt(e.balance)}` : ''}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {e.paymentId && (
                          <button className="btn btn-secondary btn-sm" title="View Invoice"
                            disabled={viewingKey === e.paymentId} onClick={() => handleViewReceipt(e.paymentId)}>
                            {viewingKey === e.paymentId ? '…' : <Eye size={11} />}
                          </button>
                        )}
                        {e.paymentId && (
                          <button className="btn btn-secondary btn-sm" title="Send Bill on WhatsApp"
                            disabled={sendingKey === e.paymentId} onClick={() => handleSendBill(e.paymentId)}>
                            {sendingKey === e.paymentId ? '…' : <Send size={11} />}
                          </button>
                        )}
                        {canEditPayment && fullPayment && (
                          <button className="btn btn-secondary btn-sm" title="Edit" onClick={() => onEditPayment(fullPayment)}>
                            <Edit2 size={11} />
                          </button>
                        )}
                        {canTransferPayment && canTransfer && e.transferable && (
                          <button className="btn btn-secondary btn-sm"
                            title={isCombo
                              ? `Move to ${e.serviceType === 'broadband' ? 'CableTV' : 'Broadband'}`
                              : `Shift to linked ${e.serviceType === 'broadband' ? 'CableTV' : 'Broadband'} account (${customer.linkedAccount?.name || ''})`}
                            disabled={transferringKey === e.paymentId} onClick={() => handleTransfer(e)}>
                            <RefreshCw size={11} />
                          </button>
                        )}
                        {canDeletePayment && canDeleteEntry(e) && (
                          <button className="btn btn-danger btn-sm" title="Delete" disabled={deletingKey === key} onClick={() => handleDelete(e)}>
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, color }) {
  return (
    <div style={{ flex: 1, background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--c-6b7280)' }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: '.95rem', color: color || 'var(--c-111111)' }}>₹{fmt(value)}</div>
    </div>
  );
}

// ─── Hardware Details Panel ──────────────────────────────────────────────────
function HardwarePanel({ customer, onSaved }) {
  const [editing, setEditing]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [oltFetching, setOltFetching] = useState(false);
  const [oltMatch,    setOltMatch]    = useState(null);
  const [stbModelOptions, setStbModelOptions] = useState([]);
  const [entityCodeOptions, setEntityCodeOptions] = useState([]);
  const [nodeOptions, setNodeOptions] = useState([]);
  const [form, setForm]           = useState({
    catv: {
      stbModel:        customer.catv?.stbModel        || '',
      setTopBoxSerial: customer.catv?.setTopBoxSerial || '',
      stbModel2:       customer.catv?.stbModel        || '',
      entityCode:      customer.catv?.entityCode      || '',
      accountNumber:   customer.catv?.accountNumber   || '',
      node:            customer.catv?.node            || '',
    },
    broadband: {
      routerModel:  customer.broadband?.routerModel  || '',
      routerSerial: customer.broadband?.routerSerial || '',
      ponNumber:    customer.broadband?.ponNumber    || '',
      username:     customer.broadband?.username     || '',
      staticIp:     customer.broadband?.staticIp    || '',
      houseNumber:  customer.broadband?.houseNumber  || '',
    },
  });

  const isCatv = customer.customerType === 'catv';
  const isBB   = customer.customerType === 'broadband';

  useEffect(() => {
    if (!editing || !isCatv) return;
    // Every value already assigned to a real customer — merged into the AddableSelects below
    // so each isn't limited to whatever's been manually added via "+".
    customerAPI.getNodes().then(r => setNodeOptions(r.data?.data || [])).catch(() => {});
    customerAPI.getStbModels().then(r => setStbModelOptions(r.data?.data || [])).catch(() => {});
    customerAPI.getEntityCodes().then(r => setEntityCodeOptions(r.data?.data || [])).catch(() => {});
  }, [editing, isCatv]);

  const inp = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.85rem', boxSizing: 'border-box' };
  const lbl = { fontSize: '.75rem', color: 'var(--c-6b7280)', marginBottom: 3, display: 'block' };

  const set = (section, key, val) => setForm(f => ({ ...f, [section]: { ...f[section], [key]: val } }));

  const fetchFromOLT = async () => {
    const username = customer.broadband?.username;
    if (!username) return;
    setOltFetching(true);
    try {
      const { data } = await oltAPI.matchByUsername(username);
      const onu = data.data;
      if (!onu) {
        toast(`No ONU matched "${username}" — set its label on the OLT page first`, { icon: '⚠️' });
        return;
      }
      setOltMatch(onu);
      setForm(f => ({
        ...f,
        broadband: {
          ...f.broadband,
          routerSerial: onu.serialNumber                            || f.broadband.routerSerial,
          routerModel:  onu.model                                   || f.broadband.routerModel,
          ponNumber:    onu.ponPort != null ? String(onu.ponPort)   : f.broadband.ponNumber,
        },
      }));
      toast.success('OLT data loaded — review and save');
    } catch {
      toast.error('Could not reach OLT');
    } finally {
      setOltFetching(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (isCatv) payload.catv = {
        ...customer.catv,
        stbModel:        form.catv.stbModel,
        setTopBoxSerial: form.catv.setTopBoxSerial,
        entityCode:      form.catv.entityCode,
        accountNumber:   form.catv.accountNumber,
        node:            form.catv.node,
        package:         customer.catv?.package?._id || customer.catv?.package,
      };
      if (isBB) payload.broadband = {
        ...customer.broadband,
        routerModel:  form.broadband.routerModel,
        routerSerial: form.broadband.routerSerial,
        ponNumber:    form.broadband.ponNumber,
        username:     form.broadband.username,
        staticIp:     form.broadband.staticIp,
        houseNumber:  form.broadband.houseNumber,
        plan:         customer.broadband?.plan?._id || customer.broadband?.plan,
      };
      const { data } = await customerAPI.update(customer._id, payload);
      toast.success('Hardware details saved');
      onSaved(data.data);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const viewRows = [];
  if (isCatv) viewRows.push(
    ['STB Model',      customer.catv?.stbModel],
    ['STB Serial',     customer.catv?.setTopBoxSerial],
    ['Entity Code',    customer.catv?.entityCode],
    ['Account Number', customer.catv?.accountNumber],
    ['Node',           customer.catv?.node],
  );
  if (isBB) viewRows.push(
    ['Router Model',   customer.broadband?.routerModel],
    ['Router Serial',  customer.broadband?.routerSerial],
    ['PON Number',     customer.broadband?.ponNumber],
    ['PPPoE Username', customer.broadband?.username],
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Hardware Details</h3>
        {editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>
              <X size={12} style={{ marginRight: 4 }} />Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <Save size={12} style={{ marginRight: 4 }} />{saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
            <Edit2 size={12} style={{ marginRight: 5 }} />Edit
          </button>
        )}
      </div>

      {!editing ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.88rem' }}>
          <tbody>
            {viewRows.filter(([, v]) => v).map(([label, value]) => (
              <tr key={label} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                <td style={{ padding: '10px 14px', color: 'var(--c-6b7280)', fontWeight: 500, width: '40%', background: 'var(--cb-f9fafb)' }}>{label}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 600 }}>{value}</td>
              </tr>
            ))}
            {viewRows.filter(([, v]) => v).length === 0 && (
              <tr><td colSpan={2} style={{ padding: '24px', color: 'var(--c-9ca3af)', textAlign: 'center' }}>No hardware details yet — click Edit to add.</td></tr>
            )}
          </tbody>
        </table>
      ) : (
        <div style={{ display: 'grid', gap: 20 }}>
          {isCatv && (
            <div>
              <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--primary)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-ede9fe)' }}>CableTV Hardware</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div>
                  <label style={lbl}>STB Model</label>
                  <AddableSelect optionsKey="stbModels" searchable value={form.catv.stbModel} onChange={v => set('catv','stbModel',v)} placeholder="Select STB model…" themeColor="#1e3a8a" extraOptions={stbModelOptions} />
                </div>
                <div><label style={lbl}>STB Serial</label><input style={inp} value={form.catv.setTopBoxSerial} onChange={e => set('catv','setTopBoxSerial',e.target.value)} /></div>
                <div>
                  <label style={lbl}>Entity Code</label>
                  <AddableSelect optionsKey="entityCodes" searchable value={form.catv.entityCode} onChange={v => set('catv','entityCode',v)} placeholder="Select entity code…" themeColor="#1e3a8a" extraOptions={entityCodeOptions} />
                </div>
                <div>
                  <label style={lbl}>Account Number</label>
                  <UniqueField
                    value={form.catv.accountNumber}
                    onChange={v => set('catv','accountNumber',v)}
                    checkFn={(v) => customerAPI.checkAccountNumber(v, customer._id)}
                    placeholder="Enter account number"
                  />
                </div>
                <div>
                  <label style={lbl}>Node</label>
                  <AddableSelect optionsKey="nodes" searchable value={form.catv.node} onChange={v => set('catv','node',v)} placeholder="Select node…" themeColor="#1e3a8a" extraOptions={nodeOptions} />
                </div>
              </div>
            </div>
          )}
          {isBB && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e0f2fe)' }}>
                <span style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--accent)' }}>Broadband Hardware</span>
                {customer.broadband?.username && (
                  <button
                    type="button"
                    onClick={fetchFromOLT}
                    disabled={oltFetching}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '4px 10px', borderRadius: 6, border: '1px solid var(--accent)',
                      background: oltFetching ? 'var(--cb-f0f9ff)' : 'var(--cb-ffffff)', color: 'var(--accent)',
                      fontSize: '.75rem', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <Router size={12} />
                    {oltFetching ? 'Fetching…' : 'Fetch from OLT'}
                  </button>
                )}
              </div>
              {oltMatch && (
                <div style={{
                  marginBottom: 12, padding: '8px 12px', borderRadius: 6,
                  background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)',
                  display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: '.78rem',
                }}>
                  <span style={{ color: 'var(--c-15803d)', fontWeight: 600 }}>✓ ONU matched</span>
                  <span>Status: <strong style={{ color: oltMatch.status === 'online' ? 'var(--c-15803d)' : 'var(--c-dc2626)' }}>{oltMatch.status}</strong></span>
                  {oltMatch.rxPower != null && <span>Rx: <strong>{oltMatch.rxPower} dBm</strong></span>}
                  {oltMatch.profile  && <span>Profile: <strong>{oltMatch.profile}</strong></span>}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div><label style={lbl}>Router Model</label><input style={inp} value={form.broadband.routerModel} onChange={e => set('broadband','routerModel',e.target.value)} /></div>
                <div><label style={lbl}>Router Serial</label><input style={{ ...inp, fontFamily: 'monospace', background: oltMatch?.serialNumber ? 'var(--cb-f0fdf4)' : undefined }} value={form.broadband.routerSerial} onChange={e => set('broadband','routerSerial',e.target.value)} /></div>
                <div><label style={lbl}>PPPoE Username</label><input style={inp} value={form.broadband.username} onChange={e => set('broadband','username',e.target.value)} /></div>
                <div><label style={lbl}>PON Number</label><input style={{ ...inp, fontFamily: 'monospace', background: oltMatch?.ponPort != null ? 'var(--cb-f0fdf4)' : undefined }} value={form.broadband.ponNumber} onChange={e => set('broadband','ponNumber',e.target.value)} /></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const COMPLAINT_CATEGORIES = [
  { value: 'no_internet', label: 'No Internet' },
  { value: 'slow_internet', label: 'Slow Internet' },
  { value: 'red_light_router', label: 'Red Light in Router' },
  { value: 'cable_cut', label: 'Cable Cut' },
  { value: 'router_issue', label: 'Router Issue' },
  { value: 'e61_no_signal', label: 'E61 - No Signal' },
  { value: 'e51_channel_error', label: 'E51/999 Channel Error' },
  { value: 'channel_stuck', label: 'ચેનલ ચોટે છે' },
  { value: 'colors_only', label: 'Colors Only' },
  { value: 'relocation_transfer', label: 'Relocation/Transfer' },
  { value: 'billing_dispute', label: 'Billing Dispute' },
  { value: 'device_issue', label: 'Device Issue' },
  { value: 'installation_request', label: 'Installation Request' },
  { value: 'other', label: 'Other' },
];

// ─── Raise Complaint Modal (from Customer Detail — customer is already known) ──────
function RaiseComplaintModal({ customer, onClose, onSuccess }) {
  const defaultServiceType = customer.customerType === 'catv' ? 'catv' : 'broadband';
  const defaultCategoryLabel = COMPLAINT_CATEGORIES.find(c => c.value === 'no_internet')?.label || '';
  const [form, setForm] = useState({
    category: 'no_internet', title: defaultCategoryLabel, description: defaultCategoryLabel, customerNote: '',
    priority: 'medium', serviceType: defaultServiceType, sendWhatsapp: true, assignedTo: '',
  });
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]); // [{file, preview, isVideo}] — up to 5, matches backend's complaintMediaUpload limit
  const [staff, setStaff] = useState([]);

  // Same assignee list as ComplaintsPage's New Complaint modal.
  useEffect(() => {
    userAPI.getAll().then(({ data }) => setStaff(data.data)).catch(() => {});
  }, []);

  const addImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    if (!files.length) return;
    setImages(list => [...list, ...files.map(file => ({ file, preview: URL.createObjectURL(file), isVideo: file.type.startsWith('video/') }))]);
    e.target.value = '';
  };
  const removeImage = (i) => setImages(list => list.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // assignedTo is an ObjectId ref on the backend — "Unassigned" must omit it, not send ''.
      const { assignedTo, ...rest } = form;
      const fields = { ...(assignedTo ? form : rest), customer: customer._id };
      let payload = fields;
      if (images.length) {
        payload = new FormData();
        Object.entries(fields).forEach(([k, v]) => payload.append(k, v));
        images.forEach(img => payload.append('media', img.file));
      }
      const { data } = await complaintAPI.create(payload);
      toast.success(`Ticket ${data.data.ticketNumber} created`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <h3 style={{ margin: 0 }}>Raise Complaint — {customer.name}</h3>
            {(customer.address?.area || customer.broadband?.houseNumber || customer.catv?.houseNumber) && (
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {[customer.address?.area, customer.broadband?.houseNumber || customer.catv?.houseNumber].filter(Boolean).join(' - ')}
              </div>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {customer.customerType === 'both' && (
              <div className="form-group">
                <label className="form-label">Service</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button"
                    className={`btn btn-${form.serviceType === 'broadband' ? 'primary' : 'secondary'} btn-sm`}
                    onClick={() => setForm(f => ({ ...f, serviceType: 'broadband' }))}>
                    Broadband
                  </button>
                  <button type="button"
                    className={`btn btn-${form.serviceType === 'catv' ? 'primary' : 'secondary'} btn-sm`}
                    onClick={() => setForm(f => ({ ...f, serviceType: 'catv' }))}>
                    Cable TV
                  </button>
                </div>
              </div>
            )}
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-control" value={form.category} onChange={e => {
                  const category = e.target.value;
                  const newLabel = COMPLAINT_CATEGORIES.find(c => c.value === category)?.label || '';
                  const oldLabel = COMPLAINT_CATEGORIES.find(c => c.value === form.category)?.label || '';
                  setForm(f => ({
                    ...f, category,
                    title: (!f.title || f.title === oldLabel) ? newLabel : f.title,
                    description: (!f.description || f.description === oldLabel) ? newLabel : f.description,
                  }));
                }}>
                  {COMPLAINT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-control" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Assign To (optional)</label>
              <select className="form-control" value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}>
                <option value="">— Unassigned, assign later —</option>
                {staff.map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Title <span className="required">*</span></label>
              <input className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Brief description of the issue" required />
            </div>
            <div className="form-group">
              <label className="form-label">Description <span className="required">*</span></label>
              <textarea className="form-control" rows={4} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Detailed description..." required />
            </div>
            <div className="form-group">
              <label className="form-label">Customer Remarks <span style={{ color: 'var(--c-9ca3af)', fontWeight: 400 }}>(sent to customer in the WhatsApp message)</span></label>
              <textarea className="form-control" rows={2} value={form.customerNote}
                onChange={e => setForm(f => ({ ...f, customerNote: e.target.value }))}
                placeholder="Optional — appears in the ticket-registered message sent to the customer..." />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.sendWhatsapp}
                  onChange={e => setForm(f => ({ ...f, sendWhatsapp: e.target.checked }))} />
                Send WhatsApp update to customer
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Photos / Videos (optional)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {images.map((img, i) => (
                  <div key={i} style={{ position: 'relative', width: 64, height: 64 }}>
                    {img.isVideo ? (
                      <>
                        <video src={img.preview} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, background: 'var(--cb-000000)' }} />
                        <Film size={16} color="var(--c-ffffff)" style={{ position: 'absolute', bottom: 4, left: 4, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.8))' }} />
                      </>
                    ) : (
                      <img src={img.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    )}
                    <button type="button" onClick={() => removeImage(i)}
                      style={{ position: 'absolute', top: -6, right: -6, background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      <X size={12} color="var(--c-ffffff)" />
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <label style={{ width: 64, height: 64, border: '1px dashed var(--border)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    <ImagePlus size={20} />
                    <input type="file" accept="image/*,video/*" multiple hidden onChange={addImages} />
                  </label>
                )}
              </div>
              <p style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 6 }}>Up to 5 photos or videos of the issue.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <MessageSquare size={14} /> {loading ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Complaints Panel ────────────────────────────────────────────────────────
function ComplaintsPanel({ complaints, isAppView, customer, onSuccess, canRaise }) {
  const [showModal, setShowModal] = useState(false);
  const raiseBtn = canRaise && (
    <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
      <Plus size={14} /> Raise Complaint
    </button>
  );

  if (complaints.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-9ca3af)' }}>
        <MessageSquare size={32} style={{ marginBottom: 8 }} />
        <div style={{ marginBottom: 16 }}>No complaints</div>
        {raiseBtn}
        {canRaise && showModal && (
          <RaiseComplaintModal customer={customer} onClose={() => setShowModal(false)}
            onSuccess={() => { setShowModal(false); onSuccess(); }} />
        )}
      </div>
    );
  }
  const badgeMap = { open: 'var(--cb-dc2626)', in_progress: 'var(--cb-d97706)', resolved: 'var(--cb-16a34a)', closed: 'var(--cb-6b7280)' };

  if (isAppView) {
    // Card list — same reasoning as BalanceHistoryPanel: the ticket table's columns
    // (ticket/category/status/priority/date/view) can't fit a phone width without
    // horizontal scroll, so stack the fields instead.
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Customer Complaints</h3>
          {raiseBtn}
        </div>
        {showModal && (
          <RaiseComplaintModal customer={customer} onClose={() => setShowModal(false)}
            onSuccess={() => { setShowModal(false); onSuccess(); }} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {complaints.map(c => (
            <div key={c._id} style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '10px 12px', background: 'var(--cb-ffffff)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '.8rem', color: 'var(--c-6b7280)' }}>{c.ticketNumber}</span>
                <span style={{ background: badgeMap[c.status] || 'var(--cb-6b7280)', color: 'var(--c-ffffff)', padding: '2px 8px', borderRadius: 3, fontSize: '.72rem', fontWeight: 600, textTransform: 'capitalize', flexShrink: 0 }}>
                  {c.status?.replace(/_/g, ' ')}
                </span>
              </div>
              <div style={{ marginTop: 4, fontWeight: 600, fontSize: '.88rem', textTransform: 'capitalize', wordBreak: 'break-word' }}>
                {c.category?.replace(/_/g, ' ')}
              </div>
              <div style={{ marginTop: 4, display: 'flex', gap: 14, fontSize: '.78rem', color: 'var(--c-6b7280)', flexWrap: 'wrap' }}>
                <span>Priority: <span style={{ textTransform: 'capitalize', color: 'var(--c-111111)', fontWeight: 600 }}>{c.priority}</span></span>
                <span>{fmtDate(c.createdAt)}</span>
              </div>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <Link to={`/complaints/${c._id}`} className="btn btn-secondary btn-sm">
                  <ExternalLink size={11} /> View
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Customer Complaints</h3>
        {raiseBtn}
      </div>
      {showModal && (
        <RaiseComplaintModal customer={customer} onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); onSuccess(); }} />
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
          <thead>
            <tr style={{ background: 'var(--cb-f9fafb)', borderBottom: '2px solid var(--cb-e5e7eb)' }}>
              {['Ticket', 'Category', 'Status', 'Priority', 'Raised', ''].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--c-374151)', fontSize: '.78rem', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {complaints.map(c => (
              <tr key={c._id} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: '.8rem' }}>{c.ticketNumber}</td>
                <td style={{ padding: '9px 12px', textTransform: 'capitalize', color: 'var(--c-6b7280)' }}>{c.category?.replace(/_/g, ' ')}</td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ background: badgeMap[c.status] || 'var(--cb-6b7280)', color: 'var(--c-ffffff)', padding: '2px 8px', borderRadius: 3, fontSize: '.75rem', fontWeight: 600, textTransform: 'capitalize' }}>
                    {c.status?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ padding: '9px 12px', textTransform: 'capitalize', fontSize: '.82rem' }}>{c.priority}</td>
                <td style={{ padding: '9px 12px', fontSize: '.8rem', color: 'var(--c-6b7280)' }}>{fmtDate(c.createdAt)}</td>
                <td style={{ padding: '9px 12px' }}>
                  <Link to={`/complaints/${c._id}`} className="btn btn-secondary btn-sm">
                    <ExternalLink size={11} /> View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Change History Panel ────────────────────────────────────────────────────
// Same ChangeLog data as the admin Change Ledger page (backend/src/routes/logs.js),
// scoped to this one customer via ?recordId= so staff don't have to search the
// whole-app ledger by name/mobile to see what changed on this account.
const CHANGE_HISTORY_HIDDEN_VALUES = ['(hidden)', '(changed)', '(rotated)'];
function CustomerHistoryPanel({ customer, isAdmin }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await logsAPI.getAll({ entity: 'customer', recordId: customer._id, limit: 300 });
      setLogs(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [customer._id]);
  useEffect(() => { load(); }, [load]);

  const handleRestore = async (l) => {
    if (!window.confirm(`Restore ${l.field} back to "${l.oldValue ?? '—'}"?`)) return;
    setRestoring(l._id);
    try {
      const { data } = await logsAPI.restore(l._id);
      toast.success(data.message || 'Restored');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  if (logs.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-9ca3af)' }}>
        <History size={32} style={{ marginBottom: 8 }} />
        <div>No changes logged for this customer yet</div>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>Change History</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {logs.map(l => {
          const hiddenValue = CHANGE_HISTORY_HIDDEN_VALUES.includes(l.oldValue);
          const canRestore = isAdmin && l.action === 'update' && !l.restored && !hiddenValue
            && l.oldValue !== undefined && l.oldValue !== null && l.oldValue !== '';
          return (
            <div key={l._id} style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '10px 12px', background: 'var(--cb-ffffff)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: '.85rem' }}>{l.field || (l.action === 'create' ? 'Created' : l.action === 'delete' ? 'Deleted' : 'Note')}</span>
                  {l.restored && <span style={{ marginLeft: 8, fontSize: '.68rem', color: 'var(--c-0e9e96)', fontWeight: 700 }}>✓ restored</span>}
                  <div style={{ marginTop: 4, fontFamily: 'monospace', fontSize: '.8rem' }}>
                    {(l.action === 'update' || l.action === 'note') ? (
                      <>
                        <span style={{ background: '#DB4C5C14', color: 'var(--c-b91c1c)', borderRadius: 4, padding: '1px 6px', textDecoration: 'line-through' }}>{l.oldValue == null || l.oldValue === '' ? '—' : String(l.oldValue)}</span>
                        <span style={{ margin: '0 6px' }}>→</span>
                        <span style={{ background: '#0E9E9614', color: 'var(--c-0f766e)', borderRadius: 4, padding: '1px 6px' }}>{l.newValue == null || l.newValue === '' ? '—' : String(l.newValue)}</span>
                      </>
                    ) : (
                      <span>{l.action === 'delete' ? l.oldValue : l.newValue}</span>
                    )}
                  </div>
                  <div style={{ marginTop: 4, fontSize: '.75rem', color: 'var(--c-6b7280)' }}>
                    {fmtDateTime(l.createdAt)}{l.changedByName && <> · by {l.changedByName}</>}
                  </div>
                </div>
                {canRestore && (
                  <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }} disabled={restoring === l._id} onClick={() => handleRestore(l)}>
                    <RotateCcw size={12} /> Restore
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Customer Info Panel ─────────────────────────────────────────────────────
function CustomerInfoPanel({ customer, onSaved, initialEditing = false, onCancel }) {
  const toInputDate = d => d ? format(new Date(d), 'yyyy-MM-dd') : '';
  const fmtDate2    = d => d ? format(new Date(d), 'dd MMM yyyy') : '—';

  const [editing, setEditing] = useState(initialEditing);
  const [saving,  setSaving]  = useState(false);
  const [plans,   setPlans]   = useState([]);
  const [pkgs,    setPkgs]    = useState([]);
  const [form, setForm] = useState({
    broadband: {
      plan:       customer.broadband?.plan?._id || customer.broadband?.plan || '',
      ispName:    customer.broadband?.ispName   || '',
      username:   customer.broadband?.username  || '',
      expiryDate: toInputDate(customer.broadband?.expiryDate),
      isActive:   customer.broadband?.isActive  ?? false,
    },
    catv: {
      package:    customer.catv?.package?._id || customer.catv?.package || '',
      expiryDate: toInputDate(customer.catv?.expiryDate),
      catvType:   customer.catv?.catvType || 'regular',
      isActive:   customer.catv?.isActive ?? false,
    },
    billing: {
      billType:      customer.billing?.billType      || 'prepaid',
      durationType:  customer.billing?.durationType  || 'regular',
      durationDays:  customer.billing?.durationDays  || 30,
      gstType:       customer.billing?.gstType       || 'no_gst',
    },
    imbs: {
      macCheck: customer.imbs?.macCheck ?? true,
    },
    installationDate: toInputDate(customer.installationDate),
    notes: customer.notes || '',
  });

  const isCatv = customer.customerType === 'catv';
  const isBB   = customer.customerType === 'broadband';

  useEffect(() => {
    if (!editing) return;
    if (isBB)   broadbandAPI.getPlans({ limit: 100 }).then(r => setPlans(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
    if (isCatv) catvAPI.getPackages({ limit: 100 }).then(r => setPkgs(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
  }, [editing, isBB, isCatv]);

  const inp = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.85rem', boxSizing: 'border-box' };
  const lbl = { fontSize: '.75rem', color: 'var(--c-6b7280)', marginBottom: 3, display: 'block' };
  const set = (section, key, val) => setForm(f => ({ ...f, [section]: { ...f[section], [key]: val } }));
  // CATV's "Type" (catv.catvType) and Billing's "Duration" (billing.durationType) are two
  // separate fields that both mean the same thing for a CATV customer — the Renew/Collect
  // Payment screens only read billing.durationType, so leaving catv.catvType out of sync (e.g.
  // set to DTD while billing.durationType stays 'regular') silently keeps the customer on the
  // monthly flow. Keep them mirrored whichever select the user touches.
  const setDurationType = (val) => setForm(f => ({
    ...f,
    catv: { ...f.catv, catvType: val },
    billing: { ...f.billing, durationType: val, durationDays: val === 'dtd' ? (f.billing.durationDays || 30) : f.billing.durationDays },
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        notes: form.notes,
        installationDate: form.installationDate || undefined,
        billing: form.billing,
        imbs: customer.imbs ? { ...customer.imbs, ...form.imbs } : undefined,
      };
      if (isBB) payload.broadband = { ...customer.broadband, ...form.broadband };
      if (isCatv) payload.catv    = { ...customer.catv,      ...form.catv };
      const { data } = await customerAPI.update(customer._id, payload);
      toast.success('Subscription details saved');
      onSaved(data.data);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const section = (label, pairs) => (
    <div style={{ marginBottom: 20 }} key={label}>
      <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-6b7280)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e5e7eb)' }}>{label}</div>
      <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '.88rem' }}>
        <tbody>
          {pairs.filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => (
            <tr key={k}>
              <td style={{ padding: '6px 8px 6px 0', color: 'var(--c-6b7280)', width: '45%', wordBreak: 'break-word' }}>{k}</td>
              <td style={{ padding: '6px 0', fontWeight: 500, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{v || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Subscription Details</h3>
        {editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); onCancel?.(); }} disabled={saving}>
              <X size={12} style={{ marginRight: 4 }} />Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <Save size={12} style={{ marginRight: 4 }} />{saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
            <Edit2 size={12} style={{ marginRight: 5 }} />Edit
          </button>
        )}
      </div>

      {!editing ? (
        <>
          {isBB && section('Broadband', [
            ['Plan',      customer.broadband?.plan?.displayName],
            ['Speed',     customer.broadband?.plan ? `${customer.broadband.plan.speed?.download}/${customer.broadband.plan.speed?.upload} Mbps` : null],
            ['Price',     customer.broadband?.plan ? `₹${customer.broadband.plan.price}` : null],
            ['Username',  customer.broadband?.username],
            ['ISP',        customer.broadband?.ispName || null],
            ['Expiry',    customer.broadband?.expiryDate ? fmtDate2(customer.broadband.expiryDate) : null],
            ['Active',    customer.broadband?.isActive ? 'Yes' : 'No'],
          ])}
          {isCatv && section('CableTV', [
            ['Package',    customer.catv?.package?.displayName],
            ['Price',      customer.catv?.package ? `₹${customer.catv.package.price}` : null],
            ['Type',       customer.catv?.catvType],
            ['Account No.',customer.catv?.accountNumber],
            ['Expiry',     customer.catv?.expiryDate ? fmtDate2(customer.catv.expiryDate) : null],
            ['Active',     customer.catv?.isActive ? 'Yes' : 'No'],
          ])}
          {section('Billing', [
            ['Billing No.',customer.billingNumber != null ? String(customer.billingNumber) : null],
            ['Bill Type',  customer.billing?.billType],
            ['Duration',   customer.billing?.durationType === 'dtd'
              ? `DTD (${customer.billing?.durationDays || 30} days)`
              : 'End of every month'],
            ['GST Type',   customer.billing?.gstType?.replace(/_/g, ' ')],
            ['Installed',  customer.installationDate ? fmtDate2(customer.installationDate) : null],
          ])}
          {customer.notes && section('Notes', [['', customer.notes]])}
        </>
      ) : (
        <div style={{ display: 'grid', gap: 20 }}>
          {isBB && (
            <div>
              <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--accent)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e0f2fe)' }}>Broadband</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Plan</label>
                  <SearchableSelect
                    value={form.broadband.plan}
                    onChange={v => set('broadband','plan',v)}
                    placeholder="Search plan…"
                    options={plans.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}` }))}
                  />
                </div>
                <div><label style={lbl}>PPPoE Username</label><input style={inp} value={form.broadband.username} onChange={e => set('broadband','username',e.target.value)} /></div>
                <div><label style={lbl}>ISP Name</label><input style={inp} value={form.broadband.ispName} onChange={e => set('broadband','ispName',e.target.value)} /></div>
                <div><label style={lbl}>Expiry Date</label><input type="date" style={inp} value={form.broadband.expiryDate} onChange={e => set('broadband','expiryDate',e.target.value)} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                  <input type="checkbox" id="bbActive" checked={form.broadband.isActive} onChange={e => set('broadband','isActive',e.target.checked)} />
                  <label htmlFor="bbActive" style={{ fontSize: '.85rem', cursor: 'pointer' }}>Broadband Active</label>
                </div>
                {customer.imbs?.subscriberId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                    <input
                      type="checkbox"
                      id="imbsMacCheck"
                      checked={form.imbs.macCheck}
                      onChange={e => setForm(f => ({ ...f, imbs: { ...f.imbs, macCheck: e.target.checked } }))}
                    />
                    <label htmlFor="imbsMacCheck" style={{ fontSize: '.85rem', cursor: 'pointer' }}>
                      MAC Check <span style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)' }}>(Netzur)</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}
          {isCatv && (
            <div>
              <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--primary)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-ede9fe)' }}>CableTV</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Package</label>
                  <SearchableSelect
                    value={form.catv.package}
                    onChange={v => set('catv','package',v)}
                    placeholder="Search package…"
                    options={pkgs.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}` }))}
                  />
                </div>
                <div>
                  <label style={lbl}>Type</label>
                  <select style={inp} value={form.catv.catvType} onChange={e => setDurationType(e.target.value)}>
                    <option value="regular">Regular</option>
                    <option value="dtd">DTD</option>
                    <option value="amp">AMP</option>
                  </select>
                </div>
                <div><label style={lbl}>Expiry Date</label><input type="date" style={inp} value={form.catv.expiryDate} onChange={e => set('catv','expiryDate',e.target.value)} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                  <input type="checkbox" id="catvActive" checked={form.catv.isActive} onChange={e => set('catv','isActive',e.target.checked)} />
                  <label htmlFor="catvActive" style={{ fontSize: '.85rem', cursor: 'pointer' }}>CATV Active</label>
                </div>
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-6b7280)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e5e7eb)' }}>Billing</div>
            {customer.billingNumber != null && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--cb-f1f5f9)', border: '1px solid var(--cb-e2e8f0)', borderRadius: 6, padding: '6px 12px', marginBottom: 12 }}>
                <span style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--c-64748b)' }}>BILLING NO.</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '.95rem', color: 'var(--c-1e293b)', letterSpacing: 1 }}>{customer.billingNumber}</span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10 }}>
              <div>
                <label style={lbl}>Bill Type</label>
                <select style={inp} value={form.billing.billType} onChange={e => set('billing','billType',e.target.value)}>
                  <option value="prepaid">Prepaid</option>
                  <option value="postpaid">Postpaid</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Duration</label>
                <select style={inp} value={form.billing.durationType} onChange={e => (isCatv ? setDurationType(e.target.value) : set('billing','durationType',e.target.value))}>
                  <option value="regular">End of every month</option>
                  <option value="dtd">DTD</option>
                  <option value="amp">AMP (auto-paid by us)</option>
                </select>
              </div>
              {form.billing.durationType === 'dtd' && (
                <div>
                  <label style={lbl}>DTD Days</label>
                  <select style={inp} value={form.billing.durationDays} onChange={e => set('billing','durationDays',parseInt(e.target.value))}>
                    {Array.from({ length: 24 }, (_, i) => (i + 1) * 30).map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label style={lbl}>GST Type</label>
                <select style={inp} value={form.billing.gstType} onChange={e => set('billing','gstType',e.target.value)}>
                  <option value="no_gst">No GST</option>
                  <option value="cgst_sgst">CGST + SGST</option>
                  <option value="igst">IGST</option>
                </select>
              </div>
              <div><label style={lbl}>Installation Date</label><input type="date" style={inp} value={form.installationDate} onChange={e => setForm(f => ({ ...f, installationDate: e.target.value }))} /></div>
            </div>
          </div>
          <div>
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Edit Customer Panel ─────────────────────────────────────────────────────
// Row/SecHeader/V live at module scope: defined inside the panel they got a new
// identity on every keystroke, so React remounted the row and the input lost
// focus after a single character.
const Row = ({ label, required: req, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', minHeight: 40, borderBottom: '1px solid var(--cb-f9fafb)', padding: '2px 0', gap: 8 }}>
    <span style={{ width: 210, flexShrink: 0, fontSize: '.82rem', color: 'var(--c-6b7280)', paddingRight: 12 }}>
      {label}{req && <span style={{ color: 'var(--c-dc2626)' }}> *</span>} :
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);

const SecHeader = ({ title }) => (
  <div style={{ background: 'var(--cb-f8fafc)', borderTop: '2px solid var(--cb-e2e8f0)', borderBottom: '1px solid var(--cb-e2e8f0)', padding: '6px 16px', marginTop: 10, marginBottom: 2, marginLeft: -16, marginRight: -16 }}>
    <span style={{ fontWeight: 700, fontSize: '.88rem', color: 'var(--c-374151)', letterSpacing: '.02em' }}>{title}</span>
  </div>
);

// Plain read-only text — used for every field while not in edit mode.
const V = ({ children }) => (
  <span style={{ fontSize: '.88rem', color: children ? 'var(--c-111111)' : 'var(--c-9ca3af)', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{children || '—'}</span>
);

function EditCustomerPanel({ customer, onSuccess, isAdmin }) {
  const toInputDate = (d) => d ? format(new Date(d), 'yyyy-MM-dd') : '';
  const isCatv = customer.customerType === 'catv';

  const [form, setForm] = useState({
    billingNumber: customer.billingNumber ?? '',
    name: customer.name || '',
    mobile: customer.mobile || '',
    alternateMobile: customer.alternateMobile || '',
    email: customer.email || '',
    status: customer.status || 'active',
    notes: customer.notes || '',
    securityDeposit: customer.securityDeposit ?? 0,
    durationType: customer.billing?.durationType || 'regular',
    address: {
      line1: customer.address?.line1 || '',
      area: customer.address?.area || '',
      city: customer.address?.city || '',
      pincode: customer.address?.pincode || '',
    },
    billingAddress: {
      sameAsInstallation: customer.billingAddress?.sameAsInstallation ?? true,
      line1: customer.billingAddress?.line1 || '',
      city: customer.billingAddress?.city || '',
      pincode: customer.billingAddress?.pincode || '',
    },
    catv: {
      package: customer.catv?.package?._id || customer.catv?.package || '',
      accountNumber: customer.catv?.accountNumber || '',
      houseNumber: customer.catv?.houseNumber || '',
      billingNumber: customer.catv?.billingNumber || '',
      stbModel: customer.catv?.stbModel || '',
      setTopBoxSerial: customer.catv?.setTopBoxSerial || '',
      entityCode: customer.catv?.entityCode || '',
      catvType: customer.catv?.catvType || 'regular',
      dtdAllowShortMonths: customer.billing?.dtdAllowShortMonths ?? false,
      expiryDate: toInputDate(customer.catv?.expiryDate),
      isActive: customer.catv?.isActive ?? false,
      excludeFromPending: customer.catv?.excludeFromPending ?? false,
    },
    broadband: {
      username: customer.broadband?.username || '',
      ponNumber: customer.broadband?.ponNumber || '',
      staticIp: customer.broadband?.staticIp || '',
      ispName: customer.broadband?.ispName || '',
      houseNumber: customer.broadband?.houseNumber || '',
      expiryDate: toInputDate(customer.broadband?.expiryDate),
      isActive: customer.broadband?.isActive ?? false,
      notifyOnuLoss: customer.broadband?.notifyOnuLoss ?? false,
      autoRenew: customer.broadband?.autoRenew ?? false,
    },
  });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [retracking, setRetracking] = useState(false);
  const [retrackResult, setRetrackResult] = useState(null);
  const [packages, setPackages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [stbModelOptions, setStbModelOptions] = useState([]);
  const [entityCodeOptions, setEntityCodeOptions] = useState([]);

  const resetForm = () => setForm({
    billingNumber: customer.billingNumber ?? '',
    name: customer.name || '',
    mobile: customer.mobile || '',
    alternateMobile: customer.alternateMobile || '',
    email: customer.email || '',
    status: customer.status || 'active',
    notes: customer.notes || '',
    securityDeposit: customer.securityDeposit ?? 0,
    durationType: customer.billing?.durationType || 'regular',
    address: {
      line1: customer.address?.line1 || '',
      area: customer.address?.area || '',
      city: customer.address?.city || '',
      pincode: customer.address?.pincode || '',
    },
    billingAddress: {
      sameAsInstallation: customer.billingAddress?.sameAsInstallation ?? true,
      line1: customer.billingAddress?.line1 || '',
      city: customer.billingAddress?.city || '',
      pincode: customer.billingAddress?.pincode || '',
    },
    catv: {
      package: customer.catv?.package?._id || customer.catv?.package || '',
      accountNumber: customer.catv?.accountNumber || '',
      houseNumber: customer.catv?.houseNumber || '',
      billingNumber: customer.catv?.billingNumber || '',
      stbModel: customer.catv?.stbModel || '',
      setTopBoxSerial: customer.catv?.setTopBoxSerial || '',
      entityCode: customer.catv?.entityCode || '',
      catvType: customer.catv?.catvType || 'regular',
      dtdAllowShortMonths: customer.billing?.dtdAllowShortMonths ?? false,
      expiryDate: toInputDate(customer.catv?.expiryDate),
      isActive: customer.catv?.isActive ?? false,
      excludeFromPending: customer.catv?.excludeFromPending ?? false,
    },
    broadband: {
      username: customer.broadband?.username || '',
      ponNumber: customer.broadband?.ponNumber || '',
      staticIp: customer.broadband?.staticIp || '',
      ispName: customer.broadband?.ispName || '',
      houseNumber: customer.broadband?.houseNumber || '',
      expiryDate: toInputDate(customer.broadband?.expiryDate),
      isActive: customer.broadband?.isActive ?? false,
      notifyOnuLoss: customer.broadband?.notifyOnuLoss ?? false,
      autoRenew: customer.broadband?.autoRenew ?? false,
    },
  });

  const stb = form.catv.setTopBoxSerial || '';

  React.useEffect(() => {
    if (isCatv) {
      catvAPI.getPackages({ limit: 200 })
        .then(r => setPackages(r.data?.data?.docs || r.data?.data || []))
        .catch(() => {});
    }
    const areaParams = customer.customerType === 'catv' ? { customerType: 'catv,both' }
      : customer.customerType === 'broadband' ? { customerType: 'broadband,both' }
      : undefined;
    customerAPI.getAreas(areaParams)
      .then(r => setAreas(r.data?.data || []))
      .catch(() => {});
    if (isCatv) {
      customerAPI.getStbModels().then(r => setStbModelOptions(r.data?.data || [])).catch(() => {});
      customerAPI.getEntityCodes().then(r => setEntityCodeOptions(r.data?.data || [])).catch(() => {});
    }
  }, [isCatv, customer.customerType]);

  const set = (path, val) => setForm(prev => {
    const parts = path.split('.');
    if (parts.length === 1) return { ...prev, [path]: val };
    return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: val } };
  });

  const handleRetrack = async () => {
    if (!stb) { toast.error('No STB serial number on this customer'); return; }
    setRetracking(true);
    setRetrackResult(null);
    try {
      const { data } = await gtplAPI.retrack(stb);
      setRetrackResult({ message: data.data?.message || 'Done', ok: data.data?.ok ?? true });
    } catch (err) {
      setRetrackResult({ message: err.response?.data?.message || err.message || 'Retrack failed', ok: false });
    } finally { setRetracking(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name, mobile: form.mobile,
        alternateMobile: form.alternateMobile, email: form.email,
        status: form.status, notes: form.notes, address: form.address,
        securityDeposit: Number(form.securityDeposit) || 0,
        billingAddress: form.billingAddress.sameAsInstallation
          ? { sameAsInstallation: true }
          : { ...form.billingAddress, sameAsInstallation: false },
      };
      // Billing number is the per-service unique key — server re-validates the pool
      if (String(form.billingNumber).trim() && Number(form.billingNumber) !== customer.billingNumber)
        payload.billingNumber = Number(form.billingNumber);
      if (isCatv) {
        payload.catv = {
          ...customer.catv,
          ...form.catv,
          package: form.catv.package || undefined,
        };
        if (!payload.catv.package) delete payload.catv.package;
        // dtdAllowShortMonths is edited alongside Type/DTD here for convenience but actually lives
        // under billing (see below) — not a real catv field, don't write it there too.
        delete payload.catv.dtdAllowShortMonths;
        // This form's "Type" is the only DTD/Regular toggle it shows, but the Renew/Collect
        // Payment screens actually key off billing.durationType (a separate field edited
        // elsewhere) — mirror it here so switching Type here isn't silently ignored.
        payload.billing = {
          ...customer.billing,
          durationType: form.catv.catvType,
          durationDays: form.catv.catvType === 'dtd' ? (customer.billing?.durationDays || 30) : customer.billing?.durationDays,
          dtdAllowShortMonths: !!form.catv.dtdAllowShortMonths,
        };
      }
      // BUG FIXED 2026-09-01: this used to check customerType === 'broadband' only — a
      // combo ('both') customer's Broadband section above was fully editable in this form
      // but every change silently never saved, since payload.broadband was never set for
      // them. Needed for real now that Auto-Renew (below) has to work for combo customers
      // on the free LCV_COMBO_NET_MONTHLY plan, which are 'both', never pure 'broadband'.
      if (customer.customerType === 'broadband' || customer.customerType === 'both') {
        payload.broadband = { ...customer.broadband, ...form.broadband, plan: customer.broadband?.plan?._id || customer.broadband?.plan };
        // Customer Type (Regular / DTD / AMP) = billing.durationType; combo keeps the CATV side's type in step
        payload.billing = {
          ...customer.billing,
          durationType: form.durationType,
          durationDays: form.durationType === 'dtd' ? (customer.billing?.durationDays || 30) : customer.billing?.durationDays,
        };
        if (customer.customerType === 'both') payload.catv = { ...customer.catv, catvType: form.durationType };
      }
      const { data } = await customerAPI.update(customer._id, payload);
      toast.success('Customer updated');
      onSuccess(data.data);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setLoading(false); }
  };

  /* ── Retrack result modal ── */
  const RetrackModal = retrackResult ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={() => setRetrackResult(null)}>
      <div style={{ background: 'var(--cb-ffffff)', borderRadius: 12, padding: '28px 32px', maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: retrackResult.ok ? 'var(--cb-dcfce7)' : 'var(--cb-fee2e2)' }}>
            {retrackResult.ok ? <CheckCircle size={20} color="var(--c-16a34a)" /> : <X size={20} color="var(--c-dc2626)" />}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{retrackResult.ok ? 'Retrack Successful' : 'Retrack Failed'}</div>
            <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>STB: {stb}</div>
          </div>
          <button type="button" onClick={() => setRetrackResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-9ca3af)' }}><X size={18} /></button>
        </div>
        <div style={{ background: retrackResult.ok ? 'var(--cb-f0fdf4)' : 'var(--cb-fef2f2)', border: `1px solid ${retrackResult.ok ? 'var(--cb-86efac)' : 'var(--cb-fca5a5)'}`, borderRadius: 8, padding: '12px 16px', color: retrackResult.ok ? 'var(--c-166534)' : 'var(--c-991b1b)', fontSize: '.9rem', lineHeight: 1.6 }}>
          {retrackResult.message}
        </div>
        <button type="button" onClick={() => setRetrackResult(null)} style={{ marginTop: 18, width: '100%', padding: '9px 0', borderRadius: 7, background: 'var(--primary)', color: 'var(--c-ffffff)', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Close</button>
      </div>
    </div>
  ) : null;

  /* ════════════════════════════════════════════
     CATV — bix42-style layout
  ════════════════════════════════════════════ */
  if (isCatv) {
    const inp = {
      width: '100%', border: 'none', borderBottom: '1px solid var(--cb-e5e7eb)',
      padding: '5px 6px', fontSize: '.88rem', background: 'transparent',
      outline: 'none', color: 'var(--c-111111)',
    };
    const selInp = { ...inp, cursor: 'pointer' };

    return (
      <form onSubmit={handleSave}>
        {RetrackModal}
        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Customer Details</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {stb && (
              <button type="button" onClick={handleRetrack} disabled={retracking}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', border: 'none', borderRadius: 5, cursor: 'pointer', background: retracking ? 'var(--cb-6b7280)' : 'var(--primary)', color: 'var(--c-ffffff)', fontSize: '.83rem', fontWeight: 600 }}>
                <RotateCcw size={13} style={{ animation: retracking ? 'spin 1s linear infinite' : 'none' }} />
                {retracking ? 'Retracking…' : 'Retrack STB'}
              </button>
            )}
            {editing ? (
              <>
                <button type="button" className="btn btn-secondary" onClick={() => { resetForm(); setEditing(false); }} disabled={loading}>
                  <X size={13} /> Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading} style={{ background: 'var(--cb-1e293b)', borderColor: 'var(--cb-1e293b)' }}>
                  <Save size={13} /> {loading ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => setEditing(true)} style={{ background: 'var(--cb-1e293b)', borderColor: 'var(--cb-1e293b)' }}>
                <Edit2 size={13} /> Edit
              </button>
            )}
          </div>
        </div>

        <div style={{ padding: '0 16px', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, background: 'var(--cb-ffffff)' }}>

          <SecHeader title="General Detail" />
          <div className="cd-rows">

          <Row label="Customer Name" required={editing}>
            {editing ? <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} required /> : <V>{customer.name}</V>}
          </Row>
          <Row label="Mobile Number 1">
            {editing ? <input style={inp} value={form.mobile} onChange={e => set('mobile', e.target.value)} /> : <V>{customer.mobile}</V>}
          </Row>
          <Row label="Mobile Number 2">
            {editing ? <input style={inp} value={form.alternateMobile} onChange={e => set('alternateMobile', e.target.value)} /> : <V>{customer.alternateMobile}</V>}
          </Row>
          <Row label="Address">
            {editing ? <textarea style={{ ...inp, resize: 'vertical', minHeight: 58 }} value={form.address.line1} onChange={e => set('address.line1', e.target.value)} /> : <V>{customer.address?.line1}</V>}
          </Row>
          <Row label="Security Deposit">
            {editing ? <input style={inp} type="number" min="0" value={form.securityDeposit} onChange={e => set('securityDeposit', e.target.value)} /> : <V>{customer.securityDeposit ? `₹${customer.securityDeposit}` : null}</V>}
          </Row>
          <Row label="Billing Area">
            {editing ? <AreaSelect style={selInp} value={form.address.area} onChange={v => set('address.area', v)} areas={areas} /> : <V>{customer.address?.area}</V>}
          </Row>
          <Row label="House / Flat No">
            {editing ? <input style={inp} value={form.catv.houseNumber} onChange={e => set('catv.houseNumber', e.target.value)} /> : <V>{customer.catv?.houseNumber}</V>}
          </Row>
          <Row label="Billing Number">
            {editing
              ? <input style={{ ...inp, fontFamily: 'monospace' }} type="number" min="0.1" step="any" value={form.billingNumber} onChange={e => set('billingNumber', e.target.value)} placeholder="e.g. 2 or 1.1 (unique in CableTV)" />
              : <V>{customer.billingNumber}</V>}
          </Row>
          <Row label="Remark / Notes">
            {editing ? <textarea style={{ ...inp, resize: 'vertical', minHeight: 58 }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes…" /> : <V>{customer.notes}</V>}
          </Row>

          </div>
          <SecHeader title="CableTV Detail" />
          <div className="cd-rows">

          <Row label="Package">
            {editing ? (
              <SearchableSelect
                style={selInp}
                value={form.catv.package}
                onChange={v => set('catv.package', v)}
                placeholder="Search package…"
                options={[
                  ...packages.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}` })),
                  ...(form.catv.package && !packages.find(p => p._id === form.catv.package)
                    ? [{ value: form.catv.package, label: customer.catv?.package?.displayName || customer.catv?.package?.name || 'Current Package' }]
                    : []),
                ]}
              />
            ) : <V>{customer.catv?.package?.displayName || customer.catv?.package?.name}</V>}
          </Row>
          <Row label="Account Number">
            {editing ? (
              <UniqueField
                value={form.catv.accountNumber}
                onChange={v => set('catv.accountNumber', v)}
                checkFn={(v) => customerAPI.checkAccountNumber(v, customer._id)}
                placeholder="Enter account number"
              />
            ) : <V>{customer.catv?.accountNumber}</V>}
          </Row>
          <Row label="STB Model">
            {editing ? <AddableSelect optionsKey="stbModels" searchable value={form.catv.stbModel} onChange={v => set('catv.stbModel', v)} placeholder="Select STB model…" themeColor="#1e3a8a" extraOptions={stbModelOptions} /> : <V>{customer.catv?.stbModel}</V>}
          </Row>
          <Row label="STB Serial">
            {editing ? <input style={{ ...inp, fontFamily: 'monospace' }} value={form.catv.setTopBoxSerial} onChange={e => set('catv.setTopBoxSerial', e.target.value)} /> : <V>{customer.catv?.setTopBoxSerial}</V>}
          </Row>
          <Row label="Entity / Card Code">
            {editing ? <AddableSelect optionsKey="entityCodes" searchable value={form.catv.entityCode} onChange={v => set('catv.entityCode', v)} placeholder="Select entity code…" themeColor="#1e3a8a" extraOptions={entityCodeOptions} /> : <V>{customer.catv?.entityCode}</V>}
          </Row>
          <Row label="CATV Type">
            {editing ? (
              <select style={selInp} value={form.catv.catvType} onChange={e => {
                const val = e.target.value;
                set('catv.catvType', val);
                if (val === 'regular' || val === 'amp') {
                  const now = new Date();
                  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                  set('catv.expiryDate', format(lastDay, 'yyyy-MM-dd'));
                }
              }}>
                <option value="regular">Regular</option>
                <option value="dtd">DTD</option>
                <option value="amp">AMP (auto-paid by us)</option>
              </select>
            ) : <V>{customer.catv?.catvType === 'dtd' ? 'DTD' : customer.catv?.catvType === 'amp' ? 'AMP' : 'Regular'}</V>}
          </Row>
          {form.catv.catvType === 'dtd' && (
            <Row label="1/2/3-Month Recharge">
              {editing ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '.88rem' }}>
                  <input type="checkbox" checked={!!form.catv.dtdAllowShortMonths} onChange={e => set('catv.dtdAllowShortMonths', e.target.checked)} />
                  Allow this customer to recharge for 1, 2, or 3 months (not just 3/6/12)
                </label>
              ) : <V>{customer.billing?.dtdAllowShortMonths ? 'Allowed' : 'Not allowed'}</V>}
            </Row>
          )}
          <Row label="Expiry Date">
            {editing ? <input type="date" style={inp} value={form.catv.expiryDate} onChange={e => set('catv.expiryDate', e.target.value)} /> : <V>{toInputDate(customer.catv?.expiryDate)}</V>}
          </Row>
          <Row label="CATV Active">
            {editing ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '.88rem' }}>
                <input type="checkbox" checked={form.catv.isActive} onChange={e => set('catv.isActive', e.target.checked)} />
                {form.catv.isActive ? 'Active' : 'Inactive'}
              </label>
            ) : <V>{customer.catv?.isActive ? 'Active' : 'Inactive'}</V>}
          </Row>
          <Row label="Total Pending">
            {editing ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '.88rem' }}>
                <input type="checkbox" checked={!!form.catv.excludeFromPending} onChange={e => set('catv.excludeFromPending', e.target.checked)} />
                Exclude this customer from the Total Pending Amount
              </label>
            ) : <V>{customer.catv?.excludeFromPending ? 'Excluded from Total Pending Amount' : 'Included'}</V>}
          </Row>
          </div>

        </div>
      </form>
    );
  }

  /* ════════════════════════════════════════════
     Broadband — existing compact grid layout
  ════════════════════════════════════════════ */
  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '.78rem', color: 'var(--c-6b7280)', marginBottom: 3, display: 'block' };
  const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 };

  return (
    <form onSubmit={handleSave}>
      {RetrackModal}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Edit Customer</h3>
        <button type="submit" className="btn btn-primary" disabled={loading} style={{ background: 'var(--cb-1e293b)', borderColor: 'var(--cb-1e293b)' }}>
          <Save size={13} /> {loading ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
        <div>
          <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-6b7280)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e5e7eb)' }}>Basic Info</div>
          <div style={grid2}>
            <div><label style={labelStyle}>Billing Number</label><input style={{ ...inputStyle, fontFamily: 'monospace' }} type="number" min="0.1" step="any" value={form.billingNumber} onChange={e => set('billingNumber', e.target.value)} /></div>
            <div><label style={labelStyle}>Full Name *</label><input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} required /></div>
            <div><label style={labelStyle}>Mobile</label><input style={inputStyle} value={form.mobile} onChange={e => set('mobile', e.target.value)} /></div>
            <div><label style={labelStyle}>Alt. Mobile</label><input style={inputStyle} value={form.alternateMobile} onChange={e => set('alternateMobile', e.target.value)} /></div>
            <div><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div>
              <label style={labelStyle}>Customer Type</label>
              <select style={inputStyle} value={form.durationType} onChange={e => set('durationType', e.target.value)}>
                <option value="regular">Regular</option>
                <option value="dtd">DTD</option>
                <option value="amp">AMP (auto-paid by us)</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-6b7280)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e5e7eb)' }}>Address</div>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={grid2}>
              <div>
                <label style={labelStyle}>Area (Society)</label>
                <AreaSelect style={inputStyle} value={form.address.area} onChange={v => set('address.area', v)} areas={areas} />
              </div>
              <div><label style={labelStyle}>House / Flat No</label><input style={inputStyle} value={form.broadband.houseNumber} onChange={e => set('broadband.houseNumber', e.target.value)} /></div>
            </div>
            <div style={{ fontSize: '.8rem', color: 'var(--c-374151)', background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 4, padding: '6px 10px' }}>
              <strong>Address:</strong> {[form.address.area, form.broadband.houseNumber].filter(Boolean).join(' - ') || '—'}
            </div>
            <div><label style={labelStyle}>Address Line 1</label><input style={inputStyle} value={form.address.line1} onChange={e => set('address.line1', e.target.value)} /></div>
            <div><label style={labelStyle}>City</label><input style={inputStyle} value={form.address.city} onChange={e => set('address.city', e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-6b7280)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e5e7eb)' }}>Billing Address</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 10, fontSize: '.85rem' }}>
          <input type="checkbox" checked={form.billingAddress.sameAsInstallation}
            onChange={e => set('billingAddress.sameAsInstallation', e.target.checked)} />
          Same as installation address above
        </label>
        {!form.billingAddress.sameAsInstallation && (
          <div style={grid2}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Billing Address Line 1</label>
              <input style={inputStyle} value={form.billingAddress.line1} onChange={e => set('billingAddress.line1', e.target.value)} placeholder="Flat/House no., street" />
            </div>
            <div><label style={labelStyle}>City</label><input style={inputStyle} value={form.billingAddress.city} onChange={e => set('billingAddress.city', e.target.value)} /></div>
            <div><label style={labelStyle}>Pincode</label><input style={inputStyle} value={form.billingAddress.pincode} onChange={e => set('billingAddress.pincode', e.target.value)} /></div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--accent)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cb-e0f2fe)' }}>Broadband</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          <div><label style={labelStyle}>PPPoE Username</label><input style={inputStyle} value={form.broadband.username} onChange={e => set('broadband.username', e.target.value)} /></div>
          <div><ISPVlanSelect ispName={form.broadband.ispName} onChange={({ ispName }) => set('broadband.ispName', ispName)} /></div>
          <div><label style={labelStyle}>Expiry Date</label><input type="date" style={inputStyle} value={form.broadband.expiryDate} onChange={e => set('broadband.expiryDate', e.target.value)} /></div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 10, fontSize: '.85rem' }}>
          <input type="checkbox" checked={form.broadband.notifyOnuLoss}
            onChange={e => set('broadband.notifyOnuLoss', e.target.checked)} />
          Notify via WhatsApp if connection drops (ONU signal loss)
        </label>
        {customer.broadband?.plan?.name === 'LCV_COMBO_NET_MONTHLY' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 10, fontSize: '.85rem' }}>
            <input type="checkbox" checked={form.broadband.autoRenew}
              onChange={e => set('broadband.autoRenew', e.target.checked)} />
            Auto-Renew (extends validity by 30 days automatically each cycle — no bill or manual renewal)
          </label>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={labelStyle}>Internal Notes</label>
        <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." />
      </div>
    </form>
  );
}

// Re-reads the live subscriber right after a write to confirm Netzur actually applied it.
// Netzur's /subscribers/update can return {status:true, message:"...updated successfully"}
// while silently ignoring the field — confirmed live 2026-08-12 on subscriber 413924
// (VASUDEV_A63): `enabled:1` and `temporary_suspended` writes no-opped this way (repeated,
// still Blocked afterward, on both the imbs. and ais. hosts), while an unrelated field
// (`comment`) on the same subscriber DID save. Netzur's own audit trail shows the disable
// that stuck was made by a human panel login, and every partner-API write is stamped
// "CRM" — so the partner token most likely can't write these two account-state fields at
// all and never could (not a KYC gate: kyc_verified=0 on all 446 subscribers, ruled out
// as a discriminator). Surfacing the truth (instead of a false "success" toast) is the
// achievable fix from our side until Netzur documents a real enable/disable endpoint.
async function verifyImbsWrite(subscriberId, checkApplied) {
  const { data } = await imbsAPI.getSubscriber(subscriberId);
  const sub = data?.data;
  return { applied: checkApplied(sub), sub };
}

function imbsWriteFailureMessage(sub) {
  const shown = sub?.connection_status || 'unchanged';
  return `Netzur accepted the request but didn't apply it — the subscriber still shows "${shown}". Use Netzur's own dashboard to change this subscriber's status.`;
}

// ─── Netzur Disable/Enable Button ────────────────────────────────────────────────
// Directly toggles the subscriber's enabled/disabled state in Netzur (the same on/off control
// Netzur's own subscriber screen offers) — mirrored onto broadband.isActive server-side so this
// never drifts from the header's own Active/Inactive toggle. Only meaningful for a customer
// with a broadband service; Netzur is a broadband/PPPoE billing system, not CATV.
// `pill` matches the borderless-pill buttons in the Hardware section (Linked OLT / TR069 /
// Fetch OLT) — used when this is rendered there instead of alongside the Netzur tab's own
// btn-secondary siblings.
// `liveEnabled` — Netzur's own `enabled` field (from a live getSubscriber read, see
// getImbsStatus) — is passed in where available. It's the direct signal for what this
// button controls, unlike customer.broadband.isActive, which only updates when this
// button's own toggle runs and goes stale if the subscriber is disabled from elsewhere
// (Netzur's own panel, confirmed live 2026-08-12 with VASUDEV_A63). Falls back to
// broadband.isActive when no live read is available (e.g. the Netzur Integration tab,
// which doesn't fetch the live subscriber).
function ImbsDisconnectButton({ customer, onCustomerUpdate, pill = false, liveEnabled }) {
  const [loading, setLoading] = React.useState(false);
  const isActive = liveEnabled ?? !!customer.broadband?.isActive;
  const handleToggle = async () => {
    const next = !isActive;
    if (!window.confirm(next
      ? 'Enable this subscriber in Netzur?'
      : "Disable this subscriber in Netzur? Their broadband connection will stop working immediately.")) return;
    setLoading(true);
    try {
      await imbsAPI.setStatus(customer._id, next);
      const { applied, sub } = await verifyImbsWrite(
        customer.imbs.subscriberId,
        s => (s?.enabled === '1' || s?.enabled === 1) === next
      );
      if (applied) toast.success(next ? 'Enabled in Netzur' : 'Disabled in Netzur');
      else toast.error(imbsWriteFailureMessage(sub), { duration: 9000 });
      onCustomerUpdate();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status in Netzur');
    } finally {
      setLoading(false);
    }
  };
  const color = isActive ? 'var(--c-dc2626)' : 'var(--c-16a34a)';
  return (
    <button
      className={pill ? undefined : 'btn btn-secondary btn-sm'}
      style={pill ? {
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 6, border: `1px solid ${isActive ? 'var(--cb-fca5a5)' : 'var(--cb-86efac)'}`,
        background: 'var(--cb-ffffff)', color, fontSize: '.75rem', fontWeight: 600, cursor: loading ? 'default' : 'pointer',
      } : { fontSize: '.78rem', color, borderColor: isActive ? 'var(--cb-fca5a5)' : 'var(--cb-86efac)' }}
      onClick={handleToggle}
      disabled={loading}
    >
      {isActive ? <PowerOff size={12} /> : <Power size={12} />} {loading ? 'Working…' : (isActive ? 'Disable' : 'Enable')}
    </button>
  );
}

// ─── Netzur Disconnect (kick session) Button ─────────────────────────────────────
// One-shot action, not a toggle: force-drops the subscriber's live PPPoE session in Netzur.
// The account stays enabled — the customer's router simply re-dials and reconnects on its
// own — distinct from ImbsDisconnectButton above, which actually blocks reconnection.
function ImbsKickButton({ customer, onCustomerUpdate, pill = false }) {
  const [loading, setLoading] = React.useState(false);
  const handleKick = async () => {
    if (!window.confirm("Force-disconnect this subscriber's live session in Netzur? Their router will reconnect on its own if the account is still enabled.")) return;
    setLoading(true);
    try {
      await imbsAPI.kickSession(customer._id);
      toast.success('Session disconnected in Netzur');
      onCustomerUpdate();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to disconnect session in Netzur');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      className={pill ? undefined : 'btn btn-secondary btn-sm'}
      style={pill ? {
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 6, border: '1px solid var(--cb-d1d5db)',
        background: 'var(--cb-ffffff)', color: 'var(--c-374151)', fontSize: '.75rem', fontWeight: 600, cursor: loading ? 'default' : 'pointer',
      } : { fontSize: '.78rem', color: 'var(--c-374151)', borderColor: 'var(--cb-d1d5db)' }}
      onClick={handleKick}
      disabled={loading}
    >
      <Unplug size={12} /> {loading ? 'Working…' : 'Disconnect'}
    </button>
  );
}

// ─── Netzur KYC / e-KYC panel ───────────────────────────────────────────────────
// Shows what Netzur actually holds for this subscriber's KYC (read live) and lets staff push the
// CRM's photo/signature images. Netzur has no e-KYC endpoint on its API, so Aadhaar e-KYC itself
// (and Manual KYC / Skip KYC) still happens in the Netzur panel — the link below opens it.
// ID and Address proof can't be pushed at all: Netzur's KYC Setting doesn't list them as mandatory.
function ImbsKycPanel({ customer }) {
  const [state, setState] = React.useState(null);   // null = loading, false = failed
  const [busy, setBusy] = React.useState(false);
  const [ekycBusy, setEkycBusy] = React.useState(false);
  const load = React.useCallback(() => {
    setState(null);
    imbsAPI.getKyc(customer._id)
      .then(r => setState(r.data?.data || false))
      .catch(() => setState(false));
  }, [customer._id]);
  React.useEffect(() => { load(); }, [load]);

  // Netzur page URLs carry an encrypted token only the panel's own search returns, so the backend
  // (imbsPanelService, using the panel login saved in Settings → Netzur) looks it up and we open
  // this customer's Documents page — "Verify e-KYC" is at its top — in a side window. If no panel
  // login is saved or the lookup fails, fall back to the panel's subscriber list (/op/, not
  // /admin/ — site logins get Access Denied there) with the username on the clipboard.
  const openImbsEkyc = async () => {
    const w = 520, h = Math.min(window.screen.availHeight, 860);
    // Open synchronously (popup blockers only allow it inside the click), navigate afterwards.
    const win = window.open('about:blank', 'imbs-ekyc',
      `popup=yes,width=${w},height=${h},left=${Math.max(0, window.screen.availWidth - w)},top=0`);
    const username = customer.broadband?.username || '';
    const fallback = (why) => {
      if (username) {
        navigator.clipboard?.writeText(username)
          .then(() => toast(`${why} Copied "${username}" — paste it in Netzur search, then Documents → Verify e-KYC`, { icon: 'ℹ️' }))
          .catch(() => {});
      }
      if (win) win.location.href = 'https://imbs.janitornetwork.com/op/subscribers';
      else window.open('https://imbs.janitornetwork.com/op/subscribers', '_blank', 'noopener');
    };
    setEkycBusy(true);
    try {
      const { data } = await imbsAPI.getKycPanelLink(customer._id);
      const url = data?.data?.url;
      if (url) {
        if (win) win.location.href = url; else window.open(url, '_blank', 'noopener');
      } else {
        fallback(data?.data?.reason === 'not_configured' ? 'Netzur panel login not set in Settings.' : `Direct link failed (${data?.data?.message || 'unknown'}).`);
      }
    } catch (err) {
      fallback('Direct link failed.');
    } finally { setEkycBusy(false); }
  };

  const push = async () => {
    setBusy(true);
    try {
      const { data } = await imbsAPI.pushKycDocuments(customer._id);
      const r = data?.data || {};
      const results = r.results || [];
      if (results.length && results.every(x => x.ok)) toast.success(`Sent to Netzur: ${results.map(x => x.kind).join(' + ')}`);
      else if (results.some(x => !x.ok)) toast.error(`Netzur did not keep: ${results.filter(x => !x.ok).map(x => x.kind).join(', ')}`);
      else toast(r.reason || 'Nothing new to send', { icon: 'ℹ️' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Push failed');
    } finally { setBusy(false); }
  };

  const box = { background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: '16px', marginBottom: 14 };
  const head = { fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-374151)', marginBottom: 12 };
  const chip = (ok, text, note) => (
    <span title={note} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.78rem', fontWeight: 600, padding: '3px 9px', borderRadius: 12,
      background: ok ? 'var(--cb-dcfce7)' : 'var(--cb-fee2e2)', color: ok ? 'var(--c-15803d)' : 'var(--c-b91c1c)' }}>
      {ok ? <CheckCircle size={12} /> : <XCircle size={12} />} {text}
    </span>
  );

  if (state === null) return <div style={box}><div style={head}>KYC / e-KYC (Netzur)</div><span style={{ fontSize: '.82rem', color: 'var(--c-6b7280)' }}>Reading from Netzur…</span></div>;
  if (state === false || !state.linked) return null;
  const k = state.kyc;
  const missingMandatory = !(k.photo && k.signature && k.caf);
  const failed = (state.crm || []).filter(d => d.sync === 'failed');

  return (
    <div style={{ ...box, background: missingMandatory ? 'var(--cb-fffbeb)' : 'var(--cb-f0fdf4)', borderColor: missingMandatory ? 'var(--cb-fde68a)' : 'var(--cb-86efac)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <div style={{ ...head, marginBottom: 0 }}>KYC / e-KYC (Netzur)</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {state.canPush?.length > 0 && (
            <button className="btn btn-primary btn-sm" style={{ fontSize: '.78rem' }} onClick={push} disabled={busy}
              title="Sends this customer's photo/signature images from the CRM to Netzur. Never overwrites files Netzur already has.">
              <UploadCloud size={12} /> {busy ? 'Sending…' : `Push ${state.canPush.join(' + ')} to Netzur`}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }} onClick={openImbsEkyc} disabled={ekycBusy}
            title="Opens this customer's Documents page in Netzur (Verify e-KYC is at the top). Aadhaar e-KYC itself is done in Netzur.">
            <ExternalLink size={12} /> {ekycBusy ? 'Opening…' : 'Open Netzur for e-KYC'}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {chip(k.photo, 'Photo', 'Mandatory in Netzur')}
        {chip(k.signature, 'Signature', 'Mandatory in Netzur')}
        {chip(k.caf, 'CAF form', 'Mandatory in Netzur — the CRM has no CAF to push, upload it in Netzur')}
        {chip(k.kycVerified, k.kycVerified ? 'KYC verified' : 'KYC not verified', 'Netzur Manual KYC / e-KYC status')}
      </div>
      <div style={{ fontSize: '.76rem', color: 'var(--c-6b7280)', lineHeight: 1.5 }}>
        {missingMandatory
          ? 'Netzur shows a KYC warning until all mandatory documents are uploaded or KYC is verified/skipped in the Netzur panel.'
          : 'All mandatory documents are in Netzur.'}
        {' '}ID proof and Address proof can't be pushed — Netzur's KYC Setting doesn't accept them.
        {state.autoPush ? ' Auto-push is on for new uploads.' : ' Auto-push is off.'}
      </div>
      {failed.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '.76rem', color: 'var(--c-b91c1c)' }}>
          Last push failed for: {failed.map(d => `${d.label} (${d.error || 'unknown error'})`).join('; ')}
        </div>
      )}
    </div>
  );
}

// ─── Reset MAC Button ─────────────────────────────────────────────────────────
function ResetMacButton({ customerId }) {
  const [loading, setLoading] = React.useState(false);
  const handleReset = async () => {
    if (!window.confirm('Reset MAC binding for this subscriber in Netzur?')) return;
    setLoading(true);
    try {
      await imbsAPI.resetMac(customerId);
      toast.success('MAC address reset in Netzur');
    } catch (err) {
      toast.error(err.response?.data?.message || 'MAC reset failed');
    } finally { setLoading(false); }
  };
  return (
    <button className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }} onClick={handleReset} disabled={loading}>
      <ShieldOff size={12} /> {loading ? 'Resetting…' : 'Reset MAC'}
    </button>
  );
}

// ─── Netzur Create Subscriber Modal ────────────────────────────────────────────
// Module scope on purpose — inline definitions remount inputs on every keystroke.
function ImbsCreateSubscriberModal({ customer, onClose, onCreated }) {
  const inp = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box' };
  const lbl = { fontSize: '.78rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' };

  const addr = customer.billingAddress?.sameAsInstallation === false && (customer.billingAddress?.line1 || customer.billingAddress?.city)
    ? customer.billingAddress
    : (customer.address || {});
  const username = customer.broadband?.username || '';
  const [form, setForm] = useState({
    username,
    password: 'Admin@123',
    portal_username: username,
    portal_password: 'Admin@123',
    fullname: customer.name || '',
    mobile_no: customer.mobile || '',
    email: customer.email || '',
    address: [addr.line1, addr.line2].filter(Boolean).join(', '),
    city: addr.city || 'Ahmedabad',
    zip: addr.pincode || '',
    package_id: '',
    mac_check: true,
  });
  const [packages, setPackages] = useState([]);
  const [pkgsLoading, setPkgsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    imbsAPI.getPackages()
      .then(r => {
        const raw = r.data?.data;
        const list = raw?.data || raw || [];
        setPackages(Array.isArray(list) ? list : []);
      })
      .catch(() => setPackages([]))
      .finally(() => setPkgsLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) return toast.error('Username is required');
    setSaving(true);
    try {
      const trimmedUsername = form.username.trim();
      const trimmedPassword = form.password || 'Admin@123';
      const { data } = await imbsAPI.createSubscriber(customer._id, {
        ...form,
        username: trimmedUsername,
        password: trimmedPassword,
        portal_username: form.portal_username || trimmedUsername,
        portal_password: form.portal_password || 'Admin@123',
        ...(form.package_id ? { package_id: form.package_id } : {}),
      });
      toast.success(data.message || 'Netzur subscriber created and linked');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Netzur subscriber creation failed', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={handleSubmit} style={{ background: 'var(--cb-ffffff)', borderRadius: 8, padding: 20, width: 560, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 2px', fontSize: '1rem', fontWeight: 600 }}>Create Netzur Subscriber</h3>
        <p style={{ margin: '0 0 14px', fontSize: '.78rem', color: 'var(--c-6b7280)' }}>
          Creates the subscriber in Netzur and links it to this customer.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <div>
            <label style={lbl}>Username (PPPoE) *</label>
            <input style={inp} value={form.username} onChange={e => set('username', e.target.value)} required />
          </div>
          <div>
            <label style={lbl}>Password</label>
            <input style={inp} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Defaults to Admin@123" />
          </div>
          <div>
            <label style={lbl}>Portal Username</label>
            <input style={inp} value={form.portal_username} onChange={e => set('portal_username', e.target.value)} placeholder="Defaults to username" />
          </div>
          <div>
            <label style={lbl}>Portal Password</label>
            <input style={inp} value={form.portal_password} onChange={e => set('portal_password', e.target.value)} placeholder="Defaults to Admin@123" />
          </div>
          <div>
            <label style={lbl}>Full Name</label>
            <input style={inp} value={form.fullname} onChange={e => set('fullname', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Mobile</label>
            <input style={inp} value={form.mobile_no} onChange={e => set('mobile_no', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Address</label>
            <input style={inp} value={form.address} onChange={e => set('address', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>City</label>
            <input style={inp} value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Pincode</label>
            <input style={inp} value={form.zip} onChange={e => set('zip', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Package</label>
            <SearchableSelect
              value={form.package_id}
              onChange={v => set('package_id', v)}
              placeholder={pkgsLoading ? 'Loading packages…' : 'Search package (optional)…'}
              options={packages.map(pkg => {
                const id    = String(pkg.id || pkg.package_id || '');
                const name  = pkg.name || pkg.package_name || id;
                const speed = pkg.download_speed || pkg.speed ? ` · ${pkg.download_speed || pkg.speed} Mbps` : '';
                const price = pkg.price || pkg.monthly_price ? ` · ₹${pkg.price || pkg.monthly_price}` : '';
                return { value: id, label: `${name}${speed}${price}` };
              })}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer', gridColumn: '1 / -1' }}>
            <input type="checkbox" checked={form.mac_check} onChange={e => set('mac_check', e.target.checked)} />
            MAC Check
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
            {saving ? 'Creating…' : 'Create & Link'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Netzur Panel ──────────────────────────────────────────────────────────────
function IMBSPanel({ customer, onCustomerUpdate }) {
  const linked = !!customer.imbs?.subscriberId;
  const [subSearch, setSubSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sessions, setSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [rechargeForm, setRechargeForm] = useState({ package_id: '', amount: '', payment_method: 'cash', remarks: '' });
  const [recharging, setRecharging] = useState(false);
  const [autoRenew, setAutoRenew] = useState(customer.imbs?.autoRenew ?? false);
  const [autoRenewCycles, setAutoRenewCycles] = useState('');
  const [autoRenewSaving, setAutoRenewSaving] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryLoading, setExpiryLoading] = useState(false);
  const [pushAmount, setPushAmount] = useState('');
  const [pushing, setPushing] = useState(false);
  const [imbsPackages, setImbsPackages] = useState([]);
  const [pkgsLoading, setPkgsLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const searchSubscribers = async () => {
    if (!subSearch.trim()) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const params = /^\d+$/.test(subSearch.trim())
        ? { mobile_no: subSearch.trim() }
        : { username: subSearch.trim() };
      const { data } = await imbsAPI.getSubscribers(params);
      const list = data.data?.data || data.data || [];
      setSearchResults(Array.isArray(list) ? list.slice(0, 10) : []);
      if (!list.length) toast('No Netzur subscribers found', { icon: '🔍' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async (imbsId) => {
    try {
      await imbsAPI.linkCustomer(customer._id, String(imbsId));
      toast.success('Linked to Netzur subscriber');
      setSearchResults([]);
      setSubSearch('');
      onCustomerUpdate();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm('Unlink this customer from Netzur?')) return;
    try {
      await imbsAPI.unlinkCustomer(customer._id);
      toast.success('Unlinked from Netzur');
      onCustomerUpdate();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unlink failed');
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    setSessions(null);
    try {
      const { data } = await imbsAPI.getSessions(customer._id);
      setSessions(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    setRecharging(true);
    try {
      await imbsAPI.recharge(customer._id, {
        package_id: rechargeForm.package_id,
        amount: rechargeForm.amount,
        payment_method: rechargeForm.payment_method,
        remarks: rechargeForm.remarks,
      });
      toast.success('Recharge sent to Netzur');
      setRechargeForm({ package_id: '', amount: '', payment_method: 'cash', remarks: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Recharge failed');
    } finally {
      setRecharging(false);
    }
  };

  const handleAutoRenew = async (enabled) => {
    setAutoRenewSaving(true);
    try {
      await imbsAPI.setAutoRenew(customer._id, enabled, autoRenewCycles ? Number(autoRenewCycles) : undefined);
      setAutoRenew(enabled);
      toast.success(enabled ? 'Auto-renew enabled in Netzur' : 'Auto-renew disabled in Netzur');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update auto-renew');
    } finally {
      setAutoRenewSaving(false);
    }
  };

  const handleChangeExpiry = async (e) => {
    e.preventDefault();
    if (!expiryDate) { toast.error('Select a date'); return; }
    setExpiryLoading(true);
    try {
      await imbsAPI.changeExpiry(customer._id, expiryDate);
      toast.success('Expiry date updated in Netzur');
      setExpiryDate('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update expiry');
    } finally {
      setExpiryLoading(false);
    }
  };

  const handlePushPayment = async (e) => {
    e.preventDefault();
    if (!pushAmount) { toast.error('Enter amount'); return; }
    setPushing(true);
    try {
      await imbsAPI.pushPayment(customer._id, { amount: parseFloat(pushAmount) });
      toast.success('Payment pushed to Netzur');
      setPushAmount('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  useEffect(() => {
    if (!linked) return;
    setPkgsLoading(true);
    imbsAPI.getPackages()
      .then(r => {
        const raw = r.data?.data;
        const list = raw?.data || raw || [];
        setImbsPackages(Array.isArray(list) ? list : []);
      })
      .catch(() => setImbsPackages([]))
      .finally(() => setPkgsLoading(false));
  }, [linked]); // eslint-disable-line

  const handlePackageSelect = (pkgId) => {
    const pkg = imbsPackages.find(p => String(p.id || p.package_id) === pkgId);
    setRechargeForm(f => ({ ...f, package_id: pkgId, amount: pkg ? String(pkg.price || pkg.monthly_price || '') : '' }));
  };
  const selectedPackage = imbsPackages.find(p => String(p.id || p.package_id) === rechargeForm.package_id);

  const inp = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box' };
  const lbl = { fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' };
  const sec = { background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: '16px', marginBottom: 14 };
  const secHead = { fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-374151)', marginBottom: 12 };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 600 }}>Netzur Integration</h3>
      <p style={{ margin: '0 0 16px', fontSize: '.82rem', color: 'var(--c-6b7280)' }}>Netzur ISP Billing System sync for this subscriber</p>

      {/* Link status */}
      <div style={{ ...sec, background: linked ? 'var(--cb-f0fdf4)' : 'var(--cb-fff7ed)', borderColor: linked ? 'var(--cb-86efac)' : 'var(--cb-fed7aa)' }}>
        <div style={secHead}>Subscriber Link</div>
        {linked ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={16} color="var(--c-16a34a)" />
              <span style={{ fontSize: '.88rem', fontWeight: 600, color: 'var(--c-166534)' }}>
                Linked — Netzur ID: <code style={{ fontFamily: 'monospace' }}>{customer.imbs.subscriberId}</code>
              </span>
              <span style={{
                fontSize: '.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 3,
                background: customer.imbs?.isActive ? 'var(--cb-dcfce7)' : 'var(--cb-fee2e2)',
                color:      customer.imbs?.isActive ? 'var(--c-15803d)' : 'var(--c-dc2626)',
              }}>
                {customer.imbs?.isActive
                  ? `Active · ${customer.imbs.packageName || 'Package assigned'}`
                  : 'No Package'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '.78rem' }}
                onClick={async () => {
                  try {
                    const { data } = await imbsAPI.pullAddress(customer._id);
                    toast.success(`Address pulled from Netzur: ${data.data?.line1 || ''}`);
                    onCustomerUpdate();
                  } catch (err) {
                    toast.error(err.response?.data?.message || 'Pull address failed');
                  }
                }}
              >
                <MapPin size={12} /> Pull Address
              </button>
              <ResetMacButton customerId={customer._id} />
              {['broadband', 'both'].includes(customer.customerType) && (
                <ImbsDisconnectButton customer={customer} onCustomerUpdate={onCustomerUpdate} />
              )}
              <button onClick={handleUnlink} className="btn btn-secondary btn-sm" style={{ color: 'var(--c-dc2626)', borderColor: 'var(--cb-fca5a5)', fontSize: '.78rem' }}>
                <Unlink size={12} /> Unlink
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--c-92400e)' }}>Not linked. Search for an Netzur subscriber to link this customer, or create a new one.</p>
              <button className="btn btn-primary btn-sm" style={{ fontSize: '.78rem' }} onClick={() => setCreateOpen(true)}>
                + Create Subscriber
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input
                style={{ ...inp, flex: 1 }}
                placeholder="Username or mobile…"
                value={subSearch}
                onChange={e => setSubSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchSubscribers()}
              />
              <button className="btn btn-primary btn-sm" onClick={searchSubscribers} disabled={searching}>
                {searching ? '…' : 'Search'}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 4, overflow: 'hidden' }}>
                {searchResults.map((sub, i) => (
                  <div key={sub.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--cb-f3f4f6)', background: 'var(--cb-ffffff)' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{sub.name || sub.full_name}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>{sub.username} · {sub.mobile_no || sub.mobile} · ID: {sub.id || sub.subscriber_id}</div>
                    </div>
                    <button className="btn btn-primary btn-sm" style={{ fontSize: '.75rem' }} onClick={() => handleLink(sub.id || sub.subscriber_id)}>
                      <Link2 size={11} /> Link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {linked && <ImbsKycPanel customer={customer} />}

      {linked && (
        <>
          {/* Online Sessions */}
          <div style={sec}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={secHead}>Online Sessions</div>
              <button className="btn btn-secondary btn-sm" onClick={loadSessions} disabled={sessionsLoading}>
                <Wifi size={12} /> {sessionsLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {sessions === null && !sessionsLoading && (
              <p style={{ fontSize: '.82rem', color: 'var(--c-9ca3af)', margin: 0 }}>Click Refresh to load live session data.</p>
            )}
            {sessions !== null && (
              (() => {
                const list = sessions?.session || (Array.isArray(sessions) ? sessions : []);
                if (!list.length) return <p style={{ fontSize: '.82rem', color: 'var(--c-9ca3af)', margin: 0 }}>No active sessions.</p>;
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                          {['IP Address', 'MAC', 'Start Time', 'Duration', 'Download', 'Upload', 'NAS'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', fontSize: '.73rem' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((s, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                            <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{s.framedipaddress || s.ip_address || '—'}</td>
                            <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: '.73rem' }}>{s.callingstationid || s.mac_address || '—'}</td>
                            <td style={{ padding: '7px 10px', color: 'var(--c-6b7280)' }}>{s.acctstarttime || s.start_time || '—'}</td>
                            <td style={{ padding: '7px 10px', color: 'var(--c-6b7280)' }}>{fmtDuration(s.acctsessiontime)}</td>
                            <td style={{ padding: '7px 10px' }}>{fmtBytes(s.acctinputoctets ?? s.download)}</td>
                            <td style={{ padding: '7px 10px' }}>{fmtBytes(s.acctoutputoctets ?? s.upload)}</td>
                            <td style={{ padding: '7px 10px', color: 'var(--c-6b7280)', fontSize: '.73rem' }}>{s.nasipaddress || s.nas || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </div>

          {/* Push Payment */}
          <div style={sec}>
            <div style={secHead}>Push Payment to Netzur</div>
            <form onSubmit={handlePushPayment} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Amount (₹)</label>
                <input type="number" min="1" style={inp} value={pushAmount} onChange={e => setPushAmount(e.target.value)} placeholder="Amount" />
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={pushing}>
                <Zap size={12} /> {pushing ? 'Pushing…' : 'Push'}
              </button>
            </form>
          </div>

          {/* Recharge */}
          <div style={sec}>
            <div style={secHead}>Recharge in Netzur</div>
            <form onSubmit={handleRecharge}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <label style={lbl}>Package</label>
                  <SearchableSelect
                    value={rechargeForm.package_id}
                    onChange={handlePackageSelect}
                    placeholder={pkgsLoading ? 'Loading packages…' : imbsPackages.length ? 'Search package…' : 'No packages found'}
                    options={imbsPackages.map(pkg => {
                      const id    = String(pkg.package_id ?? pkg.id ?? '');
                      const name  = pkg.package_name || pkg.name || id;
                      const speed = pkg.down_speed ? ` · ${pkg.down_speed}${pkg.down_speed_unit || 'Mbps'}` : '';
                      const price = pkg.price != null ? ` · ₹${pkg.price}` : '';
                      const ott   = pkg.ott_id ? ' · OTT' : '';
                      return { value: id, label: `${name}${speed}${price}${ott}` };
                    })}
                  />
                </div>
                <div>
                  <label style={lbl}>Amount (₹)</label>
                  <input type="number" min="0" style={inp} value={rechargeForm.amount} onChange={e => setRechargeForm(f => ({ ...f, amount: e.target.value }))} placeholder="Amount" />
                </div>
              </div>

              {selectedPackage && (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '.78rem', color: 'var(--c-374151)', background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 4, padding: '8px 12px', marginBottom: 10 }}>
                  {selectedPackage.down_speed && <span><strong>Speed:</strong> {selectedPackage.down_speed}/{selectedPackage.up_speed || selectedPackage.down_speed} {selectedPackage.down_speed_unit || 'Mbps'}</span>}
                  {selectedPackage.expiration_limiter === '1' && selectedPackage.expiration_limit && (
                    <span><strong>Validity:</strong> {selectedPackage.expiration_limit} {selectedPackage.expiration_limit_unit || 'Days'}</span>
                  )}
                  {selectedPackage.data_limiter === '1' && selectedPackage.data_limit > 0 && (
                    <span><strong>Data Cap:</strong> {selectedPackage.data_limit} {selectedPackage.data_limit_unit || 'GB'}</span>
                  )}
                  {selectedPackage.ott_id && <span style={{ color: 'var(--c-7c3aed)', fontWeight: 600 }}>Includes OTT bundle</span>}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <label style={lbl}>Payment Method</label>
                  <PaymentMethodSelect value={rechargeForm.payment_method} onChange={v => setRechargeForm(f => ({ ...f, payment_method: v }))} />
                </div>
                <div>
                  <label style={lbl}>Remarks</label>
                  <input style={inp} value={rechargeForm.remarks} onChange={e => setRechargeForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Optional note" />
                </div>
              </div>

              <button type="submit" className="btn btn-primary btn-sm" disabled={recharging || !rechargeForm.package_id}>
                <RefreshCw size={12} /> {recharging ? 'Recharging…' : 'Recharge'}
              </button>
            </form>
          </div>

          {/* Auto-Renew — Netzur's mechanism for queuing the subscriber's next recharge to
              fire automatically on expiry, instead of a one-off immediate recharge above */}
          <div style={sec}>
            <div style={secHead}>Auto-Renew (Scheduled Recharge)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: autoRenew ? 10 : 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={autoRenew} disabled={autoRenewSaving}
                  onChange={e => handleAutoRenew(e.target.checked)} />
                {autoRenew ? 'Auto-renew is ON — same package renews automatically on expiry' : 'Auto-renew is off'}
              </label>
            </div>
            {autoRenew && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, maxWidth: 220 }}>
                  <label style={lbl}>Renew for how many cycles?</label>
                  <input type="number" min="1" style={inp} value={autoRenewCycles}
                    onChange={e => setAutoRenewCycles(e.target.value)} placeholder="Leave blank = indefinitely" />
                </div>
                <button type="button" className="btn btn-secondary btn-sm" disabled={autoRenewSaving} onClick={() => handleAutoRenew(true)}>
                  {autoRenewSaving ? 'Saving…' : 'Update'}
                </button>
              </div>
            )}
          </div>

          {/* Change Expiry */}
          <div style={sec}>
            <div style={secHead}>Change Expiry Date in Netzur</div>
            <form onSubmit={handleChangeExpiry} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>New Expiry Date</label>
                <input type="date" style={inp} value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={expiryLoading}>
                <Calendar size={12} /> {expiryLoading ? 'Saving…' : 'Update'}
              </button>
            </form>
          </div>
        </>
      )}

      {createOpen && (
        <ImbsCreateSubscriberModal
          customer={customer}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); onCustomerUpdate(); }}
        />
      )}
    </div>
  );
}

// ─── Documents Panel ─────────────────────────────────────────────────────────
function DocumentsPanel({ customer, onCustomerUpdate }) {
  const [label, setLabel] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const docs = customer.documents || [];

  const inp = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box' };
  const lbl = { fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error('Choose at least one file');
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    if (label.trim()) fd.append('label', label.trim());
    setUploading(true);
    try {
      const { data } = await customerAPI.uploadDocuments(customer._id, fd);
      toast.success(data.message || 'Documents uploaded');
      setLabel('');
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      onCustomerUpdate();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.label || doc.originalName}"?`)) return;
    try {
      await customerAPI.deleteDocument(customer._id, doc._id);
      toast.success('Document deleted');
      onCustomerUpdate();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  // Documents are staffOrAdmin-gated on the backend now (no public /uploads URL) — a plain
  // <a href> would 401, so fetch as an authenticated blob first, same pattern as receipt PDFs.
  const [viewingId, setViewingId] = useState(null);
  const handleView = async (doc) => {
    setViewingId(doc._id);
    try {
      const { data } = await customerAPI.getDocumentBlob(customer._id, doc._id);
      const url = URL.createObjectURL(data);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Failed to open document');
    } finally {
      setViewingId(null);
    }
  };

  const fmtSize = (b) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`;

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 600 }}>Documents</h3>
      <p style={{ margin: '0 0 16px', fontSize: '.82rem', color: 'var(--c-6b7280)' }}>ID proof, address proof, agreements, photos — images and PDFs up to 15 MB</p>

      <form onSubmit={handleUpload} style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
        <div>
          <label style={lbl}>Label (optional)</label>
          <input style={inp} value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Aadhar Card, Agreement" />
        </div>
        <div>
          <label style={lbl}>Files</label>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*,application/pdf"
            style={{ ...inp, padding: '5px 10px' }}
            onChange={e => setFiles(Array.from(e.target.files || []))}
          />
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={uploading || !files.length}>
          <Plus size={12} /> {uploading ? 'Uploading…' : `Upload${files.length > 1 ? ` (${files.length})` : ''}`}
        </button>
      </form>

      {docs.length === 0 ? (
        <p style={{ fontSize: '.85rem', color: 'var(--c-9ca3af)' }}>No documents uploaded yet.</p>
      ) : (
        <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, overflow: 'hidden' }}>
          {docs.map((doc) => (
            <div key={doc._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--cb-f3f4f6)', background: 'var(--cb-ffffff)' }}>
              {/^image\//.test(doc.mimeType || '') ? (
                <button
                  type="button"
                  onClick={() => handleView(doc)}
                  style={{ padding: 0, border: '1px solid var(--cb-e5e7eb)', borderRadius: 4, background: 'none', cursor: 'pointer', lineHeight: 0 }}
                  title="View"
                >
                  <DocThumb customerId={customer._id} doc={doc} />
                </button>
              ) : (
                <FileText size={30} color="var(--c-dc2626)" style={{ flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.label || doc.originalName}</div>
                <div style={{ fontSize: '.73rem', color: 'var(--c-6b7280)' }}>
                  {doc.originalName} · {fmtSize(doc.size)} · {doc.uploadedAt ? format(new Date(doc.uploadedAt), 'dd-MMM-yyyy') : ''}
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: '.75rem' }} disabled={viewingId === doc._id} onClick={() => handleView(doc)}>
                <ExternalLink size={11} /> {viewingId === doc._id ? 'Opening…' : 'View'}
              </button>
              <button className="btn btn-secondary btn-sm" style={{ color: 'var(--c-dc2626)', borderColor: 'var(--cb-fca5a5)', fontSize: '.75rem' }} onClick={() => handleDelete(doc)}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Thumbnail for an image document — fetched as an authenticated blob (no public URL exists
// for these anymore) and cached as an object URL for the life of this row.
function DocThumb({ customerId, doc }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    customerAPI.getDocumentBlob(customerId, doc._id).then(({ data }) => {
      if (cancelled) return;
      url = URL.createObjectURL(data);
      setSrc(url);
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [customerId, doc._id]);

  return src
    ? <img src={src} alt={doc.label} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4 }} />
    : <div style={{ width: 44, height: 44, borderRadius: 4, background: 'var(--cb-f3f4f6)' }} />;
}

// ─── Renew Panel — plan + validity (days) + auto end date + a freely-editable paid amount ──
function RenewPanel({ customer, payments, onSuccess }) {
  const isBoth = customer.customerType === 'both';
  const [service, setService] = useState(isBoth ? (customer.broadband?.plan ? 'broadband' : 'catv') : (customer.customerType === 'broadband' ? 'broadband' : 'catv'));
  const hasBB = service === 'broadband';
  const paymentFor = hasBB ? 'broadband_renewal' : 'catv_renewal';

  const plan = hasBB ? customer.broadband?.plan : customer.catv?.package;
  const expiryDate = hasBB ? customer.broadband?.expiryDate : customer.catv?.expiryDate;
  const balance = customer.outstandingBalance ?? 0;

  // Plan is just a name + monthly rate — validity (days) and amount below are both fully
  // independent of it; picking a different plan never changes either on its own.
  const [planOptions, setPlanOptions] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(plan?._id || '');
  useEffect(() => {
    setSelectedPlanId(plan?._id || '');
    if (hasBB) broadbandAPI.getPlans({ activeOnly: 'true', limit: 200 }).then(r => setPlanOptions(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
    else       catvAPI.getPackages({ activeOnly: 'true', limit: 200 }).then(r => setPlanOptions(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
  }, [hasBB]); // eslint-disable-line

  const selectedPlan = planOptions.find(o => o._id === selectedPlanId) || plan;
  const planChanged = !!selectedPlanId && selectedPlanId !== (plan?._id || '');

  // Extra packages/plans already attached to the customer — editable right here so staff
  // can add/remove one and bill for it in the same renewal, instead of a separate trip to
  // the Subscription tab. Saved back via ensurePlanAssigned() alongside the primary plan.
  const additionalItems = hasBB ? (customer.broadband?.additionalPlans || []) : (customer.catv?.additionalPackages || []);
  const initialExtraIds = additionalItems.filter(a => a.plan || a.package).map(a => (a.plan || a.package)._id);
  const [extraIds, setExtraIds] = useState(initialExtraIds);
  useEffect(() => { setExtraIds(initialExtraIds); }, [hasBB]); // eslint-disable-line
  const extraTotal = extraIds.reduce((sum, id) => sum + (planOptions.find(o => o._id === id)?.price || 0), 0);
  const extraChanged = JSON.stringify([...extraIds].filter(Boolean).sort()) !== JSON.stringify([...initialExtraIds].sort());
  const subscriptionChanged = planChanged || extraChanged;

  // "Natural" continuation is the day after the current expiry (a 31/07 expiry renews
  // starting 01/08) — even when the expiry is in the past, so renewing an expired customer
  // covers the lapsed period since expiry rather than starting from today. Only a
  // subscription with no expiry at all falls back to today.
  const naturalBase = expiryDate ? addDays(new Date(expiryDate), 1) : new Date();

  // Regular (calendar-month) CATV customers renew in whole calendar months, not a day count —
  // a flat 30 (or any fixed day count) shorts every 31-day month by a day and overcounts every
  // 28/29-day February, so a renewal starting near month-end would land short of (or past) the
  // actual month end. Validity is entered as whole months and the end date is computed with
  // real calendar-month arithmetic (addMonths), so it's exact regardless of month length. DTD
  // (day-to-day) customers and broadband keep the original day-count field, unaffected.
  const catvRegularMonth = !hasBB && customer.billing?.durationType !== 'dtd';
  const isDtdCatv = !hasBB && customer.billing?.durationType === 'dtd';

  const [periodFrom, setPeriodFrom] = useState(format(naturalBase, 'yyyy-MM-dd'));
  // A DTD CableTV customer whose STB is free to use (catv.stbFreeToUse) renews for 90/180/360 days
  // only (owner, 2026-10-04 — same 3/6/12 rule as the portal's CustomerRenewPage), so it starts at 90.
  const dtdDayOptions = isDtdCatv && customer.catv?.stbFreeToUse ? DTD_FREE_STB_DAY_OPTIONS : DTD_DAY_OPTIONS;
  const [periodDays, setPeriodDays] = useState(dtdDayOptions[0]); // DTD / broadband — day-based validity
  // Keep a free-to-use customer inside the list if something else set a shorter count (a 1/2-month tier).
  useEffect(() => {
    if (dtdDayOptions === DTD_FREE_STB_DAY_OPTIONS && !dtdDayOptions.includes(periodDays)) setPeriodDays(dtdDayOptions[0]);
  }, [dtdDayOptions, periodDays]);
  const [validityMonths, setValidityMonths] = useState(1); // regular CATV — month-based validity
  const periodTo = periodFrom
    ? (catvRegularMonth
        ? format(addDays(addMonths(new Date(periodFrom), validityMonths), -1), 'yyyy-MM-dd')
        : format(addDays(new Date(periodFrom), periodDays - 1), 'yyyy-MM-dd'))
    : '';

  // "Valid till 8 PM today" (DTD CATV only) — a same-day trial reactivation: Start Date gets
  // locked to today (Validity stays whatever the user has set — not forced to 1 day), and the
  // bill goes out via "Generate" (unpaid) only. If it's still unpaid once 8 PM passes,
  // cronJobs.js's 8pm-link-reversal job auto-reverses it (deletes the bill, undoes the balance
  // + expiry extension) — see Payment.reverseAt.
  const [tempTill8pm, setTempTill8pm] = useState(false);
  // After-8 PM DTD rule (utils/dtdCutoff.js) — staff confirm before collecting.
  const [show8pmNotice, setShow8pmNotice] = useState(false);
  const [showLongTermNotice, setShowLongTermNotice] = useState(false);
  const longTermAuto = useLongTermAutoStart();
  // New CableTV customer (no CableTV payment yet) paying 3/6/12 months → ask box swap or direct order.
  const [showOrderChoice, setShowOrderChoice] = useState(false);
  const [orderChoice, setOrderChoice] = useState(null);
  const choiceMonths = catvRegularMonth ? validityMonths : (periodDays % 30 === 0 ? periodDays / 30 : 0);
  const askOrderChoice = !hasBB && LONG_TERM_MONTHS.includes(choiceMonths)
    && !(payments || []).some(p => p.paymentFor === 'catv_renewal');
  useEffect(() => { if (!isDtdCatv) setTempTill8pm(false); }, [isDtdCatv]);
  useEffect(() => {
    if (!tempTill8pm) return;
    setPeriodFrom(format(new Date(), 'yyyy-MM-dd'));
  }, [tempTill8pm]);

  // Broadband renewal on a customer with linked CATV account(s) auto-generates combo bill(s)
  // on them (see paymentService.generateLinkedComboCatvBills) — the 1:1 linkedAccount (TV1, if
  // it's a CATV/both account) plus any extra "2TV"+ accounts in broadband.linkedCatvAccounts
  // (TV2, TV3…), same ordering as backend utils/comboTv. Each normally starts on the same date
  // as this broadband period; this lets the admin anchor any one of them to a different start
  // date instead (same duration, shifted date), and change its package at the same time.
  const [linkedCatvTargets] = useState(() => {
    const list = [];
    const la = customer.linkedAccount;
    if (la && ['catv', 'both'].includes(la.customerType) && la.catv?.package) list.push({ tvNumber: 1, target: la });
    (customer.broadband?.linkedCatvAccounts || []).forEach((c, i) => {
      if (c && ['catv', 'both'].includes(c.customerType) && c.catv?.package) list.push({ tvNumber: i + 2, target: c });
    });
    return list;
  });
  const showLinkedCatvOption = hasBB && linkedCatvTargets.length > 0;
  // Keyed by target customer id: { enabled, periodFrom, packageId }. Overriding a linked CATV
  // bill's start date is also the point where the admin most often needs to switch that
  // account's package (e.g. upgrading the cable side of a combo mid-renewal) — so each block
  // offers a package picker too, defaulting to whatever's currently assigned; only actually
  // reassigned (via catvAPI.assignPackage) if changed.
  const [catvOverrides, setCatvOverrides] = useState(() => Object.fromEntries(
    linkedCatvTargets.map(({ target }) => [target._id, { enabled: false, periodFrom, packageId: target.catv?.package?._id || '' }])
  ));
  const setCatvOverride = (targetId, patch) => setCatvOverrides(prev => ({ ...prev, [targetId]: { ...prev[targetId], ...patch } }));
  const [linkedCatvPackageOptions, setLinkedCatvPackageOptions] = useState([]);
  useEffect(() => {
    if (!showLinkedCatvOption) return;
    catvAPI.getPackages({ activeOnly: 'true', limit: 200 }).then(r => setLinkedCatvPackageOptions(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
  }, [showLinkedCatvOption]);

  const [form, setForm] = useState({ amount: '', paymentMethod: 'cash', transactionId: '', notes: '', collectDate: format(new Date(), "yyyy-MM-dd'T'HH:mm") });
  const [sendMessage, setSendMessage] = useState(true);
  const [busy, setBusy] = useState(false);
  // Split payment (part cash, part UPI/etc. in one collection) — null when off, else rows
  // that must sum to the paid amount, same as CollectPaymentPanel's SplitPaymentFields.
  const [splitRows, setSplitRows] = useState(null);

  // bix42-style Generate/Generate & Collect/Edit Bill trio (catv only, mirrors the agent
  // app's Renew screen — AgentCollectPaymentPage.js). billTotal is the expected full bill
  // (old due + this period's charge, or the custom Edit Bill total); it's sent to the server
  // so the balance lands exactly on billTotal − collected (due left / advance), same model
  // paymentService.collectPayment already uses for the agent app. billItems (non-null) means
  // a custom bill is in effect — the plain package-rate auto-fill below is skipped so it
  // doesn't clobber it.
  const [billTotal, setBillTotal] = useState(0);
  const [billItems, setBillItems] = useState(null);
  const [editBillOpen, setEditBillOpen] = useState(false);
  // True once billTotal has actually been computed for a CATV renewal (including a
  // legitimately zero/negative result — a credit big enough to fully cover or exceed this
  // period's charge). Distinct from `billTotal > 0`, which used to gate usingBillTotal below
  // and silently misclassified that exact case as "not using a bill total" — see the fix
  // to the three balance-clamp sites (autofill effect, Quick Fill, Clear) below.
  const [catvBillComputed, setCatvBillComputed] = useState(false);

  // CATV renewals auto-price from the monthly rate ON TOP of the old due: every 30 days of
  // validity = one month of package + add-ons, plus the outstanding balance (old amount +
  // renew amount, e.g. due 0 + 90d → ₹1200; due 400 + 30d → ₹800). This CATV-specific
  // balance/rate math does not apply to broadband — see the separate, simpler plan→amount
  // effect below (user rule 2026-08-08) for that.
  // Quick Fill (pricing tiers) sets its own — possibly discounted — amount, so it arms
  // autoFillSkip to keep this effect from overwriting it; the field also stays hand-editable
  // afterwards since the effect only fires when period/plan/add-ons change.
  const autoFillSkip = React.useRef(false);
  useEffect(() => {
    if (hasBB) { setCatvBillComputed(false); return; }
    if (billItems) return; // custom Edit Bill total is in effect — leave it alone
    if (autoFillSkip.current) { autoFillSkip.current = false; return; }
    const monthly = (selectedPlan?.price || 0) + extraTotal;
    if (!monthly) return;
    // Regular CATV bills a flat rate per whole month; DTD (day-based) scales off the 30-day
    // baseline rate instead, since its validity isn't tied to calendar months at all.
    const periodCost = catvRegularMonth ? monthly * validityMonths : Math.round(monthly * periodDays / 30);
    // No clamp here — an existing credit (negative balance) must reduce this bill, not get
    // discarded. billTotal itself can land at or below zero (credit fully covers the period);
    // only the "Paid Amount" shown to staff floors at 0 since you can't collect a negative sum.
    const bill = balance + periodCost;
    setBillTotal(bill);
    setCatvBillComputed(true);
    setForm(f => ({ ...f, amount: String(Math.max(0, bill)) }));
  }, [hasBB, catvRegularMonth, validityMonths, periodDays, selectedPlan?.price, extraTotal, billItems]); // eslint-disable-line

  // Broadband renewals default Validity (Days) and Paid Amount straight from the selected
  // plan's own validityDays/price — e.g. picking 25_LTD_60SPEED_12M_4700 (360 days, ₹4700)
  // fills both fields with those values (user rule 2026-08-08, supersedes the 2026-07-11
  // "broadband amount stays fully manual" note above — that only meant "no CATV-style
  // balance/rate math", not "ignore the plan entirely"). Unlike CATV's rate × months
  // scaling, changing Validity afterward does NOT re-derive the amount — a broadband plan
  // is sold as a fixed package for its own term, not a per-day rate. Still fully editable
  // by hand after the fill, same as everywhere else in this form.
  useEffect(() => {
    if (!hasBB) return;
    if (billItems) return;
    if (!selectedPlan?.validityDays) return;
    setPeriodDays(selectedPlan.validityDays);
    setForm(f => ({ ...f, amount: String((selectedPlan.price || 0) + extraTotal) }));
  }, [hasBB, selectedPlan?._id, selectedPlan?.validityDays, selectedPlan?.price, extraTotal, billItems]); // eslint-disable-line

  // Broadband-only customers have no Collect Payment tab — Adjust Amount lives here instead
  // so they still have a way to manually correct a balance without a renewal/payment.
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ type: 'reduce', amount: '', reason: '' });
  const [adjustSaving, setAdjustSaving] = useState(false);

  const handleAdjust = async () => {
    const amt = Number(adjustForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!adjustForm.reason.trim()) { toast.error('A reason is required'); return; }
    setAdjustSaving(true);
    try {
      const { data } = await customerAPI.adjustBalance(customer._id, { amount: amt, type: adjustForm.type, reason: adjustForm.reason.trim() });
      toast.success(data.message || 'Balance adjusted');
      setAdjustOpen(false);
      setAdjustForm({ type: 'reduce', amount: '', reason: '' });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Adjustment failed');
    } finally {
      setAdjustSaving(false);
    }
  };

  // Only actually re-assigns the plan if the primary plan or the additional-plan list
  // changed — a normal renewal at the same subscription never touches this.
  const ensurePlanAssigned = async () => {
    if (!subscriptionChanged) return;
    if (hasBB) {
      await broadbandAPI.assignPlan({
        customerId: customer._id, planId: selectedPlanId,
        username: customer.broadband?.username, staticIp: customer.broadband?.staticIp,
        additionalPlanIds: extraIds.filter(Boolean),
      });
    } else {
      await catvAPI.assignPackage({
        customerId: customer._id, packageId: selectedPlanId,
        setTopBoxSerial: customer.catv?.setTopBoxSerial,
        additionalPackageIds: extraIds.filter(Boolean),
      });
    }
  };

  const paid = parseFloat(form.amount) || 0;
  // billTotal is only meaningful once a custom bill is actually in effect (Edit Bill or a
  // Quick Fill tier pick) — the plain manual-amount path (broadband's default, per the
  // 2026-07-11 rule that broadband amounts stay manual) never sets it, so New Balance falls
  // back to the legacy clamped-at-0 preview there, matching what the server will compute.
  const usingBillTotal = !!billItems || catvBillComputed;
  const newBal = Math.max(0, balance - paid);
  // Due-left/advance preview, matching AgentCollectPaymentPage.js: positive = due left,
  // negative = advance, exactly what billTotal (sent to the server below) will produce.
  const billTotalNewBalance = billTotal - paid;

  // Targets whose override checkbox is on AND whose package was actually changed (vs. its
  // currently-assigned one) — those need a pre-submit catvAPI.assignPackage call.
  const changedCatvPackages = linkedCatvTargets.filter(({ target }) => {
    const o = catvOverrides[target._id];
    return o?.enabled && o.packageId && o.packageId !== target.catv?.package?._id;
  });

  const splitSum = (splitRows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const splitValid = !splitRows || (splitRows.length >= 2 && splitRows.every(r => r.method && Number(r.amount) > 0) && Math.round(splitSum) === Math.round(paid));

  // Every path (Generate & Collect / Generate / after Edit Bill save) goes through here —
  // `collected` is the only thing that differs, matching the agent app's Renew screen.
  const submitRenewal = async (collected, { confirmed8pm = false, confirmedLongTerm = false, gtplOrderChoice } = {}) => {
    // paid=0 is only valid when a computed CATV bill total backs it (an existing credit fully
    // covers this period) — the manual/broadband path still requires a real amount.
    if (!paid && !usingBillTotal) { toast.error('Enter a valid amount'); return; }
    if (tempTill8pm && collected) { toast.error('"Valid till 8 PM" only applies to Generate (unpaid) — use that button instead'); return; }
    // Nothing's actually changed hands on a Generate (unpaid) bill, so a mode split is
    // meaningless there — only enforced/sent when this submission collects money now.
    if (collected && splitRows && !splitValid) { toast.error(`Split amounts must add up to ₹${fmt(paid)}`); return; }
    // New customer's first 3/6/12-month payment: staff pick box swap or direct order; either way
    // the customer becomes DTD with this payment (server: paymentService gtplOrderChoice).
    const choice = gtplOrderChoice || orderChoice;
    if (collected && askOrderChoice && !choice) { setShowOrderChoice(true); return; }
    setShowOrderChoice(false);
    const after8pm = collected && (isDtdCatv || !!choice)
      && dtdStartsTomorrowFrom(periodFrom, form.collectDate ? new Date(form.collectDate) : new Date());
    if (after8pm && !confirmed8pm) { setShow8pmNotice(true); return; }
    // 6/12 months for a regular CableTV customer with the automatic box swap on — the payer is told
    // the swap runs tomorrow and they become DTD when the paid period ends (utils/longTermNotice.js).
    if (collected && !choice && catvRegularMonth && longTermRegularNoticeApplies(longTermAuto, 'regular', validityMonths) && !confirmedLongTerm) {
      setShowLongTermNotice(true); return;
    }
    setShow8pmNotice(false);
    setShowLongTermNotice(false);
    setBusy(true);
    try {
      await ensurePlanAssigned();
      // Switch any linked account's own CATV package BEFORE creating the payment, so the
      // combo bill(s) auto-generated below (generateLinkedComboCatvBills re-reads each linked
      // customer fresh from the DB) already see the new package.
      for (const { target } of changedCatvPackages) {
        const o = catvOverrides[target._id];
        await catvAPI.assignPackage({
          customerId: target._id,
          packageId: o.packageId,
          setTopBoxSerial: target.catv?.setTopBoxSerial,
          additionalPackageIds: (target.catv?.additionalPackages || []).filter(a => a.package).map(a => a.package._id),
        });
      }
      const catvOverridePayload = Object.fromEntries(
        linkedCatvTargets
          .map(({ target }) => [target._id, catvOverrides[target._id]])
          .filter(([, o]) => o?.enabled && o.periodFrom)
          .map(([id, o]) => [id, { periodFrom: o.periodFrom }])
      );
      const useSplit = collected && !!splitRows;
      const { data } = await paymentAPI.create({
        customer: customer._id,
        paymentFor,
        amount: paid,
        notes: form.notes,
        periodFrom,
        periodTo,
        collected,
        sendMessage,
        ...(useSplit
          ? { paymentMethod: 'split', splitPayments: splitRows.map(r => ({ method: r.method, amount: Number(r.amount) || 0, transactionId: r.transactionId || undefined })) }
          : { paymentMethod: form.paymentMethod, transactionId: form.transactionId }),
        ...(form.collectDate ? { paymentDate: form.collectDate } : {}),
        // "Valid till 8 PM today" — server auto-reverses this bill (paymentService.
        // deletePaymentCascade, via cronJobs.js) if it's still unpaid past this timestamp.
        ...(tempTill8pm ? { reverseAt: (() => { const d = new Date(); d.setHours(20, 0, 0, 0); return d.toISOString(); })() } : {}),
        // Bill line items + expected total — sent whenever a custom bill is actually in effect
        // (Edit Bill or a Quick Fill tier pick); the plain manual-amount path (always true for
        // catv via the auto-price effect above, opt-in for broadband) omits them so the legacy
        // clamped-at-0 balance math applies unchanged.
        ...(usingBillTotal ? { items: billItems || undefined, billTotal } : {}),
        ...(Object.keys(catvOverridePayload).length ? { catvOverrides: catvOverridePayload } : {}),
        ...(collected && choice ? { gtplOrderChoice: choice } : {}),
      });
      toast.success(data.message || (collected ? `Payment recorded! Receipt: ${data.data.receiptNumber}` : 'Bill generated (unpaid)'));
      if (tempTill8pm) toast('Valid till 8 PM today — auto-reverses if still unpaid by then', { icon: '⏰', duration: 6000 });
      if (after8pm) toast('After 8 PM — counted from tomorrow; GTPL order goes tomorrow after 9:30 AM', { icon: '🌙', duration: 7000 });
      if (data.imbsSync?.attempted) toast('Syncing to Netzur…', { icon: '🔄' });
      (data.linkedComboBills || []).forEach(lb => {
        const from = lb.periodFrom ? new Date(lb.periodFrom).toLocaleDateString('en-GB') : '';
        const to = lb.periodTo ? new Date(lb.periodTo).toLocaleDateString('en-GB') : '';
        toast.success(
          `TV${lb.tvNumber} bill auto-generated for ${lb.customerName}: ₹${lb.amount}, valid ${from}–${to} (${lb.days} days)`,
          { duration: 6000 }
        );
      });
      onSuccess();
      setForm(f => ({ ...f, amount: '', transactionId: '', notes: '', collectDate: format(new Date(), "yyyy-MM-dd'T'HH:mm") }));
      setBillItems(null);
      setTempTill8pm(false);
      setSplitRows(null);
      setOrderChoice(null);
      if (choice === 'swap') toast('Box swap scheduled — follow it on the GTPL Saathi tab', { icon: '🔁', duration: 7000 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Renewal failed');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box', background: 'var(--cb-ffffff)' };
  const labelStyle = { fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' };

  return (
    <div>
      {isBoth && (
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Service</label>
          <select style={{ ...inputStyle, maxWidth: 240 }} value={service} onChange={e => setService(e.target.value)}>
            <option value="broadband">Broadband</option>
            <option value="catv">CableTV</option>
          </select>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--border)' }}>
          <div style={{ padding: '10px 14px', background: 'var(--bg-card)' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>Current balance</div>
            <span style={{ fontWeight: 800, fontSize: '1.15rem', color: balance > 0 ? 'var(--c-dc2626)' : 'var(--text)' }}>₹{fmt(balance)}</span>
          </div>
          <div style={{ padding: '10px 14px', background: 'var(--bg-card)' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>Last bill date</div>
            <span style={{ fontWeight: 700, fontSize: '.98rem' }}>{fmtDate(expiryDate)}</span>
          </div>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem', alignSelf: 'center' }}
          onClick={() => { setAdjustForm({ type: 'reduce', amount: '', reason: '' }); setAdjustOpen(true); }}>
          <Wallet size={12} /> Adjust Amount
        </button>
      </div>

      {adjustOpen && (
        <div className="modal-overlay" onClick={() => !adjustSaving && setAdjustOpen(false)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adjust Amount</h3>
              <button className="modal-close" onClick={() => setAdjustOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
                Current due: <strong>₹{fmt(balance)}</strong> — adjustments are recorded in System Logs.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, marginBottom: 10 }}>
                <select className="form-control" value={adjustForm.type} onChange={e => setAdjustForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="reduce">Reduce (−)</option>
                  <option value="add">Add (+)</option>
                </select>
                <input className="form-control" type="number" min="0" placeholder="Amount ₹"
                  value={adjustForm.amount} onChange={e => setAdjustForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <input className="form-control" placeholder="Reason (required)"
                value={adjustForm.reason} onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAdjustOpen(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={adjustSaving} onClick={handleAdjust}>
                {adjustSaving ? 'Saving…' : 'Save Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {show8pmNotice && (
        <DtdAfter8pmNotice
          audience="staff"
          at={form.collectDate ? new Date(form.collectDate) : new Date()}
          onConfirm={() => submitRenewal(true, { confirmed8pm: true })}
          onCancel={() => { setShow8pmNotice(false); setOrderChoice(null); }}
        />
      )}
      {showOrderChoice && (
        <div className="modal-overlay" onClick={() => setShowOrderChoice(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New customer — {choiceMonths}-month GTPL order</h3>
              <button className="modal-close" onClick={() => setShowOrderChoice(false)}><X size={16} /></button>
            </div>
            <div className="modal-body" style={{ fontSize: '.87rem', lineHeight: 1.55 }}>
              <p style={{ margin: '0 0 8px' }}><b>Box swap:</b> moves this box onto an account inactive 93+ days and orders {choiceMonths} months + AASTHA there.</p>
              <p style={{ margin: '0 0 8px' }}><b>Direct order:</b> orders {choiceMonths} months + AASTHA on this customer's own GTPL account.</p>
              <p style={{ margin: '0 0 8px' }}><b>No action in GTPL:</b> only records the payment — nothing is ordered or swapped on GTPL.</p>
              <p style={{ margin: 0, color: 'var(--c-6b7280)' }}>Either way the customer becomes <b>DTD</b> from this payment.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setOrderChoice('none'); submitRenewal(true, { gtplOrderChoice: 'none' }); }}>No action in GTPL</button>
              <button className="btn btn-secondary" onClick={() => { setOrderChoice('direct'); submitRenewal(true, { gtplOrderChoice: 'direct' }); }}>Direct order</button>
              <button className="btn btn-primary" onClick={() => { setOrderChoice('swap'); submitRenewal(true, { gtplOrderChoice: 'swap' }); }}>Box swap</button>
            </div>
          </div>
        </div>
      )}
      {showLongTermNotice && (
        <LongTermRegularNotice
          audience="staff"
          months={validityMonths}
          at={form.collectDate ? new Date(form.collectDate) : new Date()}
          onConfirm={() => submitRenewal(true, { confirmedLongTerm: true })}
          onCancel={() => setShowLongTermNotice(false)}
        />
      )}
      <form onSubmit={e => { e.preventDefault(); submitRenewal(true); }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px 16px', alignItems: 'start' }}>
        <div>
          <label style={labelStyle}>Plan</label>
          <SearchableSelect
            style={{ maxWidth: '100%' }}
            value={selectedPlanId}
            onChange={setSelectedPlanId}
            placeholder="Search plan…"
            options={[
              ...planOptions.map(o => ({ value: o._id, label: `${o.displayName || o.name} — ₹${o.price}` })),
              ...(!planOptions.some(o => o._id === selectedPlanId) && plan
                ? [{ value: plan._id, label: `${plan.displayName || plan.name} — ₹${plan.price}` }]
                : []),
            ]}
          />
          {planChanged && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-d97706)', marginTop: 4 }}>Recording this payment switches the plan too</div>
          )}
        </div>

        <div>
          <label style={labelStyle}>{hasBB ? 'Additional Plans' : 'Additional Packages'}</label>
          {extraIds.map((id, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <SearchableSelect
                style={{ flex: 1 }}
                value={id}
                onChange={v => setExtraIds(ids => ids.map((x, xi) => xi === i ? v : x))}
                placeholder="Search plan…"
                options={planOptions
                  .filter(o => o._id === id || (o._id !== selectedPlanId && !extraIds.includes(o._id)))
                  .map(o => ({ value: o._id, label: `${o.displayName || o.name} — ₹${o.price}` }))}
              />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExtraIds(ids => ids.filter((_, xi) => xi !== i))}>
                <X size={14} />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExtraIds(ids => [...ids, ''])}>
            + Add another {hasBB ? 'plan' : 'package'}
          </button>
          {extraChanged && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-d97706)', marginTop: 4 }}>Recording this payment updates the add-ons too</div>
          )}
        </div>

        {selectedPlan?.pricingTiers?.length > 0 && (
          <div>
            <label style={labelStyle}>Quick Fill</label>
            <select style={inputStyle} defaultValue="" disabled={tempTill8pm}
              onChange={e => {
                const tier = selectedPlan.pricingTiers.find(t => t.label === e.target.value);
                if (!tier) return;
                autoFillSkip.current = true; // tier price wins over the straight monthly × months auto-fill
                if (catvRegularMonth) setValidityMonths(tier.months);
                else setPeriodDays(tier.months * 30);
                setBillItems(null); // tier picks a standard bill — drop any custom Edit Bill in effect
                const bill = balance + tier.price + extraTotal; // existing credit carries forward, uncapped
                setBillTotal(bill);
                setCatvBillComputed(true);
                setForm(f => ({ ...f, amount: String(Math.max(0, bill)) }));
              }}>
              <option value="">Custom (set below)</option>
              {selectedPlan.pricingTiers.map(t => (
                <option key={t.label} value={t.label}>{t.label} — ₹{t.price}</option>
              ))}
            </select>
          </div>
        )}
        {extraTotal > 0 && (
          <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)' }}>Add-ons total: ₹{fmt(extraTotal)}</div>
        )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 12 }}>
          <div>
            <label style={labelStyle}>Start Date</label>
            <input type="date" style={inputStyle} value={periodFrom} disabled={tempTill8pm}
              onChange={e => setPeriodFrom(e.target.value)} />
            {!tempTill8pm && periodFrom !== format(new Date(), 'yyyy-MM-dd') && (
              <button type="button" onClick={() => setPeriodFrom(format(new Date(), 'yyyy-MM-dd'))}
                style={{ background: 'none', border: 'none', color: 'var(--c-2563eb)', fontSize: '.72rem', padding: 0, marginTop: 4, cursor: 'pointer', textDecoration: 'underline' }}>
                Generate from Today
              </button>
            )}
          </div>
          <div>
            {catvRegularMonth ? (
              <>
                <label style={labelStyle}>Validity (Months)</label>
                <input type="number" min="1" style={inputStyle} value={validityMonths} disabled={tempTill8pm}
                  onChange={e => setValidityMonths(Math.max(1, Number(e.target.value) || 1))} />
              </>
            ) : (
              <>
                <label style={labelStyle}>Validity (Days)</label>
                {hasBB ? (
                  // Broadband: free number with the usual lengths suggested — plans run past 360 (420, 450…), owner 2026-10-05.
                  <>
                    <input type="number" min="1" list="bb-validity-days" style={inputStyle} value={periodDays}
                      onChange={e => setPeriodDays(Math.max(1, Number(e.target.value) || 1))} />
                    <datalist id="bb-validity-days">
                      {DTD_DAY_OPTIONS.map(d => <option key={d} value={d} />)}
                    </datalist>
                  </>
                ) : customer.billing?.durationType === 'dtd' ? (
                  // DTD picks from a fixed list (owner, 2026-10-04). A day count set elsewhere (a
                  // broadband plan's own validityDays) stays selectable so the form never goes blank.
                  <select style={inputStyle} value={periodDays} onChange={e => setPeriodDays(Number(e.target.value))}>
                    {(dtdDayOptions.includes(periodDays) ? dtdDayOptions : [...dtdDayOptions, periodDays].sort((a, b) => a - b))
                      .map(d => <option key={d} value={d}>{d} days</option>)}
                  </select>
                ) : (
                  <input type="number" min="1" style={inputStyle} value={periodDays}
                    onChange={e => setPeriodDays(Math.max(1, Number(e.target.value) || 1))} />
                )}
              </>
            )}
          </div>
          <div>
            <label style={labelStyle}>End Date</label>
            <input type="date" style={{ ...inputStyle, background: 'var(--cb-f3f4f6)' }} value={periodTo} disabled />
          </div>
        </div>

        {isDtdCatv && (
          <div style={{ marginTop: 12, padding: 10, border: '1px dashed var(--cb-d1d5db)', borderRadius: 6, background: tempTill8pm ? 'var(--cb-fffbeb)' : 'var(--cb-f9fafb)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={tempTill8pm} onChange={e => setTempTill8pm(e.target.checked)} />
              <Clock size={14} /> Make link valid till 8 PM today
            </label>
            <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', marginTop: 6 }}>
              {tempTill8pm
                ? 'Start Date is locked to today (Validity keeps whatever you\'ve set). Use "Generate" (not "Generate & Collect") — if this bill is still unpaid at 8 PM, the amount and the bill are automatically reversed and the connection reverts to its earlier expiry.'
                : 'Grants a same-day extension for evening collection — auto-reverses at 8 PM if the customer still hasn\'t paid by then.'}
            </div>
          </div>
        )}

        {showLinkedCatvOption && linkedCatvTargets.map(({ tvNumber, target }) => {
          const o = catvOverrides[target._id] || {};
          return (
            <div key={target._id} style={{ marginTop: 12, padding: 10, border: '1px dashed var(--cb-d1d5db)', borderRadius: 6, background: 'var(--cb-f9fafb)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!o.enabled}
                  onChange={e => setCatvOverride(target._id, { enabled: e.target.checked, periodFrom: e.target.checked ? (o.periodFrom || periodFrom) : o.periodFrom })}
                />
                Different start date for linked TV{tvNumber} bill ({target.name})
              </label>
              {o.enabled && (
                <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ maxWidth: 200 }}>
                    <label style={labelStyle}>CableTV Start Date</label>
                    <input type="date" style={inputStyle} value={o.periodFrom || ''} onChange={e => setCatvOverride(target._id, { periodFrom: e.target.value })} />
                    <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', marginTop: 4 }}>
                      Same {periodDays}-day validity as the broadband period, just anchored to this date instead of {periodFrom}.
                    </div>
                  </div>
                  <div style={{ maxWidth: 240 }}>
                    <label style={labelStyle}>CableTV Package</label>
                    <SearchableSelect
                      value={o.packageId || ''}
                      onChange={packageId => setCatvOverride(target._id, { packageId })}
                      placeholder="Search package…"
                      options={[
                        ...(!linkedCatvPackageOptions.some(opt => opt._id === o.packageId) && target.catv?.package
                          ? [{ value: target.catv.package._id, label: target.catv.package.displayName || target.catv.package.name }]
                          : []),
                        ...linkedCatvPackageOptions.map(opt => ({ value: opt._id, label: `${opt.displayName || opt.name} — ₹${opt.price}` })),
                      ]}
                    />
                    <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', marginTop: 4 }}>
                      Leave as-is to keep the linked account's current package ({target.catv?.package?.displayName || target.catv?.package?.name}).
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px 16px', marginTop: 12, alignItems: 'start' }}>
          <div>
            <label style={labelStyle}>Paid Amount</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-6b7280)', fontSize: '.88rem' }}>₹</span>
              <input type="number" min="0" style={{ ...inputStyle, paddingLeft: 24, background: billItems ? 'var(--cb-f3f4f6)' : 'var(--cb-ffffff)' }}
                value={form.amount} disabled={!!billItems}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
            </div>
            {billItems && (
              <div style={{ fontSize: '.75rem', color: 'var(--c-b45309)', background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
                Custom bill: {billItems.length} item{billItems.length !== 1 ? 's' : ''}.{' '}
                <button type="button" onClick={() => setEditBillOpen(true)} style={{ background: 'none', border: 'none', color: 'var(--c-b45309)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Edit</button>
                {' · '}
                <button type="button" onClick={() => {
                  setBillItems(null);
                  const monthly = (selectedPlan?.price || 0) + extraTotal;
                  const periodCost = catvRegularMonth ? monthly * validityMonths : Math.round(monthly * periodDays / 30);
                  const bill = balance + periodCost; // existing credit carries forward, uncapped
                  setBillTotal(bill);
                  setCatvBillComputed(true);
                  setForm(f => ({ ...f, amount: String(Math.max(0, bill)) }));
                }} style={{ background: 'none', border: 'none', color: 'var(--c-b45309)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Clear</button>
              </div>
            )}
          </div>
          {!splitRows && (
            <div>
              <label style={labelStyle}>Mode</label>
              <PaymentMethodSelect style={inputStyle} value={form.paymentMethod} onChange={v => setForm(f => ({ ...f, paymentMethod: v }))} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Collect Payment Date</label>
            <input type="datetime-local" style={inputStyle} value={form.collectDate}
              onChange={e => setForm(f => ({ ...f, collectDate: e.target.value }))} />
          </div>
          {!splitRows && (form.paymentMethod !== 'cash' && form.paymentMethod !== 'cheque') && (
            <div>
              <label style={labelStyle}>Transaction ID <span style={{ color: 'var(--c-9ca3af)', fontWeight: 400 }}>(optional)</span></label>
              <input style={inputStyle} value={form.transactionId}
                onChange={e => setForm(f => ({ ...f, transactionId: e.target.value }))}
                placeholder="Transaction reference — leave blank if unknown" />
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <SplitPaymentFields total={paid} value={splitRows} onChange={setSplitRows} />
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Notes</label>
          <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 44 }} rows={1}
            value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Optional comment..." />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Total payment</div>
              <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>₹{fmt(paid)}</div>
            </div>
            <div>
              <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>New balance</div>
              {usingBillTotal ? (
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: billTotalNewBalance > 0 ? 'var(--c-dc2626)' : 'var(--c-16a34a)' }}>
                  {billTotalNewBalance > 0 ? `Due left ₹${fmt(billTotalNewBalance)}` : billTotalNewBalance < 0 ? `Advance ₹${fmt(-billTotalNewBalance)}` : '₹0 — settled'}
                </div>
              ) : (
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: newBal > 0 ? 'var(--c-dc2626)' : 'var(--text)' }}>₹{fmt(newBal)}</div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <SendMessageToggle checked={sendMessage} onChange={setSendMessage} />
              <div style={{ fontSize: '.7rem', color: 'var(--c-9ca3af)', marginTop: 2 }}>
                {sendMessage ? 'WhatsApp receipt will be sent.' : 'No message will be sent.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setEditBillOpen(true)}>
                <Edit2 size={14} /> Edit Bill
              </button>
              <button type="button" className="btn btn-secondary" disabled={busy}
                onClick={() => submitRenewal(false)}
                title={tempTill8pm ? 'Raise a bill valid till 8 PM today — auto-reversed if still unpaid by then' : 'Raise the bill without collecting payment — adds to outstanding balance, view/share/collect it later'}>
                {busy ? 'Working…' : 'Generate'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || tempTill8pm}
                style={{ background: 'var(--cb-1e293b)', borderColor: 'var(--cb-1e293b)', minWidth: 170 }}
                title={tempTill8pm ? 'Not available with "Valid till 8 PM" — use Generate instead' : undefined}>
                <RefreshCw size={14} /> {busy ? 'Working…' : 'Generate & Collect'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {editBillOpen && (
        <EditBillModal
          initialItems={billItems || [{ label: selectedPlan?.displayName || selectedPlan?.name || 'Subscription', amount: parseFloat(form.amount) || selectedPlan?.price || 0 }]}
          onClose={() => setEditBillOpen(false)}
          onSave={(items) => {
            setBillItems(items);
            const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
            setBillTotal(total);
            setForm(f => ({ ...f, amount: String(total) }));
            setEditBillOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Edit Bill Modal ──────────────────────────────────────────────────────────
// bix42-style itemized bill editor for RenewPanel (catv) — same shape/behavior as the agent
// app's EditBillModal (AgentCollectPaymentPage.js), styled with this file's modal-* classes.
function EditBillModal({ initialItems, onClose, onSave }) {
  const [items, setItems] = useState(initialItems.map(i => ({ label: i.label, amount: String(i.amount ?? '') })));
  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const setItem = (idx, k, v) => setItems(list => list.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));

  const save = () => {
    const clean = items
      .map(i => ({ label: i.label.trim(), amount: parseFloat(i.amount) || 0 }))
      .filter(i => i.label);
    if (!clean.length) { toast.error('Add at least one item'); return; }
    if (clean.reduce((s, i) => s + i.amount, 0) <= 0) { toast.error('Bill total must be more than 0'); return; }
    onSave(clean);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Bill</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
            These line items appear on the printed bill. The bill amount becomes their total.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 30px', gap: 8, alignItems: 'center' }}>
                <input className="form-control" placeholder="Item description" value={it.label} onChange={e => setItem(i, 'label', e.target.value)} />
                <input className="form-control" type="number" placeholder="₹" value={it.amount} onChange={e => setItem(i, 'amount', e.target.value)} />
                <button type="button" onClick={() => setItems(list => list.filter((_, x) => x !== i))} title="Remove item"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-dc2626)', display: 'flex', justifyContent: 'center' }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setItems(list => [...list, { label: '', amount: '' }])}>
            <Plus size={13} /> Add Item
          </button>
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700 }}>Total: ₹{fmt(total)}</div>
          <button type="button" className="btn btn-primary" onClick={save}>Save Items</button>
        </div>
      </div>
    </div>
  );
}

// ─── Link Account Panel ───────────────────────────────────────────────────────
function LinkAccountPanel({ customer, onSuccess }) {
  const linked = customer.linkedAccount;
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = React.useRef(null);

  React.useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await customerAPI.getAll({ search, limit: 8 });
        setResults((data.data || []).filter(c => c._id !== customer._id));
      } catch {}
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [search, customer._id]);

  const doLink = async (targetId) => {
    setBusy(true);
    try {
      await customerAPI.linkAccount(customer._id, targetId);
      toast.success('Accounts linked!');
      setSearch(''); setResults([]);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    } finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (!window.confirm('Remove link between these two accounts?')) return;
    setBusy(true);
    try {
      await customerAPI.unlinkAccount(customer._id);
      toast.success('Accounts unlinked');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unlink failed');
    } finally { setBusy(false); }
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--cb-f3f4f6)', fontSize: '.85rem' }}>
      <span style={{ color: 'var(--c-6b7280)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value || '—'}</span>
    </div>
  );

  return (
    <div>
      <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>Link Account</h3>

      {linked ? (
        <div>
          <div style={{ background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)', borderRadius: 8, padding: '14px 18px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <span style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--c-059669)', letterSpacing: '.06em' }}>Linked Account</span>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginTop: 2 }}>{linked.name}</div>
                <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginTop: 2 }}>
                  {linked.customerId} · {linked.mobile} · <span>{customerTypeLabel(linked.customerType)}</span>
                </div>
              </div>
              <button
                className="btn btn-danger btn-sm"
                disabled={busy}
                onClick={doUnlink}
                style={{ flexShrink: 0 }}
              >
                <Unlink size={13} /> {busy ? 'Unlinking…' : 'Unlink'}
              </button>
            </div>
            {linked.customerType === 'broadband' && linked.broadband?.plan && (
              <div style={{ fontSize: '.82rem', color: 'var(--c-374151)' }}>
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Broadband: </span>
                {linked.broadband.plan.displayName || linked.broadband.plan.name} — ₹{linked.broadband.plan.price}
                {linked.broadband.expiryDate && <span style={{ color: 'var(--c-6b7280)' }}> · Exp: {fmtDate(linked.broadband.expiryDate)}</span>}
              </div>
            )}
            {linked.customerType === 'catv' && (linked.catv?.package || linked.catv?.expiryDate) && (
              <div style={{ fontSize: '.82rem', color: 'var(--c-374151)' }}>
                <span style={{ color: 'var(--primary)', fontWeight: 600 }}>CableTV: </span>
                {linked.catv.package && <>{linked.catv.package.displayName || linked.catv.package.name} — ₹{linked.catv.package.price}</>}
                {linked.catv.expiryDate && <span style={{ color: 'var(--c-6b7280)' }}>{linked.catv.package ? ' · ' : ''}Exp: {fmtDate(linked.catv.expiryDate)}</span>}
              </div>
            )}
          </div>
          <p style={{ fontSize: '.83rem', color: 'var(--c-6b7280)' }}>
            These two accounts are treated as a combo. Open the linked account to manage their service.
          </p>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', marginBottom: 14 }}>
            Link this {customer.customerType === 'catv' ? 'CableTV' : 'Broadband'} account to a{' '}
            {customer.customerType === 'catv' ? 'Broadband' : 'CableTV'} account to treat them as a combo customer.
          </p>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input
              className="form-control"
              placeholder="Search by name, mobile, or customer ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoComplete="off"
            />
            {(searching || results.length > 0) && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: 'var(--cb-ffffff)', border: '1px solid var(--border)',
                borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.12)', marginTop: 4,
              }}>
                {searching ? (
                  <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.85rem' }}>Searching…</div>
                ) : results.length === 0 ? (
                  <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.85rem' }}>No customers found</div>
                ) : results.map(c => (
                  <div key={c._id}
                    style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--cb-ffffff)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{c.name}</div>
                        <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
                          {c.customerId} · {c.mobile} · <span>{customerTypeLabel(c.customerType)}</span>
                        </div>
                        {c.linkedAccount && (
                          <div style={{ fontSize: '.72rem', color: 'var(--c-d97706)' }}>Already linked to another account</div>
                        )}
                      </div>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={busy}
                        onClick={() => doLink(c._id)}
                        style={{ flexShrink: 0, marginLeft: 12 }}
                      >
                        <Link2 size={12} /> Link
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Linked CableTV Accounts Panel (unified) ────────────────────────────────────
// One combo customer can bundle multiple independently-billed CableTV connections under
// this broadband account. Rather than a separate "Link Account" step for the first one and
// a different "Linked CableTV" step for every one after, this single panel lists every
// linked CableTV account together (TV1, TV2…) and lets staff search-and-link/unlink from one
// place — no need to know or pick which underlying mechanism a link uses. Under the hood the
// FIRST linked account still goes through the 1:1 Customer.linkedAccount pairing (shared
// portal login + ledger-transfer eligibility — same field LinkAccountPanel manages from the
// CATV side of that pair) and every additional one goes into broadband.linkedCatvAccounts
// (independent billing, reference-only) — but both slots bill identically via the combo
// split (paymentService.generateLinkedComboCatvBills) and both show as numbered
// "Linked TV N" badges on the customer header, so the distinction is invisible here.
// linkedCatvSlots (BroadbandPlan) counts the WHOLE combo (e.g. a "2TV" plan means 2 total
// linked accounts, the primary included), not just the extras.
function LinkedCableTvPanel({ customer, onSuccess }) {
  const primary = customer.linkedAccount && ['catv', 'both'].includes(customer.linkedAccount.customerType)
    ? customer.linkedAccount : null;
  const extra = customer.broadband?.linkedCatvAccounts || [];
  const linked = [
    ...(primary ? [{ ...primary, tvNumber: 1, isPrimary: true }] : []),
    ...extra.map((c, i) => ({ ...c, tvNumber: i + 2, isPrimary: false })),
  ];
  const slots = customer.broadband?.plan?.linkedCatvSlots || 0;
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = React.useRef(null);
  // `linked` is a fresh array every render, so the effect keys on this string instead —
  // depending on the array itself re-ran the search after every fetch and it never settled.
  const linkedKey = linked.map(l => l._id).join(',');

  React.useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await customerAPI.getAll({ search, customerType: 'catv', limit: 8 });
        const linkedIds = new Set(linkedKey.split(','));
        setResults((data.data || []).filter(c => !linkedIds.has(c._id)));
      } catch {}
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [search, linkedKey]);

  // The first link always goes through the 1:1 linkAccount pairing (TV1); once that's taken,
  // every further link is a reference-only broadband.linkedCatvAccounts entry (TV2, TV3…).
  const doLink = async (targetId) => {
    setBusy(true);
    try {
      if (!primary) {
        await customerAPI.linkAccount(customer._id, targetId);
      } else {
        await customerAPI.setLinkedCatvAccounts(customer._id, [...extra.map(c => c._id), targetId]);
      }
      toast.success('Accounts linked!');
      setSearch(''); setResults([]);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    } finally { setBusy(false); }
  };

  const doUnlink = async (entry) => {
    if (entry.isPrimary && !window.confirm('Remove link between these two accounts?')) return;
    setBusy(true);
    try {
      if (entry.isPrimary) {
        await customerAPI.unlinkAccount(customer._id);
      } else {
        await customerAPI.setLinkedCatvAccounts(customer._id, extra.map(c => c._id).filter(id => id !== entry._id));
      }
      toast.success('Accounts unlinked');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unlink failed');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600 }}>Linked CableTV Accounts</h3>
      <p style={{ fontSize: '.83rem', color: 'var(--c-6b7280)', marginBottom: 16 }}>
        CableTV accounts bundled under this broadband plan (e.g. a "2TV" combo) — a broadband renewal auto-generates
        a combo share bill on each one too (own package, netted via a "coupon"-tagged bill).
        {slots > 0 && <> This plan expects <strong>{slots}</strong> linked account{slots === 1 ? '' : 's'} ({linked.length}/{slots} linked).</>}
      </p>

      {linked.length > 0 && (
        <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
          {linked.map(c => (
            <div key={c._id} style={{ background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)', borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.92rem' }}>TV{c.tvNumber} · {c.name}</div>
                <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginTop: 2 }}>
                  {c.customerId}{c.isPrimary && <span style={{ color: 'var(--c-059669)' }}> · shared login</span>}
                </div>
                {(c.catv?.package || c.catv?.expiryDate) && (
                  <div style={{ fontSize: '.82rem', color: 'var(--c-374151)', marginTop: 4 }}>
                    {c.catv.package && <>
                      <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Package: </span>
                      {c.catv.package.displayName || c.catv.package.name}
                    </>}
                    {c.catv.expiryDate && <span style={{ color: 'var(--c-6b7280)' }}>{c.catv.package ? ' · ' : ''}Exp: {fmtDate(c.catv.expiryDate)}</span>}
                  </div>
                )}
              </div>
              <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => doUnlink(c)} style={{ flexShrink: 0 }}>
                <Unlink size={13} /> Unlink
              </button>
            </div>
          ))}
        </div>
      )}

      {(!slots || linked.length < slots) && (
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <input
            className="form-control"
            placeholder="Search CableTV customers by name, mobile, or customer ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoComplete="off"
          />
          {(searching || results.length > 0) && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: 'var(--cb-ffffff)', border: '1px solid var(--border)',
              borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.12)', marginTop: 4,
            }}>
              {searching ? (
                <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.85rem' }}>Searching…</div>
              ) : results.length === 0 ? (
                <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.85rem' }}>No CableTV customers found</div>
              ) : results.map(c => (
                <div key={c._id}
                  style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--cb-ffffff)'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{c.name}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{c.customerId} · {c.mobile}</div>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={busy}
                      onClick={() => doLink(c._id)}
                      style={{ flexShrink: 0, marginLeft: 12 }}
                    >
                      <Link2 size={12} /> Link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── App Login Panel ───────────────────────────────────────────────────────────
function AppLoginPanel({ customer, onSuccess }) {
  const [login, setLogin]         = useState(undefined); // undefined = loading, null = none
  const [mobile, setMobile]       = useState('');
  const [savingMobile, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [resetting, setResetting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sharingLink, setSharingLink] = useState(false);
  // Customer-facing login link now points at www.laxmicablevision.in/portal/login
  // (app.laxmicablevision.in still works but is no longer what we hand out); fall
  // back to the current origin for local dev / other hosts so the link still works there.
  const portalLoginUrl = window.location.hostname === 'crm.laxmicablevision.in'
    ? 'https://www.laxmicablevision.in/portal/login'
    : `${window.location.origin}/portal/login`;
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(portalLoginUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select and copy the link manually');
    }
  };

  // Sent through the company WhatsApp gateway (server builds the text + link), not wa.me.
  const handleShareLink = async () => {
    if (!customer.mobile) { toast.error('Customer has no mobile number'); return; }
    setSharingLink(true);
    try {
      const { data } = await customerAPI.sendPortalLogin(customer._id);
      toast.success(data.message || 'Login link sent on WhatsApp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send on WhatsApp');
    } finally { setSharingLink(false); }
  };

  const load = React.useCallback(async () => {
    try {
      const { data } = await customerAPI.getPortalLogin(customer._id);
      setLogin(data.data);
      setMobile(data.data?.mobile || customer.mobile || '');
    } catch {
      toast.error('Failed to load app login');
    }
  }, [customer._id, customer.mobile]);

  useEffect(() => { load(); }, [load]);

  const saveMobile = async (e) => {
    e.preventDefault();
    if (!mobile.trim()) { toast.error('Enter a mobile number'); return; }
    setSaving(true);
    try {
      const { data } = await customerAPI.setPortalLoginMobile(customer._id, mobile.trim());
      toast.success(data.message || 'Saved');
      await load();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const resetPassword = async (useDefault) => {
    if (!useDefault && !newPassword.trim()) { toast.error('Enter a new password'); return; }
    setResetting(true);
    try {
      const { data } = await customerAPI.resetPortalPassword(customer._id, useDefault ? undefined : newPassword.trim());
      toast.success(data.message || 'Password updated');
      setNewPassword('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally { setResetting(false); }
  };

  if (login === undefined) {
    return <div style={{ textAlign: 'center', padding: 30 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600 }}>App / Portal Login</h3>
      <p style={{ fontSize: '.82rem', color: 'var(--c-6b7280)', margin: '0 0 12px' }}>
        Controls what this customer signs into the customer app/portal with — the mobile number below is the login ID,
        separate from the contact mobile shown on Customer Details.
      </p>

      {customer.linkedAccount && (
        <div style={{ background: 'var(--cb-eff6ff)', border: '1px solid var(--cb-bfdbfe)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '.83rem', color: 'var(--c-1e40af)' }}>
          Linked to <strong>{customer.linkedAccount.name}</strong> ({customerTypeLabel(customer.linkedAccount.customerType)}) — this same login
          also shows that account's details in the portal, so no separate login is needed for it.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
      <div>
      {login ? (
        <div style={{ background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)', borderRadius: 8, padding: '12px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <span style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--c-059669)', letterSpacing: '.06em' }}>Login Active</span>
            <div style={{ fontWeight: 700, fontSize: '.95rem', marginTop: 2 }}>{login.mobile}</div>
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)' }}>
            {login.lastLogin ? `Last signed in ${format(new Date(login.lastLogin), 'dd MMM yyyy, HH:mm')}` : 'Never signed in yet'}
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: '.85rem', color: 'var(--c-92400e)' }}>
          No app login exists for this customer yet — set a mobile number below to create one.
        </div>
      )}

      <div style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
        <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 8 }}>Customer Portal Login Link</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <code style={{ fontSize: '.82rem', background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 4, padding: '6px 10px', flex: 1, minWidth: 200, overflow: 'auto', whiteSpace: 'nowrap' }}>
            {portalLoginUrl}
          </code>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopyLink}>
            {linkCopied ? 'Copied!' : 'Copy Link'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleShareLink} disabled={sharingLink}>
            {sharingLink ? 'Sending…' : 'Share on WhatsApp'}
          </button>
        </div>
        <p className="form-hint" style={{ marginTop: 8 }}>Send this link to the customer — they'll land straight on the portal login page and sign in with the mobile/password set below.</p>
      </div>

      </div>
      <div>
      <form onSubmit={saveMobile} style={{ marginBottom: 16 }}>
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={13} /> Login Mobile Number</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-control" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="10-digit mobile number" />
          <button type="submit" className="btn btn-primary btn-sm" disabled={savingMobile} style={{ flexShrink: 0 }}>
            <Save size={12} /> {savingMobile ? 'Saving…' : login ? 'Update' : 'Create'}
          </button>
        </div>
        <p className="form-hint">Choose which mobile number this customer logs into the app with.</p>
      </form>

      <div>
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Password</label>
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <input
            type={showPass ? 'text' : 'password'} className="form-control"
            placeholder="New password (leave blank to just reset default)"
            value={newPassword} onChange={e => setNewPassword(e.target.value)}
            style={{ paddingRight: 40 }}
            disabled={!login}
          />
          <button type="button" onClick={() => setShowPass(s => !s)}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" disabled={!login || resetting || !newPassword.trim()} onClick={() => resetPassword(false)}>
            <RotateCcw size={12} /> Set Password
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!login || resetting} onClick={() => resetPassword(true)} title="Reset to last 4 digits of the login mobile number">
            Reset to Default
          </button>
        </div>
        {!login && <p className="form-hint">Create the login above first.</p>}
      </div>
      </div>
      </div>
    </div>
  );
}

// ─── Usage Stats Panel — broadband-only tab: live status/speed, this-session data used,
// connection details, and (for Netzur-linked customers) the real session history. Adjust
// Amount for broadband customers lives in the Renew tab instead (see RenewPanel above),
// so this tab was free to become the usage view.
function fmtBytes(b) {
  if (!b || b <= 0) return '0 MB';
  const mb = Number(b) / 1048576;
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

// Netzur returns acctsessiontime in whole seconds (as a string) — used for both live
// session duration and, via uptime_limit, the subscriber's FUP time cap.
function fmtDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Number(seconds);
  if (!s || s <= 0) return '0m';
  const mo = Math.floor(s / 2592000); // 30-day months — cosmetic only, matches long "all time" totals
  const d  = Math.floor((s % 2592000) / 86400);
  const h  = Math.floor((s % 86400) / 3600);
  const m  = Math.floor((s % 3600) / 60);
  if (mo > 0) return `${mo}mo ${d}d ${h}h`;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Netzur's own connection_status is the authoritative online/offline/blocked signal — it
// already folds in enabled, temporary_suspended, account_status, etc. on Netzur's side
// (confirmed live 2026-08-12: a subscriber disabled via `enabled=0` reports
// connection_status "Blocked", not "Offline", even though account_status is literally
// "Disabled" and temporary_suspended is "0" — checking those fields individually missed
// this case). Netzur has no separate block-reason field; account_status ("Disabled",
// "Suspended", etc.) is the closest thing to a reason and is shown as one.
function getImbsStatus(imbsSub) {
  if (!imbsSub) return { online: null, blocked: false, reason: null, enabled: null };
  const blocked = imbsSub.connection_status === 'Blocked';
  return {
    online: imbsSub.connection_status === 'Online',
    blocked,
    reason: blocked ? (imbsSub.account_status || null) : null,
    // Raw account-exists flag behind the Disable/Enable button — distinct from
    // `blocked` above (temporary_suspended/connection_status), see ImbsDisconnectButton.
    enabled: imbsSub.enabled === '1' || imbsSub.enabled === 1,
  };
}

function StatTile({ label, value, sub, color }) {
  return (
    <div style={{ padding: '9px 14px', background: 'var(--bg-card, var(--cb-ffffff))' }}>
      <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted, var(--c-6b7280))', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: '1rem', overflowWrap: 'anywhere', color: color || 'var(--c-111111)' }}>{value}</div>
      {sub && <div style={{ fontSize: '.7rem', color: 'var(--c-9ca3af)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function UsageStatRow({ label, stats }) {
  const td = { padding: '8px 10px', fontSize: '.82rem' };
  return (
    <tr style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
      <td style={{ ...td, fontWeight: 600, color: 'var(--c-374151)' }}>{label}</td>
      <td style={td}>{stats?.sessions ?? '—'}</td>
      <td style={td}>{fmtDuration(stats?.upTimeSeconds)}</td>
      <td style={td}>{fmtBytes(stats?.rxBytes)}</td>
      <td style={td}>{fmtBytes(stats?.txBytes)}</td>
      <td style={{ ...td, fontWeight: 600 }}>{fmtBytes((stats?.rxBytes || 0) + (stats?.txBytes || 0))}</td>
    </tr>
  );
}

// `mode` splits this panel so the Broadband page can show each part once, in its own section:
//   'usage'    - session usage, data cap, lifetime / today / week / month totals (under Live Traffic)
//   'sessions' - PPPoE session facts and recent session history (under Uptime & Connection)
//   (none)     - the full panel, as before (Usage and speed on the phone layout)
// In a split mode the live rate is passed in (`liveRate`) instead of polled again.
function BroadbandStatsPanel({ customer, mode, liveRate }) {
  const username = customer.broadband?.username;
  const linked = !!customer.imbs?.subscriberId;
  const [polledRate, setRate] = useState(null);
  const rate = mode ? (liveRate || null) : polledRate;
  const showUsage = !mode || mode === 'usage';
  const showSessions = !mode || mode === 'sessions';
  const [imbsSub, setImbsSub] = useState(null);
  const [imbsSubLoading, setImbsSubLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [recentSessions, setRecentSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [imbsSessionBytes, setImbsSessionBytes] = useState(null);

  useEffect(() => {
    if (!username || mode) return;
    let mounted = true;
    const fetch = () => {
      nasAPI.getSubscriberLive(username).then(r => { if (mounted) setRate(r.data.data); }).catch(() => {});
    };
    fetch();
    const timer = setInterval(fetch, 1_000);
    return () => { mounted = false; clearInterval(timer); };
  }, [username, mode]);

  useEffect(() => {
    if (!username || !showUsage) return;
    nasAPI.getUsageSummary(username).then(r => setUsage(r.data?.data)).catch(() => {});
  }, [username]);

  const loadRecentSessions = useCallback(() => {
    if (!username) return;
    setSessionsLoading(true);
    nasAPI.getUsageSessions(username, 10).then(r => setRecentSessions(r.data?.data || [])).catch(() => {}).finally(() => setSessionsLoading(false));
  }, [username]);

  useEffect(() => { if (showSessions) loadRecentSessions(); }, [loadRecentSessions, showSessions]);

  useEffect(() => {
    if (!linked) return;
    setImbsSubLoading(true);
    imbsAPI.getSubscriber(customer.imbs.subscriberId)
      .then(r => setImbsSub(r.data?.data?.data || null))
      .catch(() => {})
      .finally(() => setImbsSubLoading(false));
  }, [linked, customer.imbs?.subscriberId]);

  // Accounting octets straight from Netzur's own RADIUS accounting for the current session,
  // distinct from `rate` above which is our own NAS poller's independent measurement. The
  // "Data cap usage" bar below was previously computed from `rate`'s bytes, which frequently
  // disagreed with Netzur's own numbers (different counters, resets on interface bounce,
  // polling gaps) — it must use these Netzur-sourced octets instead so the percentage shown
  // here actually matches what Netzur itself reports for the same cap.
  useEffect(() => {
    if (!linked || !showUsage) return;
    imbsAPI.getSessions(customer._id)
      .then(r => {
        const sessions = r.data?.data?.session || [];
        const s = sessions[0];
        setImbsSessionBytes(s ? {
          rx: parseInt(s.acctinputoctets, 10) || 0,
          tx: parseInt(s.acctoutputoctets, 10) || 0,
        } : null);
      })
      .catch(() => {});
  }, [linked, customer._id, showUsage]);

  const online = rate?.online;
  // Netzur-sourced fallback (used when our own NAS poller can't reach this subscriber's
  // NAS, e.g. older RouterOS with no REST API) carries rx/tx as null — accounting only
  // updates every few minutes, so there's no per-second activity to distinguish.
  const imbsSourced = rate?.source === 'imbs';
  const active = online && !imbsSourced && (rate.rx > 0.01 || rate.tx > 0.01);
  const { online: imbsOnline, blocked: imbsBlocked, reason: imbsBlockReason } = getImbsStatus(imbsSub);

  // Usage tracking has no retroactive history — right after rollout, today/week/month/
  // all-time are mathematically identical (nothing happened before today yet), which
  // reads as a bug ("why do all four rows match?") rather than what it actually is.
  const usageJustStarted = usage?.allTime && usage.allTime.sessions > 0
    && usage.today?.sessions === usage.allTime.sessions
    && usage.week?.sessions === usage.allTime.sessions
    && usage.month?.sessions === usage.allTime.sessions;

  return (
    <div>
      {!mode && <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
        <Activity size={16} color="var(--primary)" /> Usage Stats
      </h3>}

      {!username && <p style={{ fontSize: '.85rem', color: 'var(--c-9ca3af)' }}>No PPPoE username on file for this customer.</p>}

      {/* Netzur package/billing snapshot — separate from the below, which is all our own tracking */}
      {linked && !mode && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: '.88rem', marginBottom: 10 }}>Netzur Package Info</div>
          {imbsSubLoading && !imbsSub && <p style={{ fontSize: '.82rem', color: 'var(--c-9ca3af)' }}>Loading…</p>}
          {imbsSub && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1, background: 'var(--cb-f3f4f6)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
              <StatTile
                label="Status"
                value={<>
                  {imbsBlocked ? 'Blocked' : (imbsSub.connection_status || '—')}
                  {imbsBlocked && imbsBlockReason && (
                    <span
                      title={imbsBlockReason}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 14, height: 14, borderRadius: '50%', marginLeft: 6, verticalAlign: 'middle',
                        background: 'var(--cb-fca5a5)', color: 'var(--c-ffffff)', cursor: 'help', fontSize: '.62rem', fontWeight: 800,
                      }}
                    >i</span>
                  )}
                </>}
                color={imbsBlocked ? 'var(--c-dc2626)' : imbsOnline ? 'var(--c-16a34a)' : 'var(--c-94a3b8)'}
              />
              <StatTile label="Expiry Date" value={imbsSub.expiry_date ? fmtDate(imbsSub.expiry_date) : '—'} />
              <StatTile label="Data Cap (FUP)" value={imbsSub.data_limit ? fmtBytes(imbsSub.data_limit) : 'Unlimited'} />
              <StatTile label="Last Logoff" value={imbsSub.last_logoff ? fmtDate(imbsSub.last_logoff) : (imbsOnline ? 'Currently online' : '—')} />
            </div>
          )}
        </div>
      )}

      {/* Online Session — live throughput from our own NAS poller (Download/Upload Mbps,
          not available from Netzur); cumulative byte totals below come from Netzur's own RADIUS
          accounting instead, since that's what Netzur's data cap tracking is actually based
          on and what an admin sees on Netzur's own dashboard. */}
      {username && (
        <div style={{ marginBottom: 20 }}>
          {!mode && <div style={{ fontWeight: 600, fontSize: '.88rem', marginBottom: 10 }}>Online Session</div>}
          {!mode && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1, background: 'var(--cb-f3f4f6)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
            <StatTile label="Status" value={online ? (imbsSourced ? 'Online' : (active ? 'Active' : 'Idle')) : (rate ? 'Offline' : '…')}
              color={online ? (imbsSourced ? 'var(--c-16a34a)' : (active ? 'var(--c-16a34a)' : 'var(--c-92400e)')) : 'var(--c-94a3b8)'} />
            <StatTile label="Download" value={rate?.online && !imbsSourced ? `${rate.rx.toFixed(rate.rx >= 10 ? 0 : 2)} Mbps` : '—'} color="var(--primary)" />
            <StatTile label="Upload" value={rate?.online && !imbsSourced ? `${rate.tx.toFixed(rate.tx >= 10 ? 0 : 2)} Mbps` : '—'} color="var(--c-ea580c)" />
            {/* rate.address comes from the same nasAPI.getSubscriberLive poll above — either
                our own RouterOS /ppp/active poller or, when that NAS can't be polled directly,
                the Netzur-accounting fallback (both set .address to the session's Framed-IP) —
                so this works regardless of Netzur linkage, unlike the Netzur-only tile below. */}
            <StatTile label="Framed IPv4" value={rate?.address || '—'} />
            <StatTile label="Netzur Session Usage" value={imbsSessionBytes ? fmtBytes(imbsSessionBytes.rx + imbsSessionBytes.tx) : '—'}
              sub={imbsSessionBytes ? `↓${fmtBytes(imbsSessionBytes.rx)} · ↑${fmtBytes(imbsSessionBytes.tx)}` : null} />
          </div>
          )}
          {mode === 'usage' && (imbsSessionBytes || imbsSub?.data_limit > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              {imbsSessionBytes && rate?.source !== 'imbs' && <StatTile label="Session usage (Netzur)" value={fmtBytes(imbsSessionBytes.rx + imbsSessionBytes.tx)} sub={`↓${fmtBytes(imbsSessionBytes.rx)} · ↑${fmtBytes(imbsSessionBytes.tx)}`} />}
              {imbsSub?.data_limit != null && <StatTile label="Data cap (FUP)" value={imbsSub.data_limit ? fmtBytes(imbsSub.data_limit) : 'Unlimited'} />}
            </div>
          )}
          {imbsSessionBytes && imbsSub?.data_limit > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', color: 'var(--c-6b7280)', marginBottom: 4 }}>
                <span>Data cap usage (per Netzur)</span>
                <span>{fmtBytes(imbsSessionBytes.rx + imbsSessionBytes.tx)} / {fmtBytes(imbsSub.data_limit)}</span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--cb-e5e7eb)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  width: `${Math.min(100, (imbsSessionBytes.rx + imbsSessionBytes.tx) / imbsSub.data_limit * 100)}%`,
                  background: 'var(--primary)',
                }} />
              </div>
            </div>
          )}

          {showSessions && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px 16px', padding: '10px 4px', fontSize: '.82rem' }}>
              {[
                ['PPPoE username', username],
                ...(mode === 'sessions' ? [] : [['Framed IPv4', rate?.online ? rate.address : null]]),
                ['NAS', rate?.nasName],
                ['Static IP', customer.broadband?.staticIp],
                ['Session uptime', rate?.online ? (rate.uptime || (rate.connectedSince ? `since ${fmtDate(rate.connectedSince)}` : null)) : null],
                ['Last logoff', imbsSub?.last_logoff ? fmtDate(imbsSub.last_logoff) : null],
                ...(!mode ? [['MAC', rate?.online ? rate.mac : null]] : []),
              ].filter(([, v]) => v).map(([l, v]) => (
                <div key={l} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 2 }}>{l}</div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 600, overflowWrap: 'anywhere' }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {imbsSourced && !mode && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)', padding: '2px 4px 10px' }}>
              Live speed isn't available for this NAS — status updates every few minutes via Netzur.
            </div>
          )}
        </div>
      )}

      {/* Info Till Now — lifetime totals from our own session log (tracked from when this
          feature was deployed — no retroactive history, since Netzur's own lifetime totals
          aren't exposed by its partner API) */}
      {showUsage && username && usage?.allTime && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: '.88rem', marginBottom: 2 }}>Info Till Now</div>
          <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)', marginBottom: 10 }}>
            Tracked by our own system, not Netzur — only counts usage from when this feature went live, so totals will look low until enough time has passed.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1, background: 'var(--cb-f3f4f6)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
            <StatTile label="Sessions" value={usage.allTime.sessions} />
            <StatTile label="Up Time" value={fmtDuration(usage.allTime.upTimeSeconds)} />
            <StatTile label="Download" value={fmtBytes(usage.allTime.rxBytes)} color="var(--primary)" />
            <StatTile label="Upload" value={fmtBytes(usage.allTime.txBytes)} color="var(--c-ea580c)" />
            <StatTile label="Total" value={fmtBytes(usage.allTime.rxBytes + usage.allTime.txBytes)} />
          </div>
        </div>
      )}

      {/* Usage Statistics — today/week/month breakdown, same source as Info Till Now */}
      {showUsage && username && usage && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: '.88rem', marginBottom: 2 }}>Usage Statistics</div>
          {usageJustStarted && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)', marginBottom: 8 }}>
              Today / This Week / This Month match right now because usage tracking only started today — they'll diverge as more days pass.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                  {['', 'Sessions', 'Up Time', 'Download', 'Upload', 'Total'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', fontSize: '.73rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <UsageStatRow label="Today" stats={usage.today} />
                <UsageStatRow label="This Week" stats={usage.week} />
                <UsageStatRow label="This Month" stats={usage.month} />
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Sessions — recent session history, same source */}
      {showSessions && username && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: '.88rem' }}>{mode ? 'Recent sessions' : 'Quick Sessions'}</div>
            <button className="btn btn-secondary btn-sm" onClick={loadRecentSessions} disabled={sessionsLoading}>
              <Wifi size={12} /> {sessionsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {(!recentSessions || !recentSessions.length) && !sessionsLoading && (
            <p style={{ fontSize: '.82rem', color: 'var(--c-9ca3af)' }}>No session history recorded yet.</p>
          )}
          {recentSessions && recentSessions.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                    {['Start Time', 'Stop Time', 'Up Time', 'Download', 'Upload', 'Total', 'IP', 'MAC'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', fontSize: '.73rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentSessions.map(s => {
                    const durationS = ((s.endTime ? new Date(s.endTime) : new Date()) - new Date(s.startTime)) / 1000;
                    return (
                      <tr key={s._id} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                        <td style={{ padding: '7px 10px', color: 'var(--c-6b7280)' }}>{format(new Date(s.startTime), 'dd-MMM HH:mm')}</td>
                        <td style={{ padding: '7px 10px', color: 'var(--c-6b7280)' }}>{s.endTime ? format(new Date(s.endTime), 'dd-MMM HH:mm') : <span style={{ color: 'var(--c-16a34a)', fontWeight: 600 }}>Ongoing</span>}</td>
                        <td style={{ padding: '7px 10px', color: 'var(--c-6b7280)' }}>{fmtDuration(durationS)}</td>
                        <td style={{ padding: '7px 10px' }}>{fmtBytes(s.rxBytes)}</td>
                        <td style={{ padding: '7px 10px' }}>{fmtBytes(s.txBytes)}</td>
                        <td style={{ padding: '7px 10px', fontWeight: 600 }}>{fmtBytes((s.rxBytes || 0) + (s.txBytes || 0))}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{s.ip || '—'}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: '.73rem' }}>{s.mac || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Standalone CATV package change/upgrade — immediately reassigns the package (and
// optionally the STB serial), independent of Renew (no expiry/amount touched). Reuses
// the same `catvAPI.assignPackage` endpoint RenewPanel's `ensurePlanAssigned` calls
// when the admin switches plans mid-renewal.
function SubscriptionPanel({ customer, onSuccess }) {
  const pkg = customer.catv?.package;
  const [packages, setPackages] = useState([]);
  const [packageId, setPackageId] = useState(pkg?._id || '');
  const [stbSerial, setStbSerial] = useState(customer.catv?.setTopBoxSerial || '');
  const [extraIds, setExtraIds] = useState(
    (customer.catv?.additionalPackages || []).filter(a => a.package).map(a => a.package._id)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    catvAPI.getPackages({ activeOnly: 'true', limit: 200 }).then(r => setPackages(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
  }, []);

  const initialExtraIds = (customer.catv?.additionalPackages || []).filter(a => a.package).map(a => a.package._id);
  const changed = packageId !== (pkg?._id || '')
    || stbSerial !== (customer.catv?.setTopBoxSerial || '')
    || JSON.stringify([...extraIds].sort()) !== JSON.stringify([...initialExtraIds].sort());

  const handleChange = async (e) => {
    e.preventDefault();
    if (!packageId) { toast.error('Select a package'); return; }
    setSaving(true);
    try {
      await catvAPI.assignPackage({ customerId: customer._id, packageId, setTopBoxSerial: stbSerial, additionalPackageIds: extraIds.filter(Boolean) });
      toast.success('Subscription updated');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box', background: 'var(--cb-ffffff)' };
  const labelStyle = { fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' };
  const extraTotal = extraIds.reduce((sum, id) => sum + (packages.find(p => p._id === id)?.price || 0), 0);

  return (
    <div>
      <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 600 }}>Subscription</h3>
      <SummaryStrip items={[
        { label: 'Current Package', value: <>{pkg?.displayName || pkg?.name || '—'}{initialExtraIds.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> +{initialExtraIds.length} more</span>}</> },
        { label: 'Price', value: pkg?.price ? `₹${fmt(pkg.price + extraTotal)}` : '—' },
      ]} />

      <form onSubmit={handleChange}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px 16px', alignItems: 'start' }}>
        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Package</label>
          <SearchableSelect
            value={packageId}
            onChange={setPackageId}
            placeholder="Search package…"
            options={[
              ...packages.map(o => ({ value: o._id, label: `${o.displayName || o.name} — ₹${o.price}` })),
              ...(!packages.some(o => o._id === pkg?._id) && pkg
                ? [{ value: pkg._id, label: `${pkg.displayName || pkg.name} — ₹${pkg.price}` }]
                : []),
            ]}
          />
        </div>

        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Additional Packages</label>
          {extraIds.map((id, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <SearchableSelect
                style={{ flex: 1 }}
                value={id}
                onChange={v => setExtraIds(ids => ids.map((x, xi) => xi === i ? v : x))}
                placeholder="Search package…"
                options={packages
                  .filter(o => o._id === id || (o._id !== packageId && !extraIds.includes(o._id)))
                  .map(o => ({ value: o._id, label: `${o.displayName || o.name} — ₹${o.price}` }))}
              />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExtraIds(ids => ids.filter((_, xi) => xi !== i))}>
                <X size={14} />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExtraIds(ids => [...ids, ''])}>
            + Add another package
          </button>
        </div>

        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Set-Top Box Serial</label>
          <input style={inputStyle} value={stbSerial} onChange={e => setStbSerial(e.target.value.toUpperCase())} placeholder="Optional" />
        </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !changed}>
          {saving ? 'Saving…' : 'Change Package'}
        </button>
        {changed && <div style={{ fontSize: '.72rem', color: 'var(--c-d97706)', marginTop: 8 }}>This takes effect immediately — it doesn't change the current expiry or balance.</div>}
        </div>
      </form>
    </div>
  );
}

// Standalone broadband plan change/upgrade — immediately reassigns the plan (and lets the
// admin correct username/static IP alongside it), independent of Renew (no expiry/amount
// touched). Reuses the same `broadbandAPI.assignPlan` endpoint RenewPanel's
// `ensurePlanAssigned` calls when the admin switches plans mid-renewal.
function BroadbandSubscriptionPanel({ customer, onSuccess }) {
  const plan = customer.broadband?.plan;
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(plan?._id || '');
  const [username, setUsername] = useState(customer.broadband?.username || '');
  const [staticIp, setStaticIp] = useState(customer.broadband?.staticIp || '');
  const [extraIds, setExtraIds] = useState(
    (customer.broadband?.additionalPlans || []).filter(a => a.plan).map(a => a.plan._id)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // activeOnly:false so a currently-inactive plan (either the customer's existing one,
    // or one the admin still wants to hand-assign) stays selectable here, not just visible
    // via the fallback "not in list" patch below.
    broadbandAPI.getPlans({ activeOnly: 'false', limit: 200 }).then(r => setPlans(r.data?.data?.docs || r.data?.data || [])).catch(() => {});
  }, []);

  const initialExtraIds = (customer.broadband?.additionalPlans || []).filter(a => a.plan).map(a => a.plan._id);
  const changed = planId !== (plan?._id || '')
    || username !== (customer.broadband?.username || '')
    || staticIp !== (customer.broadband?.staticIp || '')
    || JSON.stringify([...extraIds].sort()) !== JSON.stringify([...initialExtraIds].sort());

  const handleChange = async (e) => {
    e.preventDefault();
    if (!planId) { toast.error('Select a plan'); return; }
    setSaving(true);
    try {
      await broadbandAPI.assignPlan({ customerId: customer._id, planId, username, staticIp, additionalPlanIds: extraIds.filter(Boolean) });
      toast.success('Subscription updated');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box', background: 'var(--cb-ffffff)' };
  const labelStyle = { fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 4, display: 'block' };
  const extraTotal = extraIds.reduce((sum, id) => sum + (plans.find(p => p._id === id)?.price || 0), 0);

  return (
    <div>
      <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 600 }}>Subscription</h3>
      <SummaryStrip items={[
        { label: 'Current Plan', value: <>{plan?.displayName || plan?.name || '—'}{initialExtraIds.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> +{initialExtraIds.length} more</span>}</> },
        { label: 'Price', value: plan?.price ? `₹${fmt(plan.price + extraTotal)}` : '—' },
      ]} />

      <form onSubmit={handleChange}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px 16px', alignItems: 'start' }}>
        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Plan</label>
          <SearchableSelect
            value={planId}
            onChange={setPlanId}
            placeholder="Search plan…"
            options={[
              ...plans.map(o => ({ value: o._id, label: `${o.displayName || o.name} — ₹${o.price}${o.isActive === false ? ' (Inactive)' : ''}` })),
              ...(!plans.some(o => o._id === plan?._id) && plan
                ? [{ value: plan._id, label: `${plan.displayName || plan.name} — ₹${plan.price}` }]
                : []),
            ]}
          />
        </div>

        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Additional Plans</label>
          {extraIds.map((id, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <SearchableSelect
                style={{ flex: 1 }}
                value={id}
                onChange={v => setExtraIds(ids => ids.map((x, xi) => xi === i ? v : x))}
                placeholder="Search plan…"
                options={plans
                  .filter(o => o._id === id || (o._id !== planId && !extraIds.includes(o._id)))
                  .map(o => ({ value: o._id, label: `${o.displayName || o.name} — ₹${o.price}${o.isActive === false ? ' (Inactive)' : ''}` }))}
              />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExtraIds(ids => ids.filter((_, xi) => xi !== i))}>
                <X size={14} />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExtraIds(ids => [...ids, ''])}>
            + Add another plan
          </button>
        </div>

        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Username</label>
          <input style={inputStyle} value={username} onChange={e => setUsername(e.target.value)} placeholder="PPPoE username" />
        </div>
        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Static IP</label>
          <input style={inputStyle} value={staticIp} onChange={e => setStaticIp(e.target.value)} placeholder="Optional" />
        </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !changed}>
          {saving ? 'Saving…' : 'Change Plan'}
        </button>
        {changed && <div style={{ fontSize: '.72rem', color: 'var(--c-d97706)', marginTop: 8 }}>This takes effect immediately — it doesn't change the current expiry or balance.</div>}
        </div>
      </form>
    </div>
  );
}

// ─── Addons Panel (generic addon plans + PlayBoxTV) ──────────────────────────
// Distinct from BroadbandSubscriptionPanel's "Additional Plans" list above: that panel's
// Change Plan takes effect at the next renewal bill and never touches the balance. Both
// mechanisms here instead bill the customer's outstandingBalance immediately (same model
// PlayBoxTV assignment already used) — "push" means charged now, not deferred.
function AddonsPanel({ customer, onSuccess }) {
  const { hasModuleRight } = useAuth();
  const canPushAddon = hasModuleRight('customers', 'assignPlan');
  const canViewPlaybox = hasModuleRight('playboxtv', 'view');
  const canPushPlaybox = hasModuleRight('playboxtv', 'assignRemove');

  const [plans, setPlans] = useState([]);
  const [addonPlanId, setAddonPlanId] = useState('');
  const [addingPlan, setAddingPlan] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const additionalPlans = customer.broadband?.additionalPlans || [];

  useEffect(() => {
    broadbandAPI.getPlans({ activeOnly: 'true', limit: 200 })
      .then(r => setPlans((r.data?.data?.docs || r.data?.data || []).filter(p => p.type === 'addon')))
      .catch(() => {});
  }, []);

  const attachedIds = additionalPlans.filter(a => a.plan).map(a => a.plan._id || a.plan);
  const availablePlans = plans.filter(p => !attachedIds.includes(p._id));

  const handlePushAddon = async () => {
    if (!addonPlanId) return;
    setAddingPlan(true);
    try {
      const r = await broadbandAPI.addAddon(customer._id, addonPlanId);
      toast.success(r.data?.message || 'Addon pushed');
      setAddonPlanId('');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to push addon');
    } finally {
      setAddingPlan(false);
    }
  };

  const handleRemoveAddon = async (entryId) => {
    setRemovingId(entryId);
    try {
      await broadbandAPI.removeAddon(customer._id, entryId);
      toast.success('Addon removed');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove addon');
    } finally {
      setRemovingId(null);
    }
  };

  const [packs, setPacks] = useState([]);
  const [packId, setPackId] = useState('');
  const [scheduleDate, setScheduleDate] = useState(''); // datetime-local string, blank = assign now
  const [pbSaving, setPbSaving] = useState(false);
  const pb = customer.broadband?.playboxtv;
  const pbActive = !!pb?.isActive;
  const pbScheduled = !!pb?.scheduledPackId;

  useEffect(() => {
    if (!canViewPlaybox) return;
    playboxtvAPI.getPacks().then(r => setPacks(r.data?.data || [])).catch(() => {});
  }, [canViewPlaybox]);

  // Every pack PlayBoxTV has for this mobile number (vendor's own record, not just the CRM's
  // current one). Re-fetched when the customer reloads, e.g. after an assign or cancel.
  const [pbHistory, setPbHistory] = useState(null); // null = loading, { error } on failure
  useEffect(() => {
    if (!canViewPlaybox) return;
    setPbHistory(null);
    playboxtvAPI.getHistory(customer._id)
      .then(r => setPbHistory(r.data?.data || []))
      .catch(err => setPbHistory({ error: err.response?.data?.message || 'Could not load PlayBoxTV history' }));
  }, [canViewPlaybox, customer._id, customer.updatedAt, customer.mobile]);

  const handleAssignPack = async () => {
    if (!packId) return;
    setPbSaving(true);
    try {
      const r = await playboxtvAPI.assign(customer._id, packId, scheduleDate ? new Date(scheduleDate).toISOString() : undefined);
      toast.success(r.data?.message || 'PlayBoxTV assigned');
      setPackId('');
      setScheduleDate('');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign PlayBoxTV pack');
    } finally {
      setPbSaving(false);
    }
  };

  const handleCancelScheduled = async () => {
    if (!window.confirm(`Cancel the scheduled ${pb.scheduledPackName || 'addon'}?`)) return;
    setPbSaving(true);
    try {
      await playboxtvAPI.cancelScheduled(customer._id);
      toast.success('Scheduled PlayBoxTV addon cancelled');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel scheduled addon');
    } finally {
      setPbSaving(false);
    }
  };

  const handleCancelPack = async () => {
    if (!window.confirm(`Cancel ${pb.packName}? This does not refund the balance already charged.`)) return;
    setPbSaving(true);
    try {
      await playboxtvAPI.cancel(customer._id);
      toast.success('PlayBoxTV addon cancelled');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel PlayBoxTV addon');
    } finally {
      setPbSaving(false);
    }
  };

  const handleRefreshStatus = async () => {
    setPbSaving(true);
    try {
      await playboxtvAPI.getStatus(customer._id);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh status');
    } finally {
      setPbSaving(false);
    }
  };

  const cardStyle = { border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 14 };

  return (
    <div>
      <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>Addons</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, alignItems: 'start' }}>

      <div style={cardStyle}>
        <div style={{ fontWeight: 700, fontSize: '.88rem', marginBottom: 10 }}>Addon Plans</div>
        {additionalPlans.length === 0 && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-9ca3af)', marginBottom: 10 }}>No addon plans attached.</div>
        )}
        {additionalPlans.map(a => (
          <div key={a._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--cb-f3f4f6)' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{a.plan?.displayName || a.plan?.name || '—'}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)' }}>₹{fmt(a.price)} · added {fmtDate(a.addedAt)}</div>
            </div>
            {canPushAddon && (
              <button className="btn btn-secondary btn-sm" disabled={removingId === a._id} onClick={() => handleRemoveAddon(a._id)}>
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        {canPushAddon && (
          <>
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              <SearchableSelect
                style={{ flex: 1 }}
                value={addonPlanId}
                onChange={setAddonPlanId}
                placeholder="Search addon plan…"
                options={availablePlans.map(o => ({ value: o._id, label: `${o.displayName || o.name} — ₹${o.price}` }))}
              />
              <button className="btn btn-primary btn-sm" disabled={!addonPlanId || addingPlan} onClick={handlePushAddon}>
                {addingPlan ? 'Pushing…' : 'Push'}
              </button>
            </div>
            <div style={{ fontSize: '.72rem', color: 'var(--c-d97706)', marginTop: 8 }}>Pushing bills the price to the customer's balance immediately.</div>
          </>
        )}
      </div>

      {canViewPlaybox && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: '.88rem', marginBottom: 10 }}>PlayBoxTV</div>
          {pbActive ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{pb.packName}</div>
              <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)' }}>₹{fmt(pb.resalePrice)} · valid till {fmtDate(pb.expiryAt)}</div>
              {pb.otts?.length > 0 && (
                <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', marginTop: 6 }}>
                  OTTs: {pb.otts.map(o => o.name).filter(Boolean).join(', ')}
                </div>
              )}
              {canPushPlaybox && (
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <button className="btn btn-secondary btn-sm" disabled={pbSaving} onClick={handleRefreshStatus}>Refresh Status</button>
                  <button className="btn btn-secondary btn-sm" disabled={pbSaving} onClick={handleCancelPack}>Cancel Addon</button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '.82rem', color: 'var(--c-9ca3af)', marginBottom: 10 }}>No active PlayBoxTV addon.</div>
          )}

          {pbScheduled && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 6, background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)' }}>
              <div style={{ fontWeight: 600, fontSize: '.85rem' }}>Scheduled: {pb.scheduledPackName}</div>
              <div style={{ fontSize: '.78rem', color: 'var(--c-92400e)' }}>Starts {fmtDate(pb.scheduledFor)}, {new Date(pb.scheduledFor).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
              {canPushPlaybox && (
                <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} disabled={pbSaving} onClick={handleCancelScheduled}>
                  Cancel Schedule
                </button>
              )}
            </div>
          )}

          {canPushPlaybox && !pbScheduled && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <SearchableSelect
                  value={packId}
                  onChange={setPackId}
                  placeholder="Search PlayBoxTV pack…"
                  options={packs.map(o => ({ value: o._id, label: `${o.name} — ₹${o.resalePrice}` }))}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="datetime-local" className="form-input" style={{ flex: 1, fontSize: '.82rem' }}
                    value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)} />
                  <button className="btn btn-primary btn-sm"
                    disabled={!packId || pbSaving || (!scheduleDate && pbActive)}
                    onClick={handleAssignPack}>
                    {pbSaving ? 'Saving…' : scheduleDate ? 'Schedule' : 'Assign Now'}
                  </button>
                </div>
              </div>
              <div style={{ fontSize: '.72rem', color: 'var(--c-d97706)', marginTop: 8 }}>
                {scheduleDate
                  ? "Nothing is billed or sent to PlayBoxTV until the scheduled time — checked every 5 minutes."
                  : pbActive
                    ? "Cancel the current addon first to assign immediately, or pick a date above to schedule the next one."
                    : "Leave the date blank to assign and bill immediately, or pick a future date to schedule it."}
              </div>
            </>
          )}

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--cb-f3f4f6)' }}>
            <div style={{ fontWeight: 700, fontSize: '.82rem', marginBottom: 8 }}>
              Past history <span style={{ fontWeight: 400, color: 'var(--c-6b7280)' }}>(PlayBoxTV, mobile {customer.mobile || '—'})</span>
            </div>
            {!customer.mobile ? (
              <div style={{ fontSize: '.78rem', color: 'var(--c-9ca3af)' }}>No mobile number on this customer.</div>
            ) : pbHistory === null ? (
              <div style={{ fontSize: '.78rem', color: 'var(--c-9ca3af)' }}>Loading…</div>
            ) : pbHistory.error ? (
              <div style={{ fontSize: '.78rem', color: 'var(--c-dc2626)' }}>{pbHistory.error}</div>
            ) : pbHistory.length === 0 ? (
              <div style={{ fontSize: '.78rem', color: 'var(--c-9ca3af)' }}>PlayBoxTV has no packs for this mobile number.</div>
            ) : pbHistory.map(h => {
              const active = h.expiryAt && new Date(h.expiryAt) > new Date();
              return (
                <div key={h.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600, fontSize: '.82rem' }}>{h.packName || h.packCode || '—'}</span>
                    <span style={{
                      fontSize: '.65rem', fontWeight: 700, borderRadius: 8, padding: '1px 6px', whiteSpace: 'nowrap',
                      color: active ? 'var(--c-166534)' : 'var(--c-6b7280)', background: active ? 'var(--cb-dcfce7)' : 'var(--cb-f3f4f6)',
                    }}>{active ? 'ACTIVE' : 'EXPIRED'}</span>
                  </div>
                  <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)' }}>
                    Sold {fmtDate(h.soldAt || h.startAt)} · {fmtDate(h.startAt)} → {fmtDate(h.expiryAt)}{h.validity ? ` (${h.validity} days)` : ''}
                  </div>
                  {h.otts.length > 0 && (
                    <div style={{ fontSize: '.7rem', color: 'var(--c-9ca3af)', marginTop: 2 }} title={h.otts.join(', ')}>
                      {h.otts.length} OTTs: {h.otts.slice(0, 6).join(', ')}{h.otts.length > 6 ? '…' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// ─── GTPL Saathi ("Apply Order") ─────────────────────────────────────────────
// Places a renewal order for this customer's STB directly on GTPL's own dealer portal
// (gtplsaathi.com), reusing the same session-cookie-based integration that Settings →
// GTPL Saathi manages. GTPL never returns a structured success/failure field — only an
// alert() popup's text — so a failure (insufficient balance, invalid STB, session expired,
// etc.) is detected server-side and a WhatsApp alert is fired automatically
// (gtpl_saathi_order_failed, see AlertSettings.js); this panel just reflects whatever the
// server decided (`ok`) so staff aren't left guessing whether the order actually went in.
// Local status (our own GtplTicket queue row) — distinct from the live GTPL status below.
const DISCONNECT_TICKET_STATUS_STYLE = {
  pending:   { bg: 'var(--cb-fef3c7)', fg: 'var(--c-92400e)' },
  raised:    { bg: 'var(--cb-dcfce7)', fg: 'var(--c-166534)' },
  cancelled: { bg: 'var(--cb-f3f4f6)', fg: 'var(--c-374151)' },
  failed:    { bg: 'var(--cb-fee2e2)', fg: 'var(--c-991b1b)' },
};
// GTPL's own live status on MyTicket.aspx — only fetched on demand (see "Check live status"
// below), same 4 values as GtplSaathiTicketPage's MyTicketsSection.
const GTPL_LIVE_STATUS_STYLE = {
  OPEN:              { bg: 'var(--cb-fef3c7)', fg: 'var(--c-92400e)' },
  CLOSE:             { bg: 'var(--cb-dcfce7)', fg: 'var(--c-166534)' },
  'IN PROGRESS':     { bg: 'var(--cb-dbeafe)', fg: 'var(--c-1e40af)' },
  'DETAILS REQUIRE': { bg: 'var(--cb-fee2e2)', fg: 'var(--c-991b1b)' },
};

// A small inline form for raising a NEW GTPL eSupport ticket against this customer's own
// account — any ticket type, not just a disconnect request. Kept deliberately minimal (no
// department/photo, unlike the full eSupport page) since this exists for the common case of
// "raise something quick for this customer, tagged to them" — the full GTPL Saathi → eSupport
// Tickets page still covers photos/departments for anything more involved.
const GTPL_TICKET_MAX_PHOTO_BYTES = 4 * 1024 * 1024;

function NewCustomerTicketForm({ customerId, onCreated, onCancel }) {
  const [options, setOptions] = useState(null);
  const [complaintType, setComplaintType] = useState('');
  const [remark, setRemark] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    gtplSaathiAPI.getSupportTicketOptions()
      .then(({ data }) => setOptions(data.data))
      .catch(() => {});
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file after removing it
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files can be attached'); return; }
    if (file.size > GTPL_TICKET_MAX_PHOTO_BYTES) { toast.error('Photo is too large — must be under 4MB'); return; }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!complaintType) { toast.error('Select a ticket type'); return; }
    if (complaintType !== '382' && !remark.trim()) { toast.error('Enter a remark'); return; }
    setSubmitting(true);
    try {
      let payload;
      if (photo) {
        payload = new FormData();
        payload.append('customerId', customerId);
        payload.append('complaintType', complaintType);
        if (remark.trim()) payload.append('remark', remark.trim());
        payload.append('mode', 'instant');
        payload.append('photo', photo);
      } else {
        payload = { customerId, complaintType, remark: remark.trim() || undefined, mode: 'instant' };
      }
      const { data } = await gtplSaathiAPI.createCustomerTicket(payload);
      toast.success(`GTPL ticket raised — Request No ${data.data.ticketNumber}`);
      clearPhoto();
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to raise ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 12, marginBottom: 10, background: 'var(--cb-f9fafb)' }}>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Ticket Type</label>
        <select className="form-control" value={complaintType} onChange={(e) => setComplaintType(e.target.value)}>
          <option value="">Select ticket type…</option>
          {(options?.ticketTypes || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>
          Remark{complaintType === '382' ? ' (optional — defaults to the account number)' : ''}
        </label>
        <textarea className="form-control" rows={3} placeholder="Describe the issue…" value={remark} onChange={(e) => setRemark(e.target.value)} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Photo (optional)</label>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
        {photoPreview ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={photoPreview} alt="Attachment preview" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--cb-e5e7eb)' }} />
            <div style={{ flex: 1, fontSize: '.78rem', color: 'var(--c-6b7280)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo?.name}</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={clearPhoto}>
              <X size={14} style={{ marginRight: 4 }} />Remove
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}>
            <Paperclip size={14} style={{ marginRight: 6 }} />
            Attach a photo
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>{submitting ? 'Raising…' : 'Raise Ticket'}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel} disabled={submitting}>Cancel</button>
      </div>
    </form>
  );
}

// Lists every GTPL eSupport ticket ever raised FOR this customer THROUGH THIS CRM (any ticket
// type — disconnect requests plus anything raised via the "Raise New Ticket" form below), so
// staff don't have to go to GTPL Saathi → eSupport Tickets and search by Request No just to
// see the ticket number/status. This is a local log, not a live GTPL query — gtplsaathi.com's
// own MyTicket.aspx history has no account-number filter at all, so there's no way to
// retroactively backfill "every ticket ever raised for this account"; it only reflects
// tickets raised going forward through this page or the Close CATV Connection flow. "Check
// live status" is opt-in per row since it drives the same slow real-browser scrape of GTPL's
// MyTicket.aspx as the eSupport page (20-90s) — see gtplSaathiAPI.getTicketDetail.
function GtplTicketsSection({ customer, canRaiseTicket, canViewLiveStatus }) {
  const [tickets, setTickets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // ticket _id currently expanded
  const [liveDetails, setLiveDetails] = useState({}); // ticketNumber -> live detail (cache)
  const [liveLoading, setLiveLoading] = useState(null); // ticket _id currently fetching
  const [showNewForm, setShowNewForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    gtplSaathiAPI.listCustomerTickets(customer._id)
      .then(({ data }) => setTickets(data.data || []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [customer._id]);

  useEffect(() => { load(); }, [load]);

  const toggleLive = async (ticket) => {
    if (expanded === ticket._id) { setExpanded(null); return; }
    setExpanded(ticket._id);
    if (liveDetails[ticket.ticketNumber]) return; // cached
    setLiveLoading(ticket._id);
    try {
      const { data } = await gtplSaathiAPI.getTicketDetail(ticket.ticketNumber);
      setLiveDetails((prev) => ({ ...prev, [ticket.ticketNumber]: data.data }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load live ticket status');
      setExpanded(null);
    } finally {
      setLiveLoading(null);
    }
  };

  if (!canRaiseTicket) return null; // nothing this staff member can do or see here

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Ticket size={16} /> GTPL Tickets
        </h3>
        {!showNewForm && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowNewForm(true)}>Raise New Ticket</button>
        )}
      </div>

      {showNewForm && (
        <NewCustomerTicketForm
          customerId={customer._id}
          onCancel={() => setShowNewForm(false)}
          onCreated={() => { setShowNewForm(false); load(); }}
        />
      )}

      {loading ? (
        <div style={{ color: 'var(--c-6b7280)', fontSize: '.83rem' }}>Loading…</div>
      ) : !tickets?.length ? (
        <div style={{ color: 'var(--c-6b7280)', fontSize: '.83rem' }}>No tickets raised for this customer yet.</div>
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {tickets.map((t) => {
          const localStyle = DISCONNECT_TICKET_STATUS_STYLE[t.status] || { bg: 'var(--cb-f3f4f6)', fg: 'var(--c-374151)' };
          const live = liveDetails[t.ticketNumber];
          const liveStyle = live?.status ? (GTPL_LIVE_STATUS_STYLE[live.status.toUpperCase()] || { bg: 'var(--cb-f3f4f6)', fg: 'var(--c-374151)' }) : null;
          return (
            <div key={t._id} style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', flexWrap: 'wrap' }}>
                <span style={{ background: localStyle.bg, color: localStyle.fg, fontSize: '.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: 999, textTransform: 'capitalize' }}>
                  {t.status}
                </span>
                <span style={{ fontWeight: 600, fontSize: '.83rem' }}>{t.ticketTypeLabel || '—'}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '.83rem' }}>{t.ticketNumber || '—'}</span>
                <span style={{ color: 'var(--c-6b7280)', fontSize: '.78rem' }}>{fmtDate(t.raisedAt || t.scheduledDate)}</span>
                {t.failReason && <span style={{ color: 'var(--c-b91c1c)', fontSize: '.78rem' }}>{t.failReason}</span>}
                <div style={{ marginLeft: 'auto' }}>
                  {canViewLiveStatus && t.ticketNumber && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleLive(t)}>
                      {liveLoading === t._id
                        ? <Loader size={12} style={{ marginRight: 4, animation: 'spin 1s linear infinite' }} />
                        : (expanded === t._id ? <ChevronUp size={12} style={{ marginRight: 4 }} /> : <ChevronDown size={12} style={{ marginRight: 4 }} />)}
                      {liveLoading === t._id ? 'Checking…' : 'Check live status'}
                    </button>
                  )}
                </div>
              </div>
              {expanded === t._id && live && (
                <div style={{ padding: '10px 14px 14px', background: 'var(--cb-f9fafb)', fontSize: '.82rem', borderTop: '1px solid var(--cb-e5e7eb)' }}>
                  {liveStyle && (
                    <span style={{ background: liveStyle.bg, color: liveStyle.fg, fontSize: '.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: 999, marginBottom: 8, display: 'inline-block' }}>
                      GTPL status: {live.status}
                    </span>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 10 }}>
                    {[['Department', live.department], ['Category', live.category], ['Branch', live.branch], ['Unit', live.unit], ['Done By', live.doneBy]]
                      .filter(([, v]) => v)
                      .map(([label, v]) => (
                        <div key={label}>
                          <div style={{ color: 'var(--c-6b7280)', fontSize: '.72rem' }}>{label}</div>
                          <div style={{ fontWeight: 600 }}>{v}</div>
                        </div>
                      ))}
                  </div>
                  <div style={{ marginBottom: live.replies?.length ? 10 : 0 }}>
                    <div style={{ color: 'var(--c-6b7280)', fontSize: '.72rem', marginBottom: 2 }}>Original Remark</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{live.originalRemark || '—'}</div>
                  </div>
                  {live.replies?.length > 0 && (
                    <div>
                      <div style={{ color: 'var(--c-6b7280)', fontSize: '.72rem', marginBottom: 4 }}>
                        {live.status?.toUpperCase() === 'CLOSE' ? 'Resolution' : 'Response'}
                      </div>
                      {live.replies.map((r, i) => (
                        <div key={i} style={{ background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', color: 'var(--c-6b7280)', marginBottom: 3 }}>
                            <span style={{ fontWeight: 700, color: 'var(--c-2980b9)' }}>{r.author || 'GTPL'}</span>
                            <span>{r.time}</span>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{r.text || '—'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function GtplSaathiPanel({ customer, canSearch, canApplyOrder, canSuspendActive, canPlaceOrder, canRaiseTicket, canViewLiveStatus, canModify, canLongTermSwap }) {
  const [stb, setStb] = useState(customer.catv?.setTopBoxSerial || '');
  const [accountNumber, setAccountNumber] = useState(customer.catv?.accountNumber || '');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const [searchingSub, setSearchingSub] = useState(false);
  const [subscriber, setSubscriber] = useState(null);
  const [subError, setSubError] = useState(null);
  const [compareWithCrm, setCompareWithCrm] = useState(true);
  const [pushingFix, setPushingFix] = useState(false);

  // renewInfo mirrors exactly what gtplsaathi.com's own Renew.aspx shows once you search on
  // it — customer header (name/entity/status/STB type) plus one row per renewable package
  // (price, contract#, due date, status, that package's own Validity options) — fetched
  // alongside the general subscriber lookup above so one Search click populates both. Only
  // fired when the staff member actually has the `renew` right (renew/search shares that
  // permission — see routes/gtplSaathi.js), same gate as the Renew button itself.
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewInfo, setRenewInfo] = useState(null);
  const [renewSearchError, setRenewSearchError] = useState(null);
  const [selections, setSelections] = useState({}); // contractNumber -> { checked, validity }

  const toggleContract = (cn) => setSelections((s) => ({ ...s, [cn]: { ...s[cn], checked: !s[cn]?.checked } }));
  const setContractValidity = (cn, v) => setSelections((s) => ({ ...s, [cn]: { ...s[cn], validity: v } }));

  const [reasons, setReasons] = useState([]);
  const [suspendAction, setSuspendAction] = useState('SUSPEND');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [suspendResult, setSuspendResult] = useState(null);

  useEffect(() => {
    if (!canSuspendActive) return;
    gtplSaathiAPI.getSuspendReasons()
      .then(({ data }) => setReasons(data.data || []))
      .catch(() => {});
  }, [canSuspendActive]);

  const handleSearchSubscriber = async () => {
    if (!stb.trim() && !accountNumber.trim()) { toast.error('Enter an STB serial or account number'); return; }
    const params = { stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined };

    setSearchingSub(true);
    setSubError(null);
    setSubscriber(null);
    const searchPromise = gtplSaathiAPI.search(params)
      .then(({ data }) => {
        if (data.data?.found === false) setSubError(data.data.message || 'No matching subscriber found on GTPL Saathi');
        else setSubscriber(data.data);
      })
      .catch((err) => setSubError(err.response?.data?.message || 'Search failed'))
      .finally(() => setSearchingSub(false));

    let renewPromise = Promise.resolve();
    if (canApplyOrder) {
      setRenewLoading(true);
      setRenewSearchError(null);
      setRenewInfo(null);
      setSelections({});
      renewPromise = gtplSaathiAPI.renewSearch(params)
        .then(({ data }) => {
          if (!data.data?.found) {
            setRenewSearchError('No renewable package found for this subscriber on GTPL Saathi');
            return;
          }
          setRenewInfo(data.data);
          const init = {};
          data.data.packages.forEach((p) => {
            const def = p.validityOptions.find((o) => o.selected) || p.validityOptions[0];
            init[p.contractNumber] = { checked: true, validity: def?.value || '' };
          });
          setSelections(init);
        })
        .catch((err) => setRenewSearchError(err.response?.data?.message || 'Failed to load renewable packages'))
        .finally(() => setRenewLoading(false));
    }

    await Promise.all([searchPromise, renewPromise]);
  };

  // Writes CRM's Name/Address/Mobile straight to GTPL Saathi via Modify.aspx (same endpoint as
  // the standalone GTPL Saathi → Modify page) — only for whichever of those three fields the
  // comparison card actually flagged as mismatched, so a field that already matches is never
  // resubmitted. Modify.aspx is keyed by account number, not STB serial, so it needs one on file.
  const handlePushFix = async () => {
    if (!subscriber) return;
    if (!accountNumber.trim()) { toast.error('Enter the GTPL account number — Modify.aspx needs it, an STB serial alone isn\'t enough'); return; }
    const rows = buildGtplCrmComparison(customer, subscriber);
    const mismatched = new Set(rows.filter(r => r.fixable && r.match === false).map(r => r.label));
    if (!mismatched.size) { toast('Nothing to push — already matches CRM'); return; }

    const fields = {};
    if (mismatched.has('Name')) {
      const parts = (customer.name || '').trim().split(/\s+/).filter(Boolean);
      fields.fname = parts[0] || '';
      fields.lname = parts.length > 1 ? parts[parts.length - 1] : '';
    }
    if (mismatched.has('Address')) {
      fields.address1 = customer.address?.line1 || '';
      // GTPL rejects a blank Address Line 2 outright ("Address line2 cannot be blank") — CRM
      // treats line2 as optional, so fall back to the (always-required) area, then landmark/city,
      // rather than ever submitting an empty string.
      fields.address2 = customer.address?.line2 || customer.address?.area || customer.address?.landmark || customer.address?.city || fields.address1;
      if (customer.address?.pincode) fields.pincode = customer.address.pincode;
    }
    if (mismatched.has('Mobile')) {
      fields.mobile = customer.mobile || '';
    }

    setPushingFix(true);
    try {
      const { data } = await gtplSaathiAPI.modifyProfile(accountNumber.trim(), fields);
      toast.success(data.data?.message || 'GTPL profile updated');
      const ticket = data.data?.ticket;
      if (ticket?.ok) {
        toast.success(`eSupport ticket raised — Request No ${ticket.ticketNumber}`);
      } else if (ticket && ticket.ok === false) {
        toast.error(`Profile updated, but the mobile-update eSupport ticket failed: ${ticket.message}`);
      }
      await handleSearchSubscriber();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Push to GTPL failed');
    } finally {
      setPushingFix(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!stb.trim() && !accountNumber.trim()) { toast.error('Enter an STB serial or account number'); return; }

    // If the package checklist loaded (see handleSearchSubscriber), renew only what's ticked,
    // each with its own validity — otherwise fall back to GTPL's own blind "renew everything
    // Renew.aspx lists" behaviour (e.g. staff who never clicked Search first).
    let contracts;
    if (renewInfo) {
      contracts = renewInfo.packages
        .filter((p) => selections[p.contractNumber]?.checked)
        .map((p) => ({ contractNumber: p.contractNumber, validity: selections[p.contractNumber]?.validity || undefined }));
      if (!contracts.length) { toast.error('Select at least one package to renew'); return; }
    }

    setApplying(true);
    setResult(null);
    try {
      const { data } = await gtplSaathiAPI.renew({
        stb: stb.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        contracts,
        customerId: customer._id,
      });
      setResult(data.data);
      if (data.data?.ok === false) {
        toast.error('Order failed on GTPL Saathi — admin has been alerted');
      } else {
        toast.success('Order applied on GTPL Saathi');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Apply order failed';
      setResult({ ok: false, message });
      toast.error(message);
    } finally {
      setApplying(false);
    }
  };

  const handleSuspend = async (e) => {
    e.preventDefault();
    if (!stb.trim() && !accountNumber.trim()) { toast.error('Enter an STB serial or account number'); return; }
    if (!reason) { toast.error('Select a reason'); return; }
    setSuspending(true);
    setSuspendResult(null);
    try {
      const { data } = await gtplSaathiAPI.suspendActive({
        stb: stb.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        action: suspendAction,
        reason,
        notes: notes.trim() || undefined,
        customerId: customer._id,
      });
      setSuspendResult({ ok: true, message: data.data?.message });
      if (data.queued) {
        toast(data.message, { icon: '⏳' });
      } else {
        toast.success(suspendAction === 'ACTIVE' ? 'Activated on GTPL Saathi' : 'Suspended on GTPL Saathi');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Suspend/Activate failed';
      setSuspendResult({ ok: false, message });
      toast.error(message);
    } finally {
      setSuspending(false);
    }
  };

  return (
    <div>
      <GtplTicketsSection customer={customer} canRaiseTicket={canRaiseTicket} canViewLiveStatus={canViewLiveStatus} />
      {canLongTermSwap && <GtplLongTermSwapSection customer={customer} />}

      <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>GTPL Saathi — Renewal</h3>
      <p style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', marginBottom: 18 }}>
        Renews this STB's existing package(s) directly on GTPL's own dealer portal (gtplsaathi.com) —
        separate from this system's own billing/renewal. If it fails for any reason (insufficient
        balance, invalid STB/account, session expired, etc.) an admin is notified on WhatsApp automatically.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary" onClick={handleSearchSubscriber} disabled={searchingSub || !canSearch} style={{ margin: 0 }}>
          <Search size={14} style={{ marginRight: 6 }} />
          {searchingSub ? 'Searching…' : 'Search on GTPL Saathi'}
        </button>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.82rem', color: 'var(--c-6b7280)', cursor: 'pointer' }} title="Diff the search result above against this customer's CRM record (name, mobile, status, package, expiry)">
          <input type="checkbox" checked={compareWithCrm} onChange={e => setCompareWithCrm(e.target.checked)} />
          <ScanSearch size={14} /> Compare with CRM
        </label>
      </div>
      {!canSearch && (
        <span style={{ fontSize: '.78rem', color: 'var(--c-b91c1c)', marginLeft: 10 }}>You don't have permission to search GTPL Saathi.</span>
      )}

      {subError && (
        <div style={{ marginBottom: 18, fontSize: '.85rem', color: 'var(--c-6b7280)' }}>{subError}</div>
      )}

      {subscriber && (
        <div style={{ marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {compareWithCrm && (
            <GtplCrmCompareCard
              rows={buildGtplCrmComparison(customer, subscriber)}
              canModify={canModify}
              pushing={pushingFix}
              onPushFix={handlePushFix}
            />
          )}

          <div style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 8 }}>
              <UserCheck size={14} /> {subscriber.customer?.name || '—'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, fontSize: '.82rem', color: 'var(--c-6b7280)' }}>
              <div>{subscriber.customer?.category || '—'}</div>
              <div>Mobile: {subscriber.customer?.mobile || '—'}</div>
              <div>{subscriber.customer?.balance || '—'}</div>
              <div>{subscriber.customer?.kyc || '—'}</div>
              <div>ePay: {subscriber.customer?.ePay || '—'}</div>
              <div>{subscriber.customer?.createdOn || '—'}</div>
            </div>
            {subscriber.address?.line1 && (
              <div style={{ fontSize: '.82rem', color: 'var(--c-6b7280)', marginTop: 8 }}>
                {[subscriber.address.line1, subscriber.address.city, subscriber.address.state].filter(Boolean).join(', ')}
              </div>
            )}
          </div>

          {subscriber.stb?.detail && (
            <div style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 8 }}>
                <Tv size={14} /> Set-Top Box
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, fontSize: '.82rem', color: 'var(--c-6b7280)' }}>
                <div>{subscriber.stb.detail}</div>
                <div>{subscriber.stb.status || '—'}</div>
                <div>{subscriber.stb.vcNo || '—'}</div>
                <div>{subscriber.stb.autoRenew || '—'}</div>
                <div>{subscriber.stb.issuedOn || '—'}</div>
                <div>{subscriber.stb.warrantyEnd || '—'}</div>
              </div>
            </div>
          )}

          {subscriber.packages?.length > 0 && (
            <div style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Packages</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {subscriber.packages.map((p, i) => (
                  <div key={i} style={{ fontSize: '.82rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, borderBottom: i < subscriber.packages.length - 1 ? '1px solid var(--cb-e5e7eb)' : 'none', paddingBottom: 6 }}>
                    <span>{p.name} {p.mrp ? `— ₹${p.mrp}` : ''}</span>
                    <span style={{ color: 'var(--c-6b7280)' }}>{p.status} · {p.startDate} → {p.endDate}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {subscriber.history?.length > 0 && (
            <div style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 8 }}>
                <History size={14} /> Recent History
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '.8rem', color: 'var(--c-6b7280)' }}>
                {subscriber.history.slice(0, 10).map((h, i) => (
                  <div key={i}>{h.date} — {h.description}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleApply} style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '16px', marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>STB Serial</label>
            <input className="form-control" value={stb} onChange={e => setStb(e.target.value)} placeholder="STB serial" />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Account Number</label>
            <input className="form-control" value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="GTPL account number" />
          </div>
        </div>

        {renewLoading && (
          <div style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>Loading packages from GTPL Saathi…</div>
        )}
        {renewSearchError && !renewLoading && (
          <div style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>{renewSearchError}</div>
        )}
        {!renewInfo && !renewLoading && !renewSearchError && (
          <div style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
            Click "Search on GTPL Saathi" above to load this subscriber's renewable packages —
            same list gtplsaathi.com's own Renew page shows, with price, due date and validity per package.
          </div>
        )}

        {renewInfo && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: '.82rem', color: 'var(--c-6b7280)', marginBottom: 10 }}>
              <strong style={{ color: 'inherit' }}>{renewInfo.customer.name}</strong>
              {renewInfo.customer.entity && <> · {renewInfo.customer.entity}</>}
              {renewInfo.customer.status && <> · {renewInfo.customer.status}</>}
              {renewInfo.customer.stbType && <> · {renewInfo.customer.stbType}</>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renewInfo.packages.map((p) => (
                <div key={p.contractNumber || p.name} style={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                  background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: '8px 10px', fontSize: '.82rem',
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!selections[p.contractNumber]?.checked} onChange={() => toggleContract(p.contractNumber)} />
                    {p.name || 'Package'}
                  </label>
                  {p.price && <span style={{ color: 'var(--c-6b7280)' }}>₹{p.price}</span>}
                  {p.contractNumber && <span style={{ color: 'var(--c-6b7280)' }}>Contract# {p.contractNumber}</span>}
                  {p.dueDate && <span style={{ color: 'var(--c-6b7280)' }}>Due {p.dueDate}</span>}
                  {p.status && (
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontSize: '.72rem', fontWeight: 600,
                      background: /active/i.test(p.status) ? 'var(--cb-dcfce7)' : 'var(--cb-fee2e2)',
                      color: /active/i.test(p.status) ? 'var(--c-166534)' : 'var(--c-991b1b)',
                    }}>
                      {p.status}
                    </span>
                  )}
                  {p.validityOptions?.length > 0 && (
                    <select
                      className="form-control"
                      style={{ width: 'auto', marginLeft: 'auto', fontSize: '.8rem', padding: '4px 8px' }}
                      value={selections[p.contractNumber]?.validity || ''}
                      onChange={(e) => setContractValidity(p.contractNumber, e.target.value)}
                      disabled={!selections[p.contractNumber]?.checked}
                    >
                      {p.validityOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={applying || !canApplyOrder}>
          <ShoppingCart size={14} style={{ marginRight: 6 }} />
          {applying ? 'Renewing…' : (renewInfo ? `Renew Selected (${renewInfo.packages.filter(p => selections[p.contractNumber]?.checked).length})` : 'Renew')}
        </button>
        {!canApplyOrder && (
          <span style={{ fontSize: '.78rem', color: 'var(--c-b91c1c)', marginLeft: 10 }}>You don't have permission to apply GTPL Saathi orders.</span>
        )}
      </form>

      {result && (
        <div style={{
          background: result.ok === false ? 'var(--cb-fef2f2)' : 'var(--cb-f0fdf4)',
          border: `1px solid ${result.ok === false ? 'var(--cb-fecaca)' : 'var(--cb-bbf7d0)'}`,
          borderRadius: 8, padding: '12px 16px', fontSize: '.85rem',
          color: result.ok === false ? 'var(--c-991b1b)' : 'var(--c-166534)',
        }}>
          <strong>{result.ok === false ? 'Renewal Failed' : 'Renewal Applied'}</strong>
          {result.message ? <div style={{ marginTop: 4 }}>{result.message}</div> : null}
          {result.ok === false && (
            <div style={{ marginTop: 6, fontSize: '.78rem', color: 'var(--c-b91c1c)' }}>An admin has been notified on WhatsApp.</div>
          )}
        </div>
      )}

      <h3 style={{ margin: '28px 0 16px', fontSize: '1rem', fontWeight: 600 }}>Suspend / Activate</h3>
      <p style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', marginBottom: 18 }}>
        Flips this STB's status directly on GTPL Saathi — e.g. suspend for non-payment, or
        reactivate once it's resolved. Uses the STB serial / account number entered above.
      </p>

      <form onSubmit={handleSuspend} style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Action</label>
            <select className="form-control" value={suspendAction} onChange={e => setSuspendAction(e.target.value)}>
              <option value="SUSPEND">Suspend</option>
              <option value="ACTIVE">Activate</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Reason</label>
            <select className="form-control" value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">Select reason…</option>
              {reasons.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
            <input className="form-control" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes" />
          </div>
        </div>
        <button type="submit" className="btn btn-secondary" disabled={suspending || !canSuspendActive}>
          {suspendAction === 'ACTIVE' ? <Power size={14} style={{ marginRight: 6 }} /> : <PowerOff size={14} style={{ marginRight: 6 }} />}
          {suspending ? 'Submitting…' : (suspendAction === 'ACTIVE' ? 'Activate' : 'Suspend')}
        </button>
        {!canSuspendActive && (
          <span style={{ fontSize: '.78rem', color: 'var(--c-b91c1c)', marginLeft: 10 }}>You don't have permission to suspend/activate on GTPL Saathi.</span>
        )}
      </form>

      {suspendResult && (
        <div style={{
          background: suspendResult.ok === false ? 'var(--cb-fef2f2)' : 'var(--cb-f0fdf4)',
          border: `1px solid ${suspendResult.ok === false ? 'var(--cb-fecaca)' : 'var(--cb-bbf7d0)'}`,
          borderRadius: 8, padding: '12px 16px', fontSize: '.85rem', marginTop: 16,
          color: suspendResult.ok === false ? 'var(--c-991b1b)' : 'var(--c-166534)',
        }}>
          <strong>{suspendResult.ok === false ? 'Failed' : 'Submitted'}</strong>
          {suspendResult.message ? <div style={{ marginTop: 4 }}>{suspendResult.message}</div> : null}
        </div>
      )}

      <GtplOrderPanel stb={stb} accountNumber={accountNumber} canPlaceOrder={canPlaceOrder} />
    </div>
  );
}

// ─── GTPL Saathi ("Order") ────────────────────────────────────────────────────
// A genuinely NEW package order — distinct from the Renewal section above, which only
// extends an EXISTING package. GTPL itself rejects this for an STB/account that already has
// an active base pack (that error surfaces on Search below). Real money changes hands on
// Place Order — the LCO's own GTPL wallet gets debited — so this is a deliberate two-step
// flow: Preview shows GTPL's own pricing (retail price, GST, total, wallet deduction)
// without spending anything, and only then does Place Order become available. Search results
// (packages/broadcasters/channels) are never hardcoded — they're scraped live from GTPL on
// every Search, same as the pricing on every Preview.
const emptyOrderSelection = { setupType: 'M', validity: 'D30', language: '', packageName: '', packageCode: '', ftaBase: '', broadcasterName: '', channelNames: [] };

function GtplOrderPanel({ stb, accountNumber, canPlaceOrder }) {
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [options, setOptions] = useState(null); // orderSearch() result
  const [sel, setSel] = useState(emptyOrderSelection);

  const [channelFilters, setChannelFilters] = useState({ genre: '', language: '', sdhd: '', search: '' });
  const [channelResults, setChannelResults] = useState(null);
  const [channelsLoading, setChannelsLoading] = useState(false);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const [placing, setPlacing] = useState(false);
  const [placeResult, setPlaceResult] = useState(null);

  const resetDownstream = () => { setPreview(null); setPreviewError(''); setPlaceResult(null); };

  const handleSearch = async () => {
    if (!stb.trim() && !accountNumber.trim()) { toast.error('Enter an STB serial or account number'); return; }
    setSearching(true);
    setSearchError('');
    setOptions(null);
    setSel(emptyOrderSelection);
    resetDownstream();
    setChannelResults(null);
    try {
      const { data } = await gtplSaathiAPI.orderSearch({ stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined });
      setOptions(data.data);
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Order search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleLoadChannels = async () => {
    setChannelsLoading(true);
    try {
      const { data } = await gtplSaathiAPI.orderChannelSearch({
        stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined,
        genre: channelFilters.genre || undefined, language: channelFilters.language || undefined,
        sdhd: channelFilters.sdhd || undefined, search: channelFilters.search || undefined,
      });
      setChannelResults(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Channel search failed');
    } finally {
      setChannelsLoading(false);
    }
  };

  const toggleChannel = (label) => setSel((s) => ({
    ...s,
    channelNames: s.channelNames.includes(label) ? s.channelNames.filter((n) => n !== label) : [...s.channelNames, label],
  }));

  const buildOrderParams = () => ({
    stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined,
    setupType: sel.setupType, validity: sel.validity, language: sel.language || undefined,
    packageName: sel.setupType === 'M' ? sel.packageName : undefined,
    packageCode: sel.setupType === 'M' ? sel.packageCode : undefined,
    ftaBase: sel.setupType !== 'M' ? sel.ftaBase : undefined,
    broadcasterName: sel.setupType === 'O' ? sel.broadcasterName : undefined,
    channelNames: sel.setupType === 'A' ? sel.channelNames : undefined,
  });

  const handlePreview = async () => {
    if (sel.setupType === 'M' && !sel.packageCode) { toast.error('Select a package'); return; }
    if (sel.setupType !== 'M' && !sel.ftaBase) { toast.error('Select an FTA base'); return; }
    if (sel.setupType === 'O' && !sel.broadcasterName) { toast.error('Select a broadcaster'); return; }
    if (sel.setupType === 'A' && !sel.channelNames.length) { toast.error('Select at least one channel'); return; }
    setPreviewing(true);
    setPreviewError('');
    setPlaceResult(null);
    try {
      const { data } = await gtplSaathiAPI.orderPreview(buildOrderParams());
      setPreview(data.data);
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handlePlace = async () => {
    setPlacing(true);
    setPlaceResult(null);
    try {
      const { data } = await gtplSaathiAPI.placeGtplOrder(buildOrderParams());
      setPlaceResult({ ok: true, ...data.data });
      toast.success('Order placed on GTPL Saathi');
    } catch (err) {
      // No err.response means the request never got a reply (client/proxy timeout — this
      // flow can run close to 60s for A la Carte, right at nginx's own proxy_read_timeout,
      // which this session can't raise). The backend keeps running after a timeout like
      // that (confirmed for the eSupport ticket path, same shared browser-automation
      // pattern) — for a REAL-MONEY order, treating "no response" as "failed" risks a
      // duplicate order on retry. Never claim failure here without a real error response.
      const unconfirmed = !err.response;
      const message = err.response?.data?.message
        || (unconfirmed ? "No response from the server — the order may still have gone through and charged the wallet. Check gtplsaathi.com before placing it again." : 'Place order failed');
      setPlaceResult({ ok: false, unconfirmed, message });
      toast.error(message);
    } finally {
      setPlacing(false);
    }
  };

  const inp = { width: '100%', padding: '7px 10px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.85rem', boxSizing: 'border-box' };
  const chipStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 999,
    border: `1px solid ${active ? 'var(--cb-2563eb)' : 'var(--cb-d1d5db)'}`, background: active ? 'var(--cb-eff6ff)' : 'var(--cb-ffffff)',
    color: active ? 'var(--c-1d4ed8)' : 'var(--c-374151)', fontSize: '.78rem', cursor: 'pointer', marginRight: 6, marginBottom: 6,
  });

  return (
    <div style={{ marginTop: 28 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600 }}>GTPL Saathi — Order</h3>
      <p style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', marginBottom: 14 }}>
        Places a genuinely NEW package order — for an STB/account with no active base pack yet.
        GTPL rejects this outright if one already exists (use Renewal above instead).
        <strong> This spends real money from the GTPL wallet</strong> — always Preview first.
        Uses the STB serial / account number entered above.
      </p>

      <button type="button" className="btn btn-secondary" onClick={handleSearch} disabled={searching || !canPlaceOrder} style={{ marginBottom: 14 }}>
        <Search size={14} style={{ marginRight: 6 }} />
        {searching ? 'Searching…' : 'Search'}
      </button>
      {!canPlaceOrder && (
        <span style={{ fontSize: '.78rem', color: 'var(--c-b91c1c)', marginLeft: 10 }}>You don't have permission to place GTPL Saathi orders.</span>
      )}
      {searchError && (
        <div style={{ background: 'var(--cb-fef2f2)', border: '1px solid var(--cb-fecaca)', borderRadius: 8, padding: '10px 14px', fontSize: '.85rem', color: 'var(--c-991b1b)', marginBottom: 14 }}>
          {searchError}
        </div>
      )}

      {options && (
        <div style={{ background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: '.85rem', marginBottom: 14, color: 'var(--c-374151)' }}>
            <strong>{options.customer.name}</strong> — {options.customer.entity}
            {' '}<span style={{ color: options.customer.status === 'INACTIVE' ? 'var(--c-b45309)' : 'var(--c-374151)' }}>({options.customer.status})</span>
            {options.customer.lastAssignedPackage && <> · Last package: {options.customer.lastAssignedPackage}</>}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 6 }}>Setup Type</label>
            {[['M', 'GTPL Recommended Pack'], ['O', 'Broadcaster Bouquet'], ['A', 'A la Carte Channel']].map(([val, label]) => (
              <label key={val} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 18, fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="radio" checked={sel.setupType === val} onChange={() => { setSel((s) => ({ ...emptyOrderSelection, validity: s.validity, language: s.language, setupType: val })); resetDownstream(); }} />
                {label}
              </label>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Validity</label>
              <select className="form-control" style={inp} value={sel.validity} onChange={(e) => { setSel((s) => ({ ...s, validity: e.target.value })); resetDownstream(); }}>
                {options.validityOptions.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>Language (optional)</label>
              <select className="form-control" style={inp} value={sel.language} onChange={(e) => { setSel((s) => ({ ...s, language: e.target.value })); resetDownstream(); }}>
                <option value="">GTPL default</option>
                {options.languages.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>

          {sel.setupType === 'M' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 6 }}>Package</label>
              {options.recommendedPackages.map((p) => (
                <label key={p.id} style={{ display: 'block', padding: '6px 10px', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, marginBottom: 6, fontSize: '.85rem', cursor: 'pointer', background: sel.packageCode === p.code ? 'var(--cb-eff6ff)' : 'var(--cb-ffffff)' }}>
                  <input type="radio" checked={sel.packageCode === p.code} onChange={() => { setSel((s) => ({ ...s, packageName: p.label, packageCode: p.code })); resetDownstream(); }} style={{ marginRight: 8 }} />
                  {p.label}
                </label>
              ))}
            </div>
          )}

          {sel.setupType !== 'M' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 6 }}>FTA Base (required)</label>
              {options.ftaBaseOptions.map((f) => (
                <label key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 18, fontSize: '.85rem', cursor: 'pointer' }}>
                  <input type="radio" checked={sel.ftaBase === f.code} onChange={() => { setSel((s) => ({ ...s, ftaBase: f.code })); resetDownstream(); }} />
                  {f.code}
                </label>
              ))}
            </div>
          )}

          {sel.setupType === 'O' && (
            <div style={{ marginBottom: 14 }}>
              {/* GTPL Saathi's own JS (SetUniqueRadioButton) forces this to single-select
                  despite each row looking like an independent checkbox — confirmed live
                  2026-08-26, so this is a real radio group, not a multi-select bug. */}
              <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 6 }}>Broadcaster (pick one)</label>
              <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: 10 }}>
                {options.broadcasters.map((b) => (
                  <label key={b.id} style={{ display: 'block', padding: '4px 6px', fontSize: '.82rem', cursor: 'pointer' }}>
                    <input type="radio" checked={sel.broadcasterName === b.label} onChange={() => { setSel((s) => ({ ...s, broadcasterName: b.label })); resetDownstream(); }} style={{ marginRight: 8 }} />
                    {b.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {sel.setupType === 'A' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 6 }}>Channels</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 8 }}>
                <input className="form-control" style={inp} placeholder="Search channel…" value={channelFilters.search} onChange={(e) => setChannelFilters((f) => ({ ...f, search: e.target.value }))} />
                {channelResults?.filterOptions?.genres?.length > 0 && (
                  <select className="form-control" style={inp} value={channelFilters.genre} onChange={(e) => setChannelFilters((f) => ({ ...f, genre: e.target.value }))}>
                    {channelResults.filterOptions.genres.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                )}
                {channelResults?.filterOptions?.languages?.length > 0 && (
                  <select className="form-control" style={inp} value={channelFilters.language} onChange={(e) => setChannelFilters((f) => ({ ...f, language: e.target.value }))}>
                    {channelResults.filterOptions.languages.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                )}
                {channelResults?.filterOptions?.sdhd?.length > 0 && (
                  <select className="form-control" style={inp} value={channelFilters.sdhd} onChange={(e) => setChannelFilters((f) => ({ ...f, sdhd: e.target.value }))}>
                    {channelResults.filterOptions.sdhd.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                )}
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleLoadChannels} disabled={channelsLoading} style={{ marginBottom: 10 }}>
                {channelsLoading ? 'Loading…' : 'Load Channels'}
              </button>

              {sel.channelNames.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  {sel.channelNames.map((n) => (
                    <span key={n} style={chipStyle(true)} onClick={() => { toggleChannel(n); resetDownstream(); }}>
                      {n} <X size={12} />
                    </span>
                  ))}
                </div>
              )}

              {channelResults && (
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: 10 }}>
                  {channelResults.channels.length === 0 && <div style={{ fontSize: '.82rem', color: 'var(--c-6b7280)' }}>No channels matched.</div>}
                  {channelResults.channels.map((c) => (
                    <label key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 6px', fontSize: '.82rem', cursor: 'pointer' }}>
                      <span><input type="checkbox" checked={sel.channelNames.includes(c.label)} onChange={() => { toggleChannel(c.label); resetDownstream(); }} style={{ marginRight: 8 }} />{c.label}</span>
                      {c.price != null && <span style={{ color: 'var(--c-6b7280)' }}>₹{c.price}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={handlePreview} disabled={previewing || !canPlaceOrder}>
              {previewing ? 'Loading pricing…' : 'Preview'}
            </button>
            <button type="button" className="btn btn-primary" onClick={handlePlace} disabled={placing || !preview || !canPlaceOrder}>
              <ShoppingCart size={14} style={{ marginRight: 6 }} />
              {placing ? 'Placing…' : 'Place Order (spends real money)'}
            </button>
          </div>

          {previewError && (
            <div style={{ marginTop: 12, background: 'var(--cb-fef2f2)', border: '1px solid var(--cb-fecaca)', borderRadius: 8, padding: '10px 14px', fontSize: '.85rem', color: 'var(--c-991b1b)' }}>
              {previewError}
            </div>
          )}

          {preview && (
            <div style={{ marginTop: 12, background: 'var(--cb-eff6ff)', border: '1px solid var(--cb-bfdbfe)', borderRadius: 8, padding: '12px 16px', fontSize: '.85rem', color: 'var(--c-1e3a8a)' }}>
              <strong>Pricing (from GTPL Saathi)</strong>
              <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'auto auto', gap: '2px 16px' }}>
                {preview.pricing.retailPrice != null && <><span>Retail Price</span><span>₹{preview.pricing.retailPrice}</span></>}
                {preview.pricing.gst != null && <><span>GST</span><span>₹{preview.pricing.gst}</span></>}
                {preview.pricing.totalRetailPrice != null && <><span>Total Retail Price</span><span>₹{preview.pricing.totalRetailPrice}</span></>}
                {preview.pricing.walletDeduction != null && <><strong>Wallet Deduction</strong><strong>₹{preview.pricing.walletDeduction}</strong></>}
              </div>
              {preview.pricing.retailPrice == null && preview.pricing.walletDeduction == null && (
                <div style={{ marginTop: 6, fontSize: '.78rem' }}>GTPL didn't show a pricing breakdown for this selection — verify the amount on gtplsaathi.com before placing.</div>
              )}
            </div>
          )}

          {placeResult && (
            <div style={{
              marginTop: 12, borderRadius: 8, padding: '12px 16px', fontSize: '.85rem',
              background: placeResult.ok === false ? (placeResult.unconfirmed ? 'var(--cb-fffbeb)' : 'var(--cb-fef2f2)') : 'var(--cb-f0fdf4)',
              border: `1px solid ${placeResult.ok === false ? (placeResult.unconfirmed ? 'var(--cb-fde68a)' : 'var(--cb-fecaca)') : 'var(--cb-bbf7d0)'}`,
              color: placeResult.ok === false ? (placeResult.unconfirmed ? 'var(--c-92400e)' : 'var(--c-991b1b)') : 'var(--c-166534)',
            }}>
              <strong>{placeResult.ok === false ? (placeResult.unconfirmed ? 'Unconfirmed' : 'Order Failed') : 'Order Placed'}</strong>
              {placeResult.message && <div style={{ marginTop: 4 }}>{placeResult.message}</div>}
              {placeResult.ok === false && !placeResult.unconfirmed && (
                <div style={{ marginTop: 6, fontSize: '.78rem', color: 'var(--c-b91c1c)' }}>An admin has been notified on WhatsApp.</div>
              )}
              {placeResult.ok !== false && (
                <div style={{ marginTop: 6, fontSize: '.78rem' }}>Verify this on gtplsaathi.com directly — this is a newly-built path, confirm the order really landed.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Action Menu ─────────────────────────────────────────────────────────────
const ACTIONS = [
  { key: 'renew',           label: 'Renew',            icon: RefreshCw },
  { key: 'collect',         label: 'Collect Payment',  icon: Wallet },
  { key: 'edit',            label: 'Customer Details', icon: Edit2 },
  { key: 'stats',           label: 'Usage Stats',      icon: Activity },
  { key: 'subscription',    label: 'Subscription',     icon: Tv },
  { key: 'addons',          label: 'Addons',           icon: PackagePlus },
  { key: 'balance_history', label: 'Balance History',  icon: History },
  { key: 'app_login',       label: 'App Login',        icon: KeyRound },
  { key: 'hardware',        label: 'Hardware Details', icon: Cpu },
  { key: 'complaints',      label: 'Complaints',       icon: MessageSquare },
  { key: 'history',         label: 'Change History',   icon: History },
  { key: 'link_account',    label: 'Link Account',     icon: Link2 },
  { key: 'linked_catv',     label: 'Linked CableTV',   icon: Tv },
  { key: 'documents',       label: 'Documents',        icon: FileText },
  { key: 'imbs',            label: 'Netzur Sync',        icon: ExternalLink },
  { key: 'gtpl_saathi',     label: 'GTPL Saathi',      icon: ShoppingCart },
];

// Icon tint group per action (see customer-refresh.css)
const RAIL_GROUP = { renew: 'billing', collect: 'billing', balance_history: 'billing', edit: 'customer', app_login: 'customer', documents: 'customer', link_account: 'customer', linked_catv: 'customer', stats: 'service', subscription: 'service', addons: 'service', hardware: 'tech', imbs: 'tech', gtpl_saathi: 'tech', complaints: 'support', history: 'support' };

// Numbered section heading for the Customer Detail page (order = operator priority).
function SecHead({ n, icon: Icon, title }) {
  return (
    <h2 className="cd-sec-h"><span className="n" aria-hidden="true">{n}</span><Icon size={15} aria-hidden="true" /> {title}</h2>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CustomerDetailPage() {
  useDarkReady();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAdmin, hasModuleRight } = useAuth();
  const canEditPayment = hasModuleRight('payments', 'edit');
  const canTransferPayment = hasModuleRight('payments', 'transferService');
  const canDeletePayment = hasModuleRight('payments', 'delete');
  const canRaiseComplaint = hasModuleRight('complaints', 'add');
  const canSearchGtpl = hasModuleRight('gtplSaathi', 'search');
  const canApplyGtplOrder = hasModuleRight('gtplSaathi', 'renew');
  const canSuspendGtplActive = hasModuleRight('gtplSaathi', 'suspendActive');
  const canPlaceGtplOrder = hasModuleRight('gtplSaathi', 'placeOrder');
  const canRaiseGtplTicket = hasModuleRight('gtplSaathi', 'raiseTicket');
  const canViewGtplTickets = hasModuleRight('gtplSaathi', 'viewTickets');
  const canModifyGtpl = hasModuleRight('gtplSaathi', 'modify');
  const canLongTermSwapGtpl = hasModuleRight('gtplSaathi', 'longTermSwap');
  // The tab itself was never gated by the 'view' right — only the actions inside it were,
  // so anyone without any GTPL Saathi rights (e.g. co-admin) still saw the tab, its headers,
  // and "you don't have permission" placeholders. Hide the whole tab without 'view'.
  const canViewGtplSaathi = hasModuleRight('gtplSaathi', 'view');
  const canViewChangeHistory = hasModuleRight('changeLedger', 'view');
  // App View (tablet/mobile bottom-tab shell) is tight on width — the action sidebar drops its
  // text labels down to icon-only there so the panel next to it gets more room to show details.
  const { viewMode } = useViewMode();
  // viewMode is a manual, persisted-per-device choice (see useViewMode's own comment on why it
  // doesn't keep watching resize) — but that means a real phone stuck on 'web' (stale preference,
  // an accidental toggle tap, a synced profile) would otherwise always get this page's desktop
  // layouts (fixed-width tab sidebar, plain tables) no matter how narrow the screen actually is.
  // This tracks the live viewport as a second, independent signal so those layouts still switch
  // to their phone-safe form below 640px even while viewMode itself stays 'web'.
  const [isNarrowScreen, setIsNarrowScreen] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsNarrowScreen(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isAppView = viewMode === 'app' || isNarrowScreen;
  const actionPanelRef = useRef(null);
  // Set by CustomersPage.js when it links here, so "Back to Customers" returns to whatever
  // filtered view (e.g. ?customerType=catv) the admin actually came from, instead of always
  // resetting to the unfiltered list.
  const backTo = location.state?.from || '/customers';

  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAction, setActiveAction] = useState(searchParams.get('tab') || 'collect');
  const [infoOpen, setInfoOpen] = useState(false);
  // Open an action's panel (in the sections list under the page) and bring it into view.
  const openAction = (key) => { setActiveAction(key); setTimeout(() => actionPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); };
  // "Customer is over 90 days" pop-up — once when the customer page opens and once when the Collect /
  // Renew panel opens (each key at most once per visit).
  const [over90Open, setOver90Open] = useState(false);
  const over90Shown = useRef(new Set());
  const [editingPayment, setEditingPayment] = useState(null);
  const [retracking, setRetracking] = useState(false);
  const [togglingGtplSuspend, setTogglingGtplSuspend] = useState(false);
  const [savingStbFree, setSavingStbFree] = useState(false);
  const [retrackResult, setRetrackResult] = useState(null);
  const [imbsSub, setImbsSub] = useState(null);
  const [hwOltFetching, setHwOltFetching] = useState(false);
  const [hwOltMatch, setHwOltMatch] = useState(null);
  const [hwSaving, setHwSaving] = useState(false);
  const [tr069Loading, setTr069Loading] = useState(false);
  const [tr069NoConfig, setTr069NoConfig] = useState(false);
  const [hwMacSaving, setHwMacSaving] = useState(false);
  const [liveRate, setLiveRate] = useState(null);
  const [bbCloseOpen, setBbCloseOpen] = useState(false);
  const [bbCloseReason, setBbCloseReason] = useState('');
  const [bbStatusSaving, setBbStatusSaving] = useState(false);
  const [ctvCloseOpen, setCtvCloseOpen] = useState(false);
  const [ctvCloseReason, setCtvCloseReason] = useState('');
  const [ctvStatusSaving, setCtvStatusSaving] = useState(false);
  // Date of inactivation on both Close Connection modals (only one is open at a time):
  // "yyyy-MM-dd", today by default, can be backdated.
  const [closeDate, setCloseDate] = useState(todayInput());
  // GTPL Saathi disconnect-ticket automation (Settings → GTPL Saathi) — offered inline in the
  // Close CATV Connection modal below when enabled and this is a regular-billing GTPL account.
  const [gtplTicketAutomation, setGtplTicketAutomation] = useState(false);
  const [ctvTicketMode, setCtvTicketMode] = useState('instant');
  const [ctvTicketDate, setCtvTicketDate] = useState(firstOfNextMonth());
  const [ctvTicketTime, setCtvTicketTime] = useState(''); // optional; blank = anytime that day (backend default, midnight)
  const [pendingReason, setPendingReason] = useState(null);
  const [reasonLabels, setReasonLabels] = useState({});

  // Guards against a stale response overwriting the page: the route reuses this same
  // component instance across different customers (App.js just updates the :id param), so
  // jumping from customer A to B via the topbar search does not unmount/remount — a slow
  // in-flight request for A can resolve after B has already loaded and clobber it with A's
  // data. Every setState below is gated on this still being the most-recently-requested id.
  const latestRequestIdRef = useRef(id);

  useEffect(() => {
    gtplSaathiAPI.getDisconnectAutomation()
      .then(({ data }) => setGtplTicketAutomation(!!data.data?.enabled))
      .catch(() => {}); // staff without GTPL rights simply never see the ticket prompt
  }, []);

  const loadData = async () => {
    const requestId = id;
    const isStale = () => latestRequestIdRef.current !== requestId;
    try {
      const [cd, pd, co] = await Promise.all([
        customerAPI.getById(id),
        customerAPI.getPayments(id),
        customerAPI.getComplaints(id),
      ]);
      if (isStale()) return;
      const cust = cd.data.data;
      setCustomer(cust);
      setPayments(pd.data.data);
      setComplaints(co.data.data);
      // Load Netzur subscriber data if linked
      if (cust.imbs?.subscriberId) {
        imbsAPI.getSubscriber(cust.imbs.subscriberId)
          .then(r => {
            if (isStale()) return;
            const raw = r.data?.data;
            setImbsSub(raw?.data || raw || null);
          })
          .catch(() => { if (!isStale()) setImbsSub(null); });
      } else {
        setImbsSub(null);
      }
      // Auto-refresh hardware details from the OLT every time this page opens, so the
      // router model/serial/PON shown here don't go stale between manual "Fetch OLT"
      // clicks. Silent (no toast) and only writes back when the OLT actually reports
      // something different — an unreachable OLT just leaves the stored values as-is.
      if (cust.customerType === 'broadband' && cust.broadband?.username) {
        oltAPI.matchByUsername(cust.broadband.username)
          .then(({ data }) => {
            if (isStale()) return;
            const onu = data.data;
            if (!onu) return;
            const changed =
              (onu.serialNumber && onu.serialNumber !== cust.broadband?.routerSerial) ||
              (onu.model && onu.model !== cust.broadband?.routerModel) ||
              (onu.ponPort != null && String(onu.ponPort) !== cust.broadband?.ponNumber);
            if (!changed) return;
            return customerAPI.update(cust._id, {
              broadband: {
                ...cust.broadband,
                routerSerial: onu.serialNumber || cust.broadband?.routerSerial,
                routerModel: onu.model || cust.broadband?.routerModel,
                ponNumber: onu.ponPort != null ? String(onu.ponPort) : cust.broadband?.ponNumber,
              },
            }).then(({ data }) => { if (!isStale()) setCustomer(data.data); });
          })
          .catch(() => {}); // OLT unreachable — keep whatever's already on file
      }
    } catch {
      if (!isStale()) toast.error('Failed to load customer');
    } finally {
      if (!isStale()) setLoading(false);
    }
    paymentAPI.getCollectionPendingLatest([id])
      .then(({ data }) => { if (!isStale()) setPendingReason(data.data?.[id] || null); })
      .catch(() => { if (!isStale()) setPendingReason(null); });
  };

  useEffect(() => { latestRequestIdRef.current = id; loadData(); }, [id]);

  useEffect(() => {
    agentAppAPI.getCollectionPendingReasons().then(({ data }) => {
      const map = {};
      (data.data || []).forEach(r => { map[r.key] = r.label; });
      setReasonLabels(map);
    }).catch(() => {});
  }, []);

  // Live NAS rate (used here just for Framed IPv4 on the Overview hardware grid — the fuller
  // Download/Upload/session view lives in BroadbandStatsPanel under the Usage Stats tab).
  useEffect(() => {
    const username = customer?.broadband?.username;
    if (!username) return;
    let mounted = true;
    const fetch = () => nasAPI.getSubscriberLive(username).then(r => { if (mounted) setLiveRate(r.data.data); }).catch(() => {});
    fetch();
    const timer = setInterval(fetch, 5_000);
    return () => { mounted = false; clearInterval(timer); };
  }, [customer?.broadband?.username]);

  // Collect Payment doesn't exist for a broadband-only customer with nothing due (amount lives
  // in their Renew tab) — bounce a stale ?tab=collect link to Renew instead of an empty tab.
  useEffect(() => {
    if (customer?.customerType === 'broadband' && (customer.outstandingBalance ?? 0) <= 0 && activeAction === 'collect') setActiveAction('renew');
  }, [customer, activeAction]);

  useEffect(() => {
    if (!customer?.expiredOver90Days) return;
    const keys = [`${customer._id}:page`];
    if (activeAction === 'collect' || activeAction === 'renew') keys.push(`${customer._id}:${activeAction}`);
    const fresh = keys.filter(k => !over90Shown.current.has(k));
    if (!fresh.length) return;
    fresh.forEach(k => over90Shown.current.add(k));
    setOver90Open(true);
  }, [customer, activeAction]);

  const handlePaymentSaved = (updated) => {
    setPayments(prev => prev.map(p => p._id === updated._id ? updated : p));
    setEditingPayment(null);
  };

  const handleDeleteCustomer = async () => {
    if (!window.confirm(`Permanently delete ${customer.name}? This cannot be undone.`)) return;
    try {
      await customerAPI.delete(customer._id);
      toast.success('Customer deleted');
      navigate(backTo);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  // App View broadband: same one-shot session drop as ImbsKickButton, and a jump to this
  // customer's ONU page (the ONU list's detail view) using the OLT match for their PPPoE username.
  const [kicking, setKicking] = useState(false);
  const handleKick = async () => {
    if (!window.confirm("Force-disconnect this subscriber's live session in Netzur? Their router will reconnect on its own if the account is still enabled.")) return;
    setKicking(true);
    try {
      await imbsAPI.kickSession(customer._id);
      toast.success('Session disconnected in Netzur');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to disconnect session in Netzur');
    } finally {
      setKicking(false);
    }
  };
  const openOnu = async () => {
    try {
      const m = hwOltMatch || (await oltAPI.matchByUsername(customer.broadband.username)).data.data;
      const p = m?.gponId?.match(/^GPON(\d+)\/(\d+):(\d+)$/);
      if (!p) { toast(`No ONU matched "${customer.broadband.username}" — set its label on the OLT page first`, { icon: '⚠️' }); return; }
      navigate(`/technical/onus/${p[1]}/${p[2]}/${p[3]}`);
    } catch {
      toast.error('Could not reach OLT');
    }
  };

  const handleRetrack = async () => {
    const stb = customer.catv?.setTopBoxSerial;
    if (!stb) { toast.error('No STB serial number on this customer'); return; }
    setRetracking(true);
    setRetrackResult(null);
    try {
      const { data } = await gtplAPI.retrack(stb);
      setRetrackResult({ message: data.data?.message || 'Done', ok: data.data?.ok ?? true, stb });
    } catch (err) {
      setRetrackResult({ message: err.response?.data?.message || err.message || 'Retrack failed', ok: false, stb });
    } finally { setRetracking(false); }
  };

  // "STB - Free to use basis" tick in the CableTV Hardware Details card. The PUT replaces the
  // whole catv sub-object (and the body sanitizer drops dotted keys), so spread it back in —
  // same as the Hardware Details panel's save.
  const handleToggleStbFree = async (checked) => {
    setSavingStbFree(true);
    try {
      const { data } = await customerAPI.update(customer._id, {
        catv: { ...customer.catv, package: customer.catv?.package?._id || customer.catv?.package, stbFreeToUse: checked },
      });
      setCustomer(data.data);
      toast.success(checked ? 'Marked STB as free to use' : 'STB free-to-use removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSavingStbFree(false);
    }
  };

  // Quick Suspend/Activate toggle in the Hardware Details card below — the common case (flip
  // for non-payment / payment received) without digging into the full Suspend/Activate form
  // on the GTPL Saathi tab (still there for less common reasons: Vacation, Cheque Bounce, STB
  // Block, etc). Performs the real call on gtplsaathi.com AND updates Customer.catv.gtplSuspended
  // together — see that field's own comment on Customer.js for why they're kept as one action.
  const handleToggleGtplSuspend = async () => {
    const stb = customer.catv?.setTopBoxSerial;
    const accountNumber = customer.catv?.accountNumber;
    if (!stb && !accountNumber) { toast.error('No STB serial or account number on this customer'); return; }
    const nextAction = customer.catv?.gtplSuspended ? 'ACTIVE' : 'SUSPEND';
    setTogglingGtplSuspend(true);
    try {
      const { data } = await gtplSaathiAPI.suspendActive({
        stb, accountNumber, action: nextAction,
        reason: nextAction === 'ACTIVE' ? 'Payment Received' : 'Payment Due',
        customerId: customer._id,
      });
      if (data.queued) {
        toast(data.message, { icon: '⏳' });
      } else {
        toast.success(nextAction === 'ACTIVE' ? 'Activated on GTPL Saathi' : 'Suspended on GTPL Saathi');
        setCustomer(prev => ({ ...prev, catv: { ...prev.catv, gtplSuspended: nextAction === 'SUSPEND' } }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Suspend/Activate failed');
    } finally {
      setTogglingGtplSuspend(false);
    }
  };

  const fetchHwFromOLT = async () => {
    const username = customer?.broadband?.username;
    if (!username) return;
    setHwOltFetching(true);
    try {
      const { data } = await oltAPI.matchByUsername(username);
      const onu = data.data;
      if (!onu) { toast(`No ONU matched "${username}" — set its label on the OLT page first`, { icon: '⚠️' }); return; }
      setHwOltMatch(onu);
      toast.success('OLT data loaded — click Save to apply');
    } catch {
      toast.error('Could not reach OLT');
    } finally {
      setHwOltFetching(false);
    }
  };

  const openTr069 = async () => {
    setTr069Loading(true);
    try {
      const { data } = await acsAPI.getDeviceByCustomer(customer._id);
      navigate(`/acs?serial=${encodeURIComponent(data.data.serial)}`);
    } catch (err) {
      if (err.response?.status === 404) {
        setTr069NoConfig(true);
      } else {
        toast.error('Could not reach ACS');
      }
    } finally {
      setTr069Loading(false);
    }
  };

  const toggleMacCheck = async (checked) => {
    if (!customer.imbs?.subscriberId) return;
    setHwMacSaving(true);
    try {
      const { data } = await imbsAPI.setMacCheck(customer._id, checked);
      setCustomer(data.data);
      toast.success(`MAC Check ${checked ? 'enabled' : 'disabled'} in Netzur`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update MAC Check in Netzur');
    } finally { setHwMacSaving(false); }
  };

  const saveHwFromOLT = async () => {
    if (!hwOltMatch) return;
    setHwSaving(true);
    try {
      const patch = {
        broadband: {
          ...customer.broadband,
          routerSerial: hwOltMatch.serialNumber || customer.broadband?.routerSerial,
          routerModel:  hwOltMatch.model         || customer.broadband?.routerModel,
          ponNumber:    hwOltMatch.ponPort != null ? String(hwOltMatch.ponPort) : customer.broadband?.ponNumber,
        },
      };
      const { data } = await customerAPI.update(customer._id, patch);
      setCustomer(data.data);
      setHwOltMatch(null);
      toast.success('Hardware details saved from OLT');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setHwSaving(false);
    }
  };

  // The GTPL disconnect-ticket choice is offered for any GTPL-linked account (owner 2026-10-10:
  // DTD customers must get the same choices as Regular ones). durationType defaults to 'regular'
  // when unset (paymentService's own convention, see billing.durationType). DTD / AMP start on
  // "Don't raise a ticket": the cron already raises theirs when the plan expires unrenewed, so
  // "now" as the default would double up.
  const ctvTicketEligible = gtplTicketAutomation && !!customer?.catv?.accountNumber;
  const ctvIsRegular = (customer?.billing?.durationType || 'regular') === 'regular';

  const handleReopenCatv = async () => {
    setCtvStatusSaving(true);
    try {
      const { data } = await customerAPI.updateCatvStatus(customer._id, { isActive: true });
      setCustomer(data.data);
      toast.success(data.cancelledTickets
        ? `CATV connection reactivated — pending GTPL disconnect ticket cancelled`
        : 'CATV connection reactivated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reactivate connection');
    } finally {
      setCtvStatusSaving(false);
    }
  };

  const handleCloseCatv = async () => {
    if (!ctvCloseReason.trim()) { toast.error('A reason is required to close the connection'); return; }
    if (ctvTicketEligible && ctvTicketMode === 'schedule' && !ctvTicketDate) {
      toast.error('Pick a date for the scheduled disconnect ticket'); return;
    }
    setCtvStatusSaving(true);
    try {
      const { data } = await customerAPI.updateCatvStatus(customer._id, { isActive: false, reason: ctvCloseReason.trim(), closedOn: closeDate });
      setCustomer(data.data);
      toast.success('CATV connection closed');
      setCtvCloseOpen(false);
      setCtvCloseReason('');

      if (ctvTicketEligible && ctvTicketMode !== 'none') {
        try {
          const { data: ticketRes } = await gtplSaathiAPI.createCustomerTicket({
            customerId: customer._id,
            complaintType: '382',
            mode: ctvTicketMode,
            scheduledDate: ctvTicketMode === 'schedule'
              ? (ctvTicketTime ? `${ctvTicketDate}T${ctvTicketTime}` : ctvTicketDate)
              : undefined,
          });
          toast.success(ctvTicketMode === 'instant'
            ? `GTPL disconnect ticket raised — Request No ${ticketRes.data.ticketNumber}`
            : `GTPL disconnect ticket scheduled for ${fmtDate(ctvTicketDate)}${ctvTicketTime ? ` at ${ctvTicketTime}` : ''}`);
        } catch (ticketErr) {
          toast.error(ticketErr.response?.data?.message || 'Failed to raise GTPL disconnect ticket');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to close connection');
    } finally {
      setCtvStatusSaving(false);
    }
  };

  const handleReopenBroadband = async () => {
    setBbStatusSaving(true);
    try {
      const { data } = await customerAPI.updateBroadbandStatus(customer._id, { isActive: true });
      setCustomer(data.data);
      toast.success('Broadband connection reactivated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reactivate connection');
    } finally {
      setBbStatusSaving(false);
    }
  };

  const handleCloseBroadband = async () => {
    if (!bbCloseReason.trim()) { toast.error('A reason is required to close the connection'); return; }
    setBbStatusSaving(true);
    try {
      const { data } = await customerAPI.updateBroadbandStatus(customer._id, { isActive: false, reason: bbCloseReason.trim(), closedOn: closeDate });
      setCustomer(data.data);
      toast.success('Broadband connection closed');
      setBbCloseOpen(false);
      setBbCloseReason('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to close connection');
    } finally {
      setBbStatusSaving(false);
    }
  };

  if (loading) return <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>;
  if (!customer) return <div className="empty-state"><h3>Customer not found</h3></div>;

  // Netzur package determines active status when linked
  const imbsLinked   = !!customer.imbs?.subscriberId;
  const imbsIsActive = !!customer.imbs?.isActive;
  const imbsPackage  = imbsSub?.package || imbsSub?.package_name || null;
  const isActive     = imbsLinked ? imbsIsActive : customer.status === 'active';

  const { online: imbsOnline, blocked: imbsBlocked, reason: imbsBlockReason, enabled: imbsEnabled } = getImbsStatus(imbsSub);

  const balance = customer.outstandingBalance ?? 0;
  const lastPayment = payments[0] || null;
  const lastBillDate = lastPayment?.paymentDate || customer.catv?.expiryDate || customer.broadband?.expiryDate || null;

  const hasBB   = customer.customerType === 'broadband';
  const hasCatv = customer.customerType === 'catv';
  // Broadband-only customers get Collect Payment only while they owe something (a bill raised
  // with Renew → Generate, collected later); otherwise their amount lives in the Renew tab.
  const hideCollect = hasBB && balance <= 0;
  // Connection Close toggle applies to any customer with a broadband service, including combo ('both').
  const hasBroadbandService = hasBB || customer.customerType === 'both';
  const hasCatvService = hasCatv || customer.customerType === 'both';
  // broadband.isActive only flips true via a renewal payment (paymentService.collectPayment)
  // or the Close/Reopen Connection toggle — a freshly created customer stays Inactive here
  // until their first renewal, instead of showing Active just because customer.status
  // (account-level, defaults 'active' at creation) said so.
  const bbActive = !!customer.broadband?.isActive;

  const visibleActions = ACTIONS.filter(a => !(hasCatv && a.key === 'imbs') && !(hideCollect && a.key === 'collect') && !(!hasBB && a.key === 'stats') && !(!hasBB && !hasCatv && a.key === 'subscription') && !(!hasBroadbandService && a.key === 'addons') && !(!hasBroadbandService && a.key === 'linked_catv') && !(hasBroadbandService && a.key === 'link_account') && !(!hasCatvService && a.key === 'gtpl_saathi') && !(!canViewGtplSaathi && a.key === 'gtpl_saathi') && !(!canViewChangeHistory && a.key === 'history'));

  // ?tab=<action> deep links: complaints / payment history are tabs, everything else opens as a task screen.
  const mobileDeepLink = (() => {
    const t = searchParams.get('tab');
    if (!t) return {};
    if (t === 'complaints') return { tab: 'complaints' };
    if (t === 'balance_history') return { tab: 'payments' };
    return { screen: t };
  })();
  const toggleCatvConnection = () => {
    if (customer.status === 'active') {
      setCtvTicketMode(ctvIsRegular ? 'instant' : 'none');
      setCtvTicketDate(firstOfNextMonth());
      setCloseDate(todayInput());
      setCtvCloseOpen(true);
    } else {
      handleReopenCatv();
    }
  };
  const toggleBbConnection = () => { if (bbActive) { setCloseDate(todayInput()); setBbCloseOpen(true); } else handleReopenBroadband(); };
  // Linked records (a combo customer is two records): shown as cards that jump across.
  const goToCustomer = (cid) => { window.location.href = `/customers/${cid}`; };
  const linkedCards = (() => {
    const la = customer.linkedAccount;
    const out = [];
    if (la && ['catv', 'both'].includes(la.customerType)) out.push({ target: la });
    (customer.broadband?.linkedCatvAccounts || []).forEach(c => out.push({ target: c }));
    const multi = out.length >= 2;
    const list = out.map((o, i) => ({ id: o.target._id, name: o.target.name, expiry: o.target.catv?.expiryDate, label: multi ? `TV${i + 1}` : 'Cable TV' }));
    if (!list.length && customer.linkedViaBroadband) {
      const via = customer.linkedViaBroadband;
      list.push({ id: via.customer._id, name: via.customer.name, label: via.comboSize >= 2 ? `TV ${via.tvNumber} of` : 'Broadband', expiry: via.customer.broadband?.expiryDate });
    } else if (!list.length && customer.linkedAccount) {
      const la2 = customer.linkedAccount;
      list.push({ id: la2._id, name: la2.name, label: la2.customerType === 'catv' ? 'Cable TV' : 'Broadband', expiry: la2.catv?.expiryDate || la2.broadband?.expiryDate });
    }
    return list;
  })();

  // ── Live Bandwidth (Broadband customers only) ──
  const bbLiveNode = hasBB && customer.broadband?.username && (
        <AppFold isAppView={false} title="Live bandwidth" summary={customer.broadband.username} defaultOpen>
          <div style={{ marginBottom: isAppView ? 0 : 12, border: isAppView ? 0 : '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-card)' }}>
            <LiveBandwidthPanel username={customer.broadband.username} showUsername={false} embedded={!isAppView} />
            {!isAppView && <details style={{ borderTop: '1px solid var(--border)' }}>
              <summary style={{ cursor: 'pointer', padding: '10px 16px', fontWeight: 600, fontSize: '.85rem', display: 'flex', alignItems: 'center', gap: 7 }}>
                <BarChart2 size={14} color="var(--primary)" aria-hidden="true" /> Consumption &amp; usage history
              </summary>
              <div style={{ padding: '4px 16px 8px' }}>
                <BroadbandStatsPanel customer={customer} mode="usage" liveRate={liveRate} />
              </div>
            </details>}
          </div>
        </AppFold>
  );
  const bbUsageNode = hasBB && <BroadbandStatsPanel customer={customer} mode="usage" liveRate={liveRate} />;

  // ── Hardware Section (Broadband customers only) ──
  const bbHwNode = hasBB && (
        <AppFold isAppView={false} title="Hardware" defaultOpen
          summary={[customer.broadband?.routerModel, customer.broadband?.ponNumber ? `PON ${customer.broadband.ponNumber}` : null, customer.broadband?.username].filter(Boolean).join(' · ')}>
        <div style={{ background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, marginBottom: isAppView ? 0 : 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 8, padding: '12px 20px', borderBottom: '1px solid var(--cb-e5e7eb)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '.88rem', color: 'var(--c-374151)' }}>
              {!isAppView && <><Cpu size={14} color="var(--accent)" /> Hardware</>}
            </div>
            {customer.broadband?.username && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {imbsLinked && (
                  <>
                    <ImbsDisconnectButton customer={customer} onCustomerUpdate={loadData} liveEnabled={imbsSub ? imbsEnabled : undefined} pill />
                    <ImbsKickButton customer={customer} onCustomerUpdate={loadData} pill />
                  </>
                )}
                <button
                  onClick={() => navigate(`/olt?serial=${encodeURIComponent(customer.broadband.username)}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 6, border: '1px solid var(--primary)',
                    background: 'var(--cb-ffffff)', color: 'var(--primary)',
                    fontSize: '.75rem', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <ExternalLink size={12} /> Linked OLT
                </button>
                <button
                  onClick={openTr069}
                  disabled={tr069Loading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 6, border: '1px solid var(--primary)',
                    background: 'var(--cb-ffffff)', color: 'var(--primary)',
                    fontSize: '.75rem', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Radio size={12} />
                  {tr069Loading ? 'Opening…' : 'TR069'}
                </button>
                <button
                  onClick={fetchHwFromOLT}
                  disabled={hwOltFetching}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 6, border: '1px solid var(--accent)',
                    background: hwOltFetching ? 'var(--cb-f0f9ff)' : 'var(--cb-ffffff)', color: 'var(--accent)',
                    fontSize: '.75rem', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Router size={12} />
                  {hwOltFetching ? 'Fetching…' : 'Fetch OLT'}
                </button>
              </div>
            )}
          </div>
          {hwOltMatch && (
            <div style={{
              margin: '10px 20px 0', padding: '10px 14px', borderRadius: 6,
              background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)',
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, fontSize: '.78rem',
            }}>
              <span style={{ color: 'var(--c-15803d)', fontWeight: 600 }}>✓ ONU matched</span>
              <span>Status: <strong style={{ color: hwOltMatch.status === 'online' ? 'var(--c-15803d)' : 'var(--c-dc2626)' }}>{hwOltMatch.status}</strong></span>
              {hwOltMatch.rxPower != null && <span>Rx: <strong>{hwOltMatch.rxPower} dBm</strong></span>}
              {hwOltMatch.profile && <span>Profile: <strong>{hwOltMatch.profile}</strong></span>}
              {hwOltMatch.serialNumber && <span>Serial: <strong style={{ fontFamily: 'monospace' }}>{hwOltMatch.serialNumber}</strong></span>}
              {hwOltMatch.ponPort != null && <span>PON: <strong>{hwOltMatch.ponPort}</strong></span>}
              {hwOltMatch.distance != null && <span>Distance: <strong>{hwOltMatch.distance} m</strong></span>}
              {hwOltMatch.lastDeregister && <span>Last Deregister: <strong>{hwOltMatch.lastDeregister}</strong></span>}
              {hwOltMatch.deregisterReason && <span>Reason: <strong>{hwOltMatch.deregisterReason}</strong></span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button onClick={() => setHwOltMatch(null)}
                  style={{ background: 'none', border: '1px solid var(--cb-86efac)', borderRadius: 5, color: 'var(--c-6b7280)', padding: '3px 8px', fontSize: '.75rem', cursor: 'pointer' }}>
                  Dismiss
                </button>
                <button onClick={saveHwFromOLT} disabled={hwSaving}
                  style={{ background: 'var(--cb-15803d)', border: 'none', borderRadius: 5, color: 'var(--c-ffffff)', padding: '3px 10px', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Save size={11} />{hwSaving ? 'Saving…' : 'Save to Customer'}
                </button>
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', padding: '14px 20px', gap: 12 }}>
            {[
              { label: 'Router / ONT model', value: hwOltMatch?.model        || customer.broadband?.routerModel,  pending: !!hwOltMatch?.model },
              { label: 'Serial number',      value: hwOltMatch?.serialNumber || customer.broadband?.routerSerial, pending: !!hwOltMatch?.serialNumber },
              { label: 'MAC (live session)', value: liveRate?.online ? liveRate.mac : null },
              { label: 'Framed IPv4',        value: liveRate?.online ? liveRate.address : null },
              { label: 'PON number',         value: hwOltMatch?.ponPort != null ? String(hwOltMatch.ponPort) : customer.broadband?.ponNumber, pending: hwOltMatch?.ponPort != null },
              { label: 'ISP',                value: customer.broadband?.ispName },
            ].map(({ label, value, pending, sub }) => (
              <div key={label} style={{ background: pending ? 'var(--cb-f0fdf4)' : undefined, borderRadius: pending ? 6 : undefined, padding: pending ? '4px 6px' : undefined }}>
                <div style={{ fontSize: '.7rem', color: pending ? 'var(--c-15803d)' : 'var(--c-6b7280)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>{label}{pending ? ' ✦' : ''}</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '.88rem', color: value ? (pending ? 'var(--c-15803d)' : 'var(--c-111111)') : 'var(--c-9ca3af)' }}>{value || '—'}</div>
                {sub && <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', marginTop: 2, fontFamily: 'monospace' }}>{sub}</div>}
              </div>
            ))}
          </div>
        </div>
        </AppFold>
  );

  // ── Hardware History (Broadband customers only) ──
  const bbHistNode = hasBB && (
        <HardwareHistoryPanel customerId={customer._id} />
  );

  // ── Uptime & connection: PPPoE session facts + recent sessions + uptime report (each shown once, here) ──
  const bbSessNode = hasBB && customer.broadband?.username && (isAppView ? <BroadbandStatsPanel customer={customer} mode="sessions" liveRate={liveRate} /> :
            <details open style={{ background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, marginBottom: isAppView ? 0 : 12, padding: '12px 20px 0' }}>
              <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '.88rem', color: 'var(--c-374151)', marginBottom: 12 }}>
                <Wifi size={14} color="var(--accent)" aria-hidden="true" /> PPPoE session &amp; connection
              </summary>
              <BroadbandStatsPanel customer={customer} mode="sessions" liveRate={liveRate} />
            </details>);
  const bbUptimeOnly = hasBB && <UptimePanel customerId={customer._id} />;
  const bbUptimeNode = hasBB && <>{bbSessNode}{bbUptimeOnly}</>;

  // ── Hardware Details (CableTV) ──
  const catvHwNode = hasCatv && (
        <AppFold isAppView={false} title="Hardware details" defaultOpen
          summary={[customer.catv?.stbModel, customer.catv?.setTopBoxSerial, customer.catv?.entityCode, customer.catv?.gtplSuspended ? 'Suspended on GTPL' : null].filter(Boolean).join(' · ')}>
        <div style={{ background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, marginBottom: isAppView ? 0 : 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 8, padding: '12px 20px', borderBottom: '1px solid var(--cb-e5e7eb)' }}>
            {!isAppView ? <span style={{ fontWeight: 700, fontSize: '.88rem', color: 'var(--c-374151)' }}>Hardware Details</span> : <span />}
            {canSuspendGtplActive && (customer.catv?.setTopBoxSerial || customer.catv?.accountNumber) && (
              <div
                onClick={() => !togglingGtplSuspend && (!isAppView || window.confirm(customer.catv?.gtplSuspended ? 'Activate this box on GTPL Saathi?' : 'Suspend this box on GTPL Saathi?')) && handleToggleGtplSuspend()}
                role="switch" aria-checked={!customer.catv?.gtplSuspended}
                title="Flip this STB's status on GTPL Saathi"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: togglingGtplSuspend ? 'default' : 'pointer', userSelect: 'none', opacity: togglingGtplSuspend ? .6 : 1 }}
              >
                <span style={{
                  width: 36, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0,
                  background: customer.catv?.gtplSuspended ? 'var(--cb-d1d5db)' : 'var(--cb-16a34a)', transition: 'background .15s',
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: customer.catv?.gtplSuspended ? 2 : 18, width: 16, height: 16,
                    borderRadius: '50%', background: 'var(--cb-ffffff)', transition: 'left .15s',
                    boxShadow: '0 1px 2px rgba(0,0,0,.25)',
                  }} />
                </span>
                <span style={{ fontSize: '.78rem', fontWeight: 700, color: customer.catv?.gtplSuspended ? 'var(--c-b91c1c)' : 'var(--c-15803d)' }}>
                  {togglingGtplSuspend ? 'Submitting…' : (customer.catv?.gtplSuspended ? 'Suspended on GTPL' : 'Active on GTPL')}
                </span>
              </div>
            )}
          </div>
          {isAppView ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px', padding: '12px 14px' }}>
              {[['STB name', customer.catv?.stbModel], ['STB', customer.catv?.setTopBoxSerial], ['Entity code', customer.catv?.entityCode], ['Account number', customer.catv?.accountNumber]].map(([l, v]) => (
                <div key={l} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>{l}</div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '.86rem', overflowWrap: 'anywhere' }}>{v || '—'}</div>
                </div>
              ))}
            </div>
          ) : (
          <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '.88rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                {['STB NAME', 'STB', 'ENTITY CODE', 'ACCOUNT NUMBER'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', background: 'var(--cb-f9fafb)', fontSize: '.8rem', letterSpacing: '.04em', wordBreak: 'break-word' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '12px 12px', fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{customer.catv?.stbModel || '—'}</td>
                <td style={{ padding: '12px 12px', fontFamily: 'monospace', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{customer.catv?.setTopBoxSerial || '—'}</td>
                <td style={{ padding: '12px 12px', fontFamily: 'monospace', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{customer.catv?.entityCode || '—'}</td>
                <td style={{ padding: '12px 12px', fontFamily: 'monospace', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{customer.catv?.accountNumber || '—'}</td>
              </tr>
            </tbody>
          </table>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderTop: '1px solid var(--cb-e5e7eb)', fontSize: '.85rem', fontWeight: 600, color: 'var(--c-374151)', cursor: savingStbFree ? 'default' : 'pointer', opacity: savingStbFree ? .6 : 1 }}>
            <input type="checkbox" checked={!!customer.catv?.stbFreeToUse} disabled={savingStbFree}
              onChange={e => handleToggleStbFree(e.target.checked)} />
            STB - Free to use basis
            <span style={{ fontWeight: 400, fontSize: '.75rem', color: 'var(--text-muted)' }}>(online recharge: 3 / 6 / 12 months only)</span>
          </label>
        </div>
        </AppFold>
  );

  // One place that maps an action key to its panel; desktop shows it beside the rail, mobile in a full-screen task view.
  const panelFor = (key) => (
    <>
      {key === 'edit' && (
        <EditCustomerPanel
          customer={customer}
          isAdmin={isAdmin}
          onSuccess={(updated) => { setCustomer(updated); toast.success('Saved'); }}
        />
      )}
      {key === 'renew' && (
        <RenewPanel
          customer={customer}
          payments={payments}
          onSuccess={loadData}
          onCustomerUpdate={(updated) => setCustomer(updated)}
        />
      )}
      {key === 'collect' && !hideCollect && (
        <CollectPaymentPanel
          customer={customer}
          payments={payments}
          onSuccess={loadData}
        />
      )}
      {key === 'stats' && hasBB && (
        <BroadbandStatsPanel
          customer={customer}
        />
      )}
      {key === 'subscription' && hasCatv && (
        <SubscriptionPanel
          customer={customer}
          onSuccess={loadData}
        />
      )}
      {key === 'subscription' && hasBB && (
        <BroadbandSubscriptionPanel
          customer={customer}
          onSuccess={loadData}
        />
      )}
      {key === 'addons' && hasBroadbandService && (
        <AddonsPanel
          customer={customer}
          onSuccess={loadData}
        />
      )}
      {key === 'balance_history' && (
        <BalanceHistoryPanel
          payments={payments}
          customer={customer}
          canEditPayment={canEditPayment}
          canTransferPayment={canTransferPayment}
          canDeletePayment={canDeletePayment}
          onEditPayment={setEditingPayment}
          onSuccess={loadData}
          isAppView={isAppView}
        />
      )}
      {key === 'hardware' && (
        <HardwarePanel customer={customer} onSaved={(updated) => setCustomer(updated)} />
      )}
      {key === 'complaints' && (
        <ComplaintsPanel complaints={complaints} isAppView={isAppView} customer={customer} onSuccess={loadData} canRaise={canRaiseComplaint} />
      )}
      {key === 'history' && (
        <CustomerHistoryPanel customer={customer} isAdmin={isAdmin} />
      )}
      {key === 'link_account' && (
        <LinkAccountPanel customer={customer} onSuccess={loadData} />
      )}
      {key === 'linked_catv' && (
        <LinkedCableTvPanel customer={customer} onSuccess={loadData} />
      )}
      {key === 'app_login' && (
        <AppLoginPanel customer={customer} onSuccess={loadData} />
      )}
      {key === 'documents' && (
        <DocumentsPanel customer={customer} onCustomerUpdate={loadData} />
      )}
      {key === 'imbs' && (
        <IMBSPanel customer={customer} onCustomerUpdate={loadData} />
      )}
      {key === 'gtpl_saathi' && canViewGtplSaathi && (
        <GtplSaathiPanel customer={customer} canSearch={canSearchGtpl} canApplyOrder={canApplyGtplOrder} canSuspendActive={canSuspendGtplActive} canPlaceOrder={canPlaceGtplOrder} canRaiseTicket={canRaiseGtplTicket} canViewLiveStatus={canViewGtplTickets} canModify={canModifyGtpl} canLongTermSwap={canLongTermSwapGtpl} />
      )}
    </>
  );

  return (
    <div style={{ background: 'var(--cb-f3f4f6)', minHeight: '100vh', padding: '0 0 40px' }}>
      {over90Open && <Over90DaysNotice info={customer?.expiredOver90Days} onClose={() => setOver90Open(false)} />}

      {/* ── Back bar ── */}
      {!isAppView && <div style={{ padding: '10px 0 0' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(backTo)}
          style={{ marginBottom: 12, fontSize: '.82rem' }}>
          <ArrowLeft size={13} /> Back to Customers
        </button>
      </div>}

      {isAppView && (
        <CustomerMobile
          customer={customer} balance={balance} payments={payments} complaints={complaints} isActive={isActive}
          hasCatvService={hasCatvService} hasBroadbandService={hasBroadbandService} hasBB={hasBB} hasCatv={hasCatv}
          hideCollect={hideCollect} bbActive={bbActive} isAdmin={isAdmin}
          allowed={new Set(visibleActions.map(a => a.key))}
          labels={Object.fromEntries(ACTIONS.map(a => [a.key, a.label]))}
          imbs={{ linked: imbsLinked, loading: imbsLinked && imbsSub === null, blocked: imbsBlocked, online: imbsOnline, reason: imbsBlockReason, packageName: imbsPackage?.name || (typeof imbsPackage === 'string' ? imbsPackage : null) }}
          pendingText={pendingReason ? `${reasonLabels[pendingReason.reason] || pendingReason.reason}${pendingReason.recordedAt ? ` · ${format(new Date(pendingReason.recordedAt), 'dd MMM HH:mm')}` : ''}` : null}
          linked={linkedCards} panelFor={panelFor} goToCustomer={goToCustomer} onBack={() => navigate(backTo)}
          nodes={{ bbLive: bbLiveNode, bbHw: bbHwNode, bbHist: bbHistNode, bbUptime: bbUptimeOnly, bbUsage: bbUsageNode, bbSess: bbSessNode, catvHw: catvHwNode }}
          toggleCatv={toggleCatvConnection} toggleBb={toggleBbConnection} catvBusy={ctvStatusSaving} bbBusy={bbStatusSaving}
          retrack={handleRetrack} retracking={retracking} kick={handleKick} kicking={kicking} openOnu={openOnu} onDelete={handleDeleteCustomer}
          gtplToggle={{
            show: canSuspendGtplActive && hasCatvService && !!(customer.catv?.setTopBoxSerial || customer.catv?.accountNumber),
            suspended: !!customer.catv?.gtplSuspended, busy: togglingGtplSuspend,
            onToggle: () => { if (window.confirm(customer.catv?.gtplSuspended ? 'Activate this box on GTPL Saathi?' : 'Suspend this box on GTPL Saathi?')) handleToggleGtplSuspend(); },
          }}
          initialTab={mobileDeepLink.tab} initialScreen={mobileDeepLink.screen}
        />
      )}

      {/* ── Customer Name Header ── */}
      {!isAppView && (<>
      <div className="cd-card cd-head">
        <div className="cd-head-id">
          <div className="cd-avatar" aria-hidden="true">
            {(customer.name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 className="cd-name">{customer.name}</h1>
            <div className="cd-meta">
              {customer.customerId && <span>ID <strong>{customer.customerId}</strong></span>}
              {customer.billingNumber != null && <span>Bill no. <strong>{customer.billingNumber}</strong></span>}
              {hasCatv && <span><Tv size={12} style={{ verticalAlign: '-2px' }} /> Cable TV</span>}
              {hasBroadbandService && <span><Wifi size={12} style={{ verticalAlign: '-2px' }} /> Broadband</span>}
            </div>
          </div>
          {imbsLinked && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              border: `1.5px solid ${imbsBlocked ? 'var(--cb-fca5a5)' : imbsOnline ? 'var(--cb-86efac)' : 'var(--cb-e5e7eb)'}`,
              background: imbsBlocked ? 'var(--cb-fef2f2)' : imbsOnline ? 'var(--cb-f0fdf4)' : 'var(--cb-f9fafb)',
              borderRadius: 6, padding: '4px 12px', fontSize: '.78rem', fontWeight: 700,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: imbsBlocked ? 'var(--cb-dc2626)' : imbsOnline ? 'var(--cb-16a34a)' : 'var(--cb-9ca3af)',
              }} />
              <span style={{ color: imbsBlocked ? 'var(--c-dc2626)' : imbsOnline ? 'var(--c-15803d)' : 'var(--c-6b7280)' }}>
                {imbsSub === null ? 'Netzur…' : imbsBlocked ? 'Blocked' : imbsOnline ? 'Online' : 'Offline'}
              </span>
              {imbsBlocked && imbsBlockReason && (
                <span
                  title={imbsBlockReason}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 14, height: 14, borderRadius: '50%',
                    background: 'var(--cb-fca5a5)', color: 'var(--c-ffffff)', cursor: 'help', fontSize: '.68rem', fontWeight: 800,
                  }}
                >i</span>
              )}
            </span>
          )}
          {imbsLinked && (imbsSub === null || imbsPackage) && (
            <span style={{
              background: imbsSub === null ? 'var(--cb-e5e7eb)' : 'var(--cb-dbeafe)',
              color: imbsSub === null ? 'var(--c-6b7280)' : 'var(--c-1e40af)',
              borderRadius: 4, padding: '3px 10px',
              fontSize: '.72rem', fontWeight: 700, letterSpacing: '.03em',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Zap size={10} />
              {imbsSub === null ? 'Netzur…' : (imbsPackage?.name || imbsPackage)}
            </span>
          )}
          {(() => {
            const comboBadgeStyle = {
              background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)', color: 'var(--c-059669)',
              borderRadius: 4, padding: '3px 10px', fontSize: '.75rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
            };
            const goTo = (id) => { window.location.href = `/customers/${id}`; };

            // Forward direction — this customer is broadband-shaped with 1+ linked CATV
            // accounts: the 1:1 linkedAccount (TV1, if it's catv/both) plus any extra "2TV"+
            // accounts in broadband.linkedCatvAccounts (TV2, TV3…), same order as
            // backend utils/comboTv.
            const la = customer.linkedAccount;
            const forward = [];
            if (la && ['catv', 'both'].includes(la.customerType)) forward.push({ tvNumber: 1, target: la });
            (customer.broadband?.linkedCatvAccounts || []).forEach((c, i) => forward.push({ tvNumber: i + 2, target: c }));

            // A real multi-TV combo (2+ linked CATV accounts) gets numbered badges; a plain
            // single pair keeps today's unnumbered "Linked · CableTV/Broadband" wording.
            if (forward.length >= 2) {
              return forward.map(({ tvNumber, target }) => (
                <span key={target._id} style={comboBadgeStyle} title={`Linked TV ${tvNumber}: ${target.name}`} onClick={() => goTo(target._id)}>
                  <Link2 size={11} /> Linked TV {tvNumber} · {target.name}
                </span>
              ));
            }
            if (forward.length === 1) {
              const { target } = forward[0];
              return (
                <span style={comboBadgeStyle} title={`Linked to ${target.name}`} onClick={() => goTo(target._id)}>
                  <Link2 size={11} /> Linked · CableTV
                </span>
              );
            }

            // Reverse direction — this customer is a CATV account referenced from a
            // broadband customer's combo (see GET /:id's linkedViaBroadband).
            const via = customer.linkedViaBroadband;
            if (via) {
              return (
                <span style={comboBadgeStyle} title={`Linked to ${via.customer.name}`} onClick={() => goTo(via.customer._id)}>
                  <Link2 size={11} /> {via.comboSize >= 2 ? `Linked TV ${via.tvNumber} · ${via.customer.name}` : 'Linked · Broadband'}
                </span>
              );
            }

            // Plain 1:1 pair with no forward/reverse combo data available (customer wasn't
            // fetched via GET /:id, e.g. a lighter-weight view) — legacy fallback.
            if (customer.linkedAccount) {
              return (
                <span style={comboBadgeStyle} title={`Linked to ${customer.linkedAccount.name}`} onClick={() => goTo(customer.linkedAccount._id)}>
                  <Link2 size={11} /> Linked · {customer.linkedAccount.customerType === 'catv' ? 'CableTV' : 'Broadband'}
                </span>
              );
            }
            return null;
          })()}
        </div>
        <div className="cd-head-actions">
          {pendingReason && (
            <span
              title={`Pending:${reasonLabels[pendingReason.reason] || pendingReason.reason}${pendingReason.agent ? ` · by ${pendingReason.agent}` : ''}${pendingReason.recordedAt ? ` · ${format(new Date(pendingReason.recordedAt), 'dd-MMM-yyyy HH:mm')}` : ''}`}
              style={{
                display: 'inline-flex', alignItems: 'flex-start', gap: 4,
                background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', color: 'var(--c-92400e)',
                borderRadius: 4, padding: '3px 8px', fontSize: '.72rem', fontWeight: 600,
                maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35,
              }}
            >
              <Clock size={11} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                Pending: {reasonLabels[pendingReason.reason] || pendingReason.reason}
                {pendingReason.recordedAt ? <><br />{format(new Date(pendingReason.recordedAt), 'dd-MMM-yyyy HH:mm')}</> : ''}
              </span>
            </span>
          )}
          {hasCatv && (
            <div className="cd-svc-pill">
              {hasBroadbandService && <span className="tag">TV</span>}
              <span style={{ fontSize: '.78rem', fontWeight: 700, color: customer.status === 'active' ? 'var(--badge-green-fg)' : 'var(--badge-red-fg)' }}>
                {customer.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              <button
                onClick={() => {
                  if (customer.status === 'active') {
                    setCtvTicketMode(ctvIsRegular ? 'instant' : 'none');
                    setCtvTicketDate(firstOfNextMonth());
                    setCloseDate(todayInput());
                    setCtvCloseOpen(true);
                  } else {
                    handleReopenCatv();
                  }
                }}
                role="switch"
                aria-checked={customer.status === 'active'}
                disabled={ctvStatusSaving}
                title={customer.status === 'active' ? 'Close Connection' : 'Reactivate Connection'}
                style={{
                  position: 'relative', width: 40, height: 22, borderRadius: 999,
                  border: 'none', cursor: ctvStatusSaving ? 'default' : 'pointer', padding: 0, flexShrink: 0,
                  background: customer.status === 'active' ? 'var(--cb-16a34a)' : 'var(--cb-d1d5db)',
                  transition: 'background .2s', opacity: ctvStatusSaving ? .6 : 1,
                }}
              >
                <span style={{
                  position: 'absolute', top: 2, left: customer.status === 'active' ? 20 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: 'var(--cb-ffffff)',
                  transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                }} />
              </button>
              {customer.status !== 'active' && customer.catv?.closureReason && (
                <span
                  title={`Closed${customer.catv.closedAt ? ` on ${fmtDate(customer.catv.closedAt)}` : ''}: ${customer.catv.closureReason}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--cb-fef2f2)', border: '1px solid var(--cb-fca5a5)', color: 'var(--c-dc2626)',
                    borderRadius: 4, padding: '3px 8px', fontSize: '.72rem', fontWeight: 600,
                    maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  <ShieldOff size={11} style={{ flexShrink: 0 }} />
                  {customer.catv.closureReason}
                </span>
              )}
            </div>
          )}
          {hasBroadbandService && (
            <div className="cd-svc-pill">
              {hasCatv && <span className="tag">Broadband</span>}
              <span style={{ fontSize: '.78rem', fontWeight: 700, color: bbActive ? 'var(--badge-green-fg)' : 'var(--badge-red-fg)' }}>
                {bbActive ? 'Active' : 'Inactive'}
              </span>
              <button
                onClick={() => { if (bbActive) { setCloseDate(todayInput()); setBbCloseOpen(true); } else handleReopenBroadband(); }}
                role="switch"
                aria-checked={bbActive}
                disabled={bbStatusSaving}
                title={bbActive ? 'Close Connection' : 'Reactivate Connection'}
                style={{
                  position: 'relative', width: 40, height: 22, borderRadius: 999,
                  border: 'none', cursor: bbStatusSaving ? 'default' : 'pointer', padding: 0, flexShrink: 0,
                  background: bbActive ? 'var(--cb-16a34a)' : 'var(--cb-d1d5db)',
                  transition: 'background .2s', opacity: bbStatusSaving ? .6 : 1,
                }}
              >
                <span style={{
                  position: 'absolute', top: 2, left: bbActive ? 20 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: 'var(--cb-ffffff)',
                  transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                }} />
              </button>
              {!bbActive && customer.broadband?.closureReason && (
                <span
                  title={`Closed${customer.broadband.closedAt ? ` on ${fmtDate(customer.broadband.closedAt)}` : ''}: ${customer.broadband.closureReason}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--cb-fef2f2)', border: '1px solid var(--cb-fca5a5)', color: 'var(--c-dc2626)',
                    borderRadius: 4, padding: '3px 8px', fontSize: '.72rem', fontWeight: 600,
                    maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  <ShieldOff size={11} style={{ flexShrink: 0 }} />
                  {customer.broadband.closureReason}
                </span>
              )}
            </div>
          )}
          {hasCatv && (
            <button onClick={handleRetrack} disabled={retracking}
              style={{
                background: retracking ? 'var(--cb-6b7280)' : 'var(--primary)', color: 'var(--c-ffffff)', border: 'none', borderRadius: 4,
                padding: '4px 14px', fontSize: '.78rem', fontWeight: 600, cursor: retracking ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
              <RotateCcw size={12} style={{ animation: retracking ? 'spin 1s linear infinite' : 'none' }} />
              {retracking ? 'Retracking…' : 'Retrack STB'}
            </button>
          )}
          {isAdmin && (
            <button onClick={handleDeleteCustomer}
              style={{
                background: 'var(--cb-dc2626)', color: 'var(--c-ffffff)', border: 'none', borderRadius: 4,
                padding: '4px 12px', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* ── Customer Details — 4 col (2 × 2 on phones, see .cd-info-grid in index.css) ── */}
      <div className="cd-info-grid cd-card cd-stats">
        {[
          { label: hasBB ? 'Current Package' : 'Current Outstanding', value: hasBB ? (
            <span style={{ fontWeight: 700, fontSize: '.9rem' }}>
              {withWbr(customer.broadband?.plan?.displayName) || '—'}
            </span>
          ) : (
            <>
              <span style={{ fontWeight: 700, fontSize: '.9rem', color: balance > 0 ? 'var(--c-dc2626)' : 'var(--c-16a34a)' }}>
                ₹{fmt(balance)}
              </span>
              <div style={{ fontWeight: 600, fontSize: '.78rem', color: 'var(--c-374151)', marginTop: 4 }}>
                {withWbr(customer.catv?.package?.displayName || customer.catv?.package?.name) || '—'}
              </div>
            </>
          ), sub: hideCollect ? null : (
            <button
              onClick={() => setActiveAction('collect')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 12px', borderRadius: 5, border: 'none',
                background: '#15803d', color: '#fff',
                fontSize: '.75rem', fontWeight: 700, cursor: 'pointer',
              }}
            >
              <Wallet size={11} /> Collect
            </button>
          )},
          { label: 'Package Expiry', value: (() => {
            const isCatvExpired = !hasBB && customer.catv?.expiryDate && new Date(customer.catv.expiryDate) < new Date();
            const dateStr = fmtDate(hasBB ? customer.broadband?.expiryDate : customer.catv?.expiryDate);
            return isCatvExpired
              ? <span style={{ color: 'var(--c-dc2626)' }}>{dateStr}</span>
              : dateStr;
          })(), sub: (() => {
            if (!hasBB) {
              return (customer.catv?.expiryDate && new Date(customer.catv.expiryDate) < new Date()) ? (
                <span style={{ color: 'var(--c-dc2626)', fontWeight: 800, fontSize: '1rem' }}>Expired</span>
              ) : null;
            }
            // Broadband customer with linked CableTV accounts: their expiry dates live on
            // the CATV records (catv.expiryDate), so show them here beside the broadband one.
            // Same order/numbering as the header "Linked TV N" badges: linkedAccount = TV1,
            // then broadband.linkedCatvAccounts = TV2, TV3…
            const la = customer.linkedAccount;
            const tvs = [
              ...(la && ['catv', 'both'].includes(la.customerType) ? [la] : []),
              ...(customer.broadband?.linkedCatvAccounts || []),
            ].map((t, i, all) => ({ target: t, label: all.length >= 2 ? `TV${i + 1}` : 'CableTV' }))
              .filter(({ target }) => target.catv?.expiryDate);
            if (!tvs.length) return null;
            return tvs.map(({ target, label }) => {
              const expired = new Date(target.catv.expiryDate) < new Date();
              return (
                <div key={target._id} style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-374151)' }} title={target.name}>
                  {label}: <span style={{ color: expired ? 'var(--c-dc2626)' : undefined }}>{fmtDate(target.catv.expiryDate)}</span>
                </div>
              );
            });
          })() },
          { label: 'Billing Area', value: [customer.address?.area, hasBB ? customer.broadband?.houseNumber : customer.catv?.houseNumber].filter(Boolean).join(' - ') || '—' },
          { label: 'Mobile No.', value: customer.mobile },
        ].map(({ label, value, sub }) => {
          const StatIcon = { 'Current Package': Tv, 'Current Outstanding': Wallet, 'Package Expiry': Calendar, 'Billing Area': MapPin, 'Mobile No.': Phone }[label];
          return (
            <div className="cd-info-cell cd-stat" key={label}>
              <div className="cd-stat-ico" aria-hidden="true"><StatIcon size={16} /></div>
              <div className="cd-stat-body">
                <div className="cd-stat-label">{label}</div>
                <div className="cd-stat-value">{value}</div>
                {sub && <div className="cd-stat-sub">{sub}</div>}
              </div>
            </div>
          );
        })}
      </div>
      </>)}

      {/* Same order as App View: live traffic, hardware folded, history, uptime. The PPPoE session block is not shown (App View dropped it too). */}
      {!isAppView && <>
        {bbLiveNode}
        {bbHwNode && <details open style={{ marginBottom: 12 }}><summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '.88rem', padding: '8px 0' }}>Current hardware</summary>{bbHwNode}</details>}
        {bbHistNode}{bbUptimeOnly}
        {catvHwNode && <details open style={{ marginBottom: 12 }}><summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '.88rem', padding: '8px 0' }}>Current hardware</summary>{catvHwNode}</details>}
      </>}

      {/* ── Tabs + Panel — a vertical icon rail beside the panel on desktop, but that rail ate
          a fixed 44px column plus its own padding in App View and left the actual content
          squeezed into what was left of a phone-width screen. App View now gets a horizontal,
          scrollable tab strip above the panel instead, so the panel gets the full width. ── */}
      <div style={isAppView ? { display: 'flex', flexDirection: 'column', gap: 8 } : { display: 'flex', gap: 8, alignItems: 'stretch' }}>

        {/* Tab Bar — horizontal scrolling strip (icon + short label) above the panel in App
            View; full-height icon+label sidebar list on desktop. */}
        {!isAppView && <nav className="cd-card cd-rail" aria-label="Customer sections">
          {visibleActions.map(({ key, label, icon: Icon }) => {
            if (key === 'subscription' && hasBB) Icon = Wifi;
            return (
              <button key={key} type="button" className="cd-rail-btn" data-g={RAIL_GROUP[key]}
                onClick={() => setActiveAction(key)} title={label}
                aria-current={activeAction === key ? 'true' : undefined}>
                <span className="ico"><Icon size={14} /></span>
                {label}
              </button>
            );
          })}
        </nav>}

        {/* Action Panel */}
        {!isAppView && <div ref={actionPanelRef} className="cd-card" style={{ scrollMarginTop: 8,
          flex: 1, minWidth: 0,
          padding: isAppView ? '16px 14px' : 18,
        }}>
          {panelFor(activeAction)}
        </div>}
      </div>

      {retrackResult && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => setRetrackResult(null)}>
          <div style={{
            background: 'var(--cb-ffffff)', borderRadius: 12, padding: '28px 32px', maxWidth: 440, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: retrackResult.ok ? 'var(--cb-dcfce7)' : 'var(--cb-fee2e2)', flexShrink: 0,
              }}>
                {retrackResult.ok ? <CheckCircle size={20} color="var(--c-16a34a)" /> : <AlertCircle size={20} color="var(--c-dc2626)" />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--c-111827)' }}>
                  {retrackResult.ok ? 'Retrack Successful' : 'Retrack Failed'}
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>STB: {retrackResult.stb}</div>
              </div>
              <button onClick={() => setRetrackResult(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-9ca3af)', padding: 4 }}>
                <X size={18} />
              </button>
            </div>
            <div style={{
              background: retrackResult.ok ? 'var(--cb-f0fdf4)' : 'var(--cb-fef2f2)',
              border: `1px solid ${retrackResult.ok ? 'var(--cb-86efac)' : 'var(--cb-fca5a5)'}`,
              borderRadius: 8, padding: '12px 16px',
              color: retrackResult.ok ? 'var(--c-166534)' : 'var(--c-991b1b)',
              fontSize: '.9rem', lineHeight: 1.6, wordBreak: 'break-word',
            }}>
              {retrackResult.message}
            </div>
            <button onClick={() => setRetrackResult(null)}
              style={{
                marginTop: 18, width: '100%', padding: '9px 0', borderRadius: 7,
                background: 'var(--primary)', color: 'var(--c-ffffff)', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '.9rem',
              }}>
              Close
            </button>
          </div>
        </div>
      )}

      {tr069NoConfig && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => setTr069NoConfig(false)}>
          <div style={{
            background: 'var(--cb-ffffff)', borderRadius: 12, padding: '28px 32px', maxWidth: 400, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--cb-fee2e2)', flexShrink: 0,
              }}>
                <AlertCircle size={20} color="var(--c-dc2626)" />
              </div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--c-111827)', flex: 1 }}>TR069</div>
              <button onClick={() => setTr069NoConfig(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-9ca3af)', padding: 4 }}>
                <X size={18} />
              </button>
            </div>
            <div style={{
              background: 'var(--cb-fef2f2)', border: '1px solid var(--cb-fca5a5)', borderRadius: 8,
              padding: '12px 16px', color: 'var(--c-991b1b)', fontSize: '.9rem', lineHeight: 1.6,
            }}>
              No configuration of TR069 done for this customer.
            </div>
            <button onClick={() => setTr069NoConfig(false)}
              style={{
                marginTop: 18, width: '100%', padding: '9px 0', borderRadius: 7,
                background: 'var(--primary)', color: 'var(--c-ffffff)', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '.9rem',
              }}>
              Close
            </button>
          </div>
        </div>
      )}

      {bbCloseOpen && (
        <div className="modal-overlay" onClick={() => !bbStatusSaving && setBbCloseOpen(false)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Close Broadband Connection</h3>
              <button className="modal-close" onClick={() => setBbCloseOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
                This marks the broadband connection as closed and is recorded in System Logs.
                {customer.customerType === 'broadband' && ' The customer will also be set Inactive.'}
              </div>
              <textarea className="form-control" rows={3} placeholder="Reason for closing (required)"
                value={bbCloseReason} onChange={e => setBbCloseReason(e.target.value)} autoFocus />
              <label className="form-label" style={{ marginTop: 12 }}>Date of inactivation</label>
              <input type="date" className="form-control" value={closeDate} max={todayInput()}
                onChange={e => setCloseDate(e.target.value)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setBbCloseOpen(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={bbStatusSaving || !bbCloseReason.trim()} onClick={handleCloseBroadband}>
                {bbStatusSaving ? 'Closing…' : 'Close Connection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {ctvCloseOpen && (
        <div className="modal-overlay" onClick={() => !ctvStatusSaving && setCtvCloseOpen(false)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Close CATV Connection</h3>
              <button className="modal-close" onClick={() => setCtvCloseOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 12 }}>
                This marks the CATV connection as closed and is recorded in System Logs.
                {customer.customerType === 'catv' && ' The customer will also be set Inactive.'}
              </div>
              <textarea className="form-control" rows={3} placeholder="Reason for closing (required)"
                value={ctvCloseReason} onChange={e => setCtvCloseReason(e.target.value)} autoFocus />
              <label className="form-label" style={{ marginTop: 12 }}>Date of inactivation</label>
              <input type="date" className="form-control" value={closeDate} max={todayInput()}
                onChange={e => setCloseDate(e.target.value)} />

              {ctvTicketEligible && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--cb-e5e7eb)' }}>
                  <div style={{ fontSize: '.8rem', fontWeight: 600, marginBottom: 8 }}>
                    GTPL disconnect ticket
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginBottom: 10 }}>
                    Raise a "PACKAGE DISCONNECT REQUEST" ticket on GTPL Saathi for account{' '}
                    <strong>{customer.catv.accountNumber}</strong>.
                    {!ctvIsRegular && ' This is a DTD / AMP customer: a ticket is already raised automatically when the plan expires without a renewal, so raise one here only if you need it sooner.'}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.83rem', marginBottom: 8, cursor: 'pointer' }}>
                    <input type="radio" name="ctvTicketMode" checked={ctvTicketMode === 'instant'}
                      onChange={() => setCtvTicketMode('instant')} />
                    Raise ticket now
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.83rem', marginBottom: ctvTicketMode === 'schedule' ? 6 : 0, cursor: 'pointer' }}>
                    <input type="radio" name="ctvTicketMode" checked={ctvTicketMode === 'schedule'}
                      onChange={() => setCtvTicketMode('schedule')} />
                    Schedule for a date
                  </label>
                  {ctvTicketMode === 'schedule' && (
                    <div style={{ display: 'flex', gap: 8, marginLeft: 24 }}>
                      <input type="date" className="form-control" style={{ flex: 2 }}
                        value={ctvTicketDate} onChange={e => setCtvTicketDate(e.target.value)} />
                      <input type="time" className="form-control" style={{ flex: 1 }}
                        placeholder="Time (optional)" value={ctvTicketTime} onChange={e => setCtvTicketTime(e.target.value)} />
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.83rem', marginTop: 8, cursor: 'pointer' }}>
                    <input type="radio" name="ctvTicketMode" checked={ctvTicketMode === 'none'}
                      onChange={() => setCtvTicketMode('none')} />
                    Don't raise a ticket
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCtvCloseOpen(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={ctvStatusSaving || !ctvCloseReason.trim()} onClick={handleCloseCatv}>
                {ctvStatusSaving ? 'Closing…' : 'Close Connection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          onClose={() => setEditingPayment(null)}
          onSaved={handlePaymentSaved}
        />
      )}
    </div>
  );
}
